<template>
  <div class="order-management">
    <div v-if="isRefundTab" class="lvyun-profile-banner">
      <h2>退款订单</h2>
      <p>汇总已退款、已退单，以及待处理的「退款审核中」订单（含小程序零售与随心订）。</p>
    </div>
    <div v-else-if="isPackageDeliveryMain" class="lvyun-profile-banner">
      <h2>随心订订单</h2>
      <p>管理随心订套餐订单，查看总餐数/剩余餐数，支持当日排餐筛选、延期与超时扣减同步。</p>
    </div>
    <div v-else-if="isOrderSituation" class="lvyun-profile-banner">
      <h2>收银台订单</h2>
      <p>汇总收银台与小程序零售订单（饮品、小食等），支持取餐码筛选与出餐管理；随心订请到「随心订订单」。</p>
    </div>

    <el-card v-if="isPackageDeliveryMain" shadow="never" class="schedule-filter-card" style="margin-bottom: 20px;">
      <div class="schedule-filter-row">
        <span class="filter-label">排餐日期筛选：</span>
        <el-date-picker
          v-model="scheduleFilterDate"
          type="date"
          placeholder="选择日期"
          value-format="yyyy-MM-dd"
          @change="loadTodayMealOrders"
        />
        <el-button class="btn-primary" style="margin-left: 10px;" @click="loadTodayMealOrders">查询当日排餐</el-button>
        <el-button class="btn-primary" style="margin-left: 10px;" @click="openBatchVerifyPastDialog">一键补核销</el-button>
        <el-button
          class="btn-secondary"
          style="margin-left: 10px;"
          :disabled="!selectedOrderIds.length || batchDelaySubmitting"
          :loading="batchDelaySubmitting"
          @click="openBatchDelayDialog"
        >批量延期</el-button>
        <el-button
          class="btn-secondary"
          style="margin-left: 10px;"
          :disabled="!selectedOrderIds.length || batchCheckSubmitting"
          :loading="batchCheckSubmitting"
          @click="openBatchCheckDialog"
        >批量核销</el-button>
        <el-button
          v-if="canAdminDeleteSession"
          class="btn-secondary danger"
          style="margin-left: 10px;"
          :disabled="!selectedOrderIds.length || batchDeleteSubmitting"
          :loading="batchDeleteSubmitting"
          @click="batchDeleteSelectedOrders"
        >批量删除</el-button>
        <el-button class="btn-secondary" style="margin-left: 10px;" @click="exportTodayMeals">导出当日Excel</el-button>
        <el-button
          class="btn-secondary"
          style="margin-left: 10px;"
          :loading="subscriptionExportLoading"
          :disabled="subscriptionExportLoading"
          @click="exportSubscriptionOrders"
        >{{ subscriptionExportLoading ? '导出中...' : '批量导出订单' }}</el-button>
        <el-button class="btn-secondary" style="margin-left: 10px;" @click="downloadSubscriptionOrderTemplate">下载导入模板</el-button>
        <el-upload
          style="display: inline-block; margin-left: 10px;"
          action=""
          accept=".xlsx,.xls,.csv"
          :show-file-list="false"
          :before-upload="handleSubscriptionOrderImport"
        >
          <el-button class="btn-secondary">批量导入订单</el-button>
        </el-upload>
        <el-button class="btn-secondary" style="margin-left: 10px;" @click="showScheduleTable = !showScheduleTable">
          {{ showScheduleTable ? '隐藏当日表格' : '显示当日表格' }}
        </el-button>
      </div>
      <el-table
        v-if="showScheduleTable && todayMealOrders.length"
        :data="todayMealOrders"
        stripe
        size="small"
        style="width: 100%; margin-top: 16px;"
      >
        <el-table-column prop="user_name" label="顾客" width="100" />
        <el-table-column prop="remaining_meals" label="剩余餐数" width="90" />
        <el-table-column prop="meal_slot" label="餐时段" width="80" />
        <el-table-column label="是否超时" width="90">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.is_timeout ? 'danger' : 'info'">{{ scope.row.is_timeout ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否延期" width="90">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.is_deferred ? 'warning' : 'info'">{{ scope.row.is_deferred ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" />
      </el-table>
    </el-card>

    <!-- 搜索筛选区域 -->
    <el-card shadow="never" class="order-filter-card">
      <div class="order-filter-panel">
        <div class="order-filter-grid">
          <div class="filter-field">
            <label class="filter-field-label">订单搜索</label>
            <div class="filter-field-control filter-field-split">
              <el-select v-model="searchForm.orderSearchType" placeholder="订单编号" size="small">
                <el-option label="订单编号" value="order_no"></el-option>
                <el-option v-if="isOrderSituation || isOrderQueryTab" label="取餐码" value="pickup_code"></el-option>
                <el-option label="商品名称" value="product_name"></el-option>
                <el-option label="收货人" value="receiver"></el-option>
              </el-select>
              <el-input
                v-model="searchForm.keyword"
                placeholder="请输入搜索内容"
                clearable
                size="small"
                @keyup.enter.native="searchOrders"
              />
            </div>
          </div>

          <div class="filter-field filter-field-span2">
            <label class="filter-field-label">时间搜索</label>
            <div class="filter-field-control filter-time-range">
              <el-select v-model="searchForm.timeSearchType" placeholder="下单时间" size="small">
                <el-option label="下单时间" value="create_time"></el-option>
                <el-option label="发货时间" value="send_time"></el-option>
                <el-option label="完成时间" value="complete_time"></el-option>
              </el-select>
              <el-date-picker
                v-model="searchForm.startDate"
                type="datetime"
                placeholder="开始时间"
                size="small"
                :picker-options="datePickerOptions"
              />
              <span class="filter-range-sep">至</span>
              <el-date-picker
                v-model="searchForm.endDate"
                type="datetime"
                placeholder="结束时间"
                size="small"
                :picker-options="datePickerOptions"
              />
            </div>
          </div>

          <div class="filter-field">
            <label class="filter-field-label">姓名查询</label>
            <div class="filter-field-control">
              <el-input
                v-model="searchForm.userName"
                placeholder="请输入顾客姓名"
                clearable
                size="small"
                @keyup.enter.native="searchOrders"
              />
            </div>
          </div>

          <div class="filter-field">
            <label class="filter-field-label">联系方式</label>
            <div class="filter-field-control">
              <el-input
                v-model="searchForm.userPhone"
                placeholder="请输入手机号"
                clearable
                size="small"
                @keyup.enter.native="searchOrders"
              />
            </div>
          </div>

          <div class="filter-field">
            <label class="filter-field-label">订单状态</label>
            <div class="filter-field-control">
              <el-select v-model="searchForm.orderStatus" placeholder="全部" clearable size="small">
                <el-option label="全部" value=""></el-option>
                <el-option label="待支付" value="待支付"></el-option>
                <el-option v-if="isOrderSituation" label="已下单" value="已下单"></el-option>
                <el-option v-if="isPackageDeliveryMain || isOrderQueryTab" label="进行中" value="进行中"></el-option>
                <el-option v-if="isOrderSituation" label="进行中" value="进行中"></el-option>
                <el-option v-if="isOrderQueryTab || isOrderSituation" label="已支付" value="已支付"></el-option>
                <el-option v-if="isOrderSituation || isOrderQueryTab" label="制作中" value="制作中"></el-option>
                <el-option v-if="isOrderSituation || isOrderQueryTab" label="待取餐" value="待取餐"></el-option>
                <el-option v-if="isOrderQueryTab || isOrderSituation" label="待配送" value="待配送"></el-option>
                <el-option label="已完成" value="已完成"></el-option>
                <el-option v-if="!isPackageDeliveryMain" label="已取消" value="已取消"></el-option>
                <el-option label="已取消订阅" value="已取消订阅"></el-option>
                <el-option v-if="isRefundTab" label="退款审核中" value="退款审核中"></el-option>
                <el-option label="已退款" value="已退款"></el-option>
                <el-option v-if="isRefundTab || isPackageDeliveryMain" label="已退单" value="已退单"></el-option>
              </el-select>
            </div>
          </div>

          <div v-if="isPackageDeliveryMain" class="filter-field">
            <label class="filter-field-label">餐次筛选</label>
            <div class="filter-field-control">
              <MealSlotSegment v-model="searchForm.mealSlot" @change="searchOrders" />
            </div>
          </div>

          <div class="filter-field">
            <label class="filter-field-label">配送方式</label>
            <div class="filter-field-control">
              <el-select v-model="searchForm.deliveryMethod" placeholder="全部" clearable size="small">
                <el-option label="全部" value=""></el-option>
                <el-option label="配送" value="delivery"></el-option>
                <el-option label="到店自取" value="self_pickup"></el-option>
              </el-select>
            </div>
          </div>

          <div class="filter-field">
            <label class="filter-field-label">支付方式</label>
            <div class="filter-field-control">
              <el-select v-model="searchForm.paymentMethod" placeholder="全部" clearable size="small">
                <el-option label="全部" value=""></el-option>
                <el-option label="微信支付" value="wechat"></el-option>
                <el-option label="支付宝支付" value="alipay"></el-option>
                <el-option v-if="isPackageDelivery || isOrderQueryTab" label="小程序下单" value="mini_order"></el-option>
                <el-option v-if="isOrderQueryTab" label="兑换券" value="voucher"></el-option>
                <el-option v-if="isPackageDelivery || isOrderQueryTab" label="银行卡支付" value="bank_card"></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <div class="order-filter-actions">
          <el-button class="btn-primary" size="small" @click="searchOrders">筛选</el-button>
          <el-button class="btn-secondary" size="small" @click="resetFilters">重置</el-button>
        </div>
      </div>
    </el-card>
    
    <!-- 订单列表（改为接近图4的卡片式布局） -->
    <el-card shadow="hover" class="order-list-card">
      <template slot="header">
        <div class="card-header">
          <span class="card-header-title">{{ listTitle }}</span>
          <div class="order-status-boxes">
            <button
              v-for="item in statusBoxItems"
              :key="item.value || 'all'"
              type="button"
              class="status-box-btn"
              :class="{ active: activeStatusBox === item.value }"
              @click="changeStatusBox(item.value)"
            >
              {{ item.label }}
              <span class="status-box-count">{{ item.count }}</span>
            </button>
          </div>
          <div class="order-header-actions">
            <el-dropdown v-if="isPackageDeliveryMain" trigger="click" @command="printJolimarkTodayBatch">
              <el-button class="btn-primary">
                映美云批量打印<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="both">午餐 + 晚餐（默认）</el-dropdown-item>
                <el-dropdown-item command="lunch">仅午餐</el-dropdown-item>
                <el-dropdown-item command="dinner">仅晚餐</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button
              v-if="!isRefundTab"
              class="btn-primary"
              icon="el-icon-plus"
              @click="openCreateOrder"
            >添加订单</el-button>
          </div>
        </div>
      </template>

      <div v-if="isPackageDeliveryMain" class="order-list-select-bar">
        <el-checkbox
          :value="isAllSelectableOrdersSelected"
          :indeterminate="isOrderSelectionIndeterminate"
          @change="toggleSelectAllOrders"
        >全选本页</el-checkbox>
        <span v-if="selectedOrderIds.length" class="order-list-select-hint">已选 {{ selectedOrderIds.length }} 项</span>
      </div>
      
      <div class="order-list">
        <div
          v-for="order in orders"
          :key="order._id || order.order_no"
          class="order-item-card"
          :class="{ 'order-item-card--selected': isOrderSelected(order) }"
        >
          <div class="order-item-header">
            <div class="order-item-header-left">
              <el-checkbox
                v-if="isPackageDeliveryMain"
                class="order-select-checkbox"
                :value="isOrderSelected(order)"
                :disabled="!isOrderBatchSelectable(order)"
                @input="(val) => toggleOrderSelection(order, val)"
              />
              <span class="order-no">{{ order.order_no }}</span>
              <el-tag
                v-if="order.pickup_code"
                type="success"
                effect="dark"
                size="mini"
                style="margin-left: 8px"
              >取餐码 {{ order.pickup_code }}</el-tag>
              <span class="order-created-at">{{ order.createdAt }}</span>
              <span class="order-channel">{{ order.channel || '随心订小程序' }}</span>
            </div>
            <div class="order-item-header-right">
              <el-tag :type="getStatusType(order.order_status)">
                {{ order.order_status || '待支付' }}
              </el-tag>
            </div>
            </div>

          <div class="order-item-body">
            <div class="order-body-section order-body-product">
              <div class="obs-label">套餐</div>
              <div class="obs-product-name">{{ order.product_name || '—' }}</div>
              <div class="obs-muted">¥{{ order.unit_price || 0 }} × {{ order.quantity || 0 }} 件</div>
              <div v-if="order.order_type === '随心订'" class="obs-meal-stats">
                <span>总 {{ order.total_meals != null ? order.total_meals : '—' }}</span>
                <span class="obs-dot">·</span>
                <span>用 {{ order.used_meals != null ? order.used_meals : '—' }}</span>
                <span class="obs-dot">·</span>
                <span>剩 {{ order.remaining_meals != null ? order.remaining_meals : '—' }}</span>
              </div>
            </div>

            <div class="order-body-section order-body-customer">
              <div class="obs-label">顾客</div>
              <div class="obs-customer-name">{{ order.user_name }}</div>
              <div class="obs-muted">{{ order.user_phone }}</div>
              <div class="obs-address" :title="order.address">{{ order.address }}</div>
              <div v-if="order.delivery_region" class="obs-tag obs-tag-region">{{ order.delivery_region }}</div>
              <div v-if="order.rider_name" class="obs-tag obs-tag-rider">
                {{ order.rider_name }}{{ order.rider_phone ? ' · ' + order.rider_phone : '' }}
              </div>
            </div>

            <div class="order-body-section order-body-meta">
              <div class="obs-label">配送信息</div>
              <div class="obs-meta-row">
                <span class="obs-meta-k">周期</span>
                <span>{{ order.subscription_period || '—' }}</span>
              </div>
              <div v-if="order.order_type === '随心订' && order.expected_end_date" class="obs-meta-row">
                <span class="obs-meta-k">预计结束</span>
                <span>{{ order.expected_end_date }}</span>
              </div>
              <div class="obs-meta-row">
                <span class="obs-meta-k">餐次</span>
                <span>{{ formatOrderMealSlotDisplay(order) }}</span>
              </div>
              <div class="obs-meta-row">
                <span class="obs-meta-k">配送</span>
                <span>{{ order.delivery_method || '同城配送' }}</span>
              </div>
              <div v-if="order.meal_run_status === 'paused'" class="obs-pause-badge">已暂停</div>
              <div v-if="order.delay_log_summary" class="obs-delay-hint">{{ order.delay_log_summary }}</div>
            </div>

            <div class="order-body-section order-body-amount">
              <div class="obs-label">金额</div>
              <div class="obs-amount-main">¥{{ order.display_paid_amount != null ? order.display_paid_amount : (order.total_price || 0) }}</div>
              <div class="obs-amount-label">实收金额</div>
              <div
                v-if="order.order_type === '随心订' && order.delivery_unit_price != null"
                class="obs-amount-sub"
              >本单 ¥{{ order.delivery_unit_price }}</div>
            </div>
          </div>

          <div class="order-item-footer">
            <div class="order-footer-left">
              <el-button
                v-if="isPackageDelivery"
                size="small"
                class="btn-ghost-warn"
                icon="el-icon-time"
                @click="showDelayRecord(order)"
              >延期记录</el-button>
            </div>
            <div class="order-footer-right">
              <el-button size="small" type="primary" plain icon="el-icon-view" @click="viewOrder(order)">详情</el-button>
              <el-button
                v-if="order.pickup_status === 'preparing'"
                size="small"
                type="success"
                @click="completePickupPrepare(order)"
              >完成制作</el-button>
              <el-button
                v-if="order.pickup_status === 'ready'"
                size="small"
                type="warning"
                @click="confirmPickupOrder(order)"
              >确认取餐</el-button>
              <el-button size="small" class="btn-secondary" icon="el-icon-edit" @click="modifyOrder(order)">修改</el-button>
              <el-button
                v-if="canMaintainDelay(order)"
                size="small"
                class="btn-secondary"
                icon="el-icon-date"
                @click="openSubscriptionCalendar(order)"
              >日历</el-button>
              <el-button
                v-if="isPackageDelivery && order.order_type === '随心订'"
                size="small"
                class="btn-secondary"
                icon="el-icon-right"
                @click="extendOrder(order)"
              >延期</el-button>
              <el-button
                v-if="isPackageDelivery"
                size="small"
                class="btn-secondary"
                icon="el-icon-printer"
                @click="printJolimarkOne(order)"
              >打印</el-button>
              <el-button
                v-if="isPackageDelivery && order.order_type === '随心订'"
                size="small"
                class="btn-secondary"
                icon="el-icon-circle-check"
                @click="manualVerifyOne(order)"
              >手动核销</el-button>
              <el-dropdown trigger="click" @command="(cmd) => handleOrderMoreAction(cmd, order)">
                <el-button size="small" class="btn-more">
                  更多<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown" class="order-more-menu">
                  <el-dropdown-item command="remark" icon="el-icon-edit-outline">发货备注</el-dropdown-item>
                  <el-dropdown-item v-if="canPauseOrder(order)" command="pause" icon="el-icon-video-pause">暂停订单</el-dropdown-item>
                  <el-dropdown-item v-if="canResumeOrder(order)" command="resume" icon="el-icon-video-play">恢复继续</el-dropdown-item>
                  <el-dropdown-item v-if="showShipButton(order)" command="ship" icon="el-icon-truck">发货</el-dropdown-item>
                  <el-dropdown-item command="printBrowser" icon="el-icon-document">浏览器打印</el-dropdown-item>
                  <el-dropdown-item v-if="showChargebackButton(order)" command="chargeback" divided icon="el-icon-circle-close">退单</el-dropdown-item>
                  <el-dropdown-item v-if="showRefundButton(order)" command="refund" icon="el-icon-money">退款</el-dropdown-item>
                  <el-dropdown-item v-if="canAdminCancelSubscription(order)" command="cancelSub" icon="el-icon-close">取消订阅</el-dropdown-item>
                  <el-dropdown-item command="complete" icon="el-icon-check">确认完成</el-dropdown-item>
                  <el-dropdown-item
                    v-if="(isPackageDelivery || isOrderSituation) && canAdminDeleteSession"
                    command="delete"
                    divided
                    class="danger-text"
                  >删除订单</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <admin-pagination
        :total="total"
        :page.sync="currentPage"
        :page-size.sync="pageSize"
        @change="onOrdersPaginationChange"
      />
    </el-card>

    <!-- 新建/编辑订单弹窗 -->
    <el-dialog
      custom-class="app-admin-dialog"
      :title="orderDialogMode === 'create' ? '添加订单' : '编辑订单'"
      :visible.sync="orderDialogVisible"
      width="680px"
    >
      <div v-loading="orderFormLoading" element-loading-text="正在加载订单数据…">
      <el-form label-width="100px">
        <el-form-item label="订单编号">
          <el-input v-model="orderForm.order_no" disabled></el-input>
        </el-form-item>
        <el-form-item label="顾客查询">
          <el-autocomplete
            v-model="customerSearchQuery"
            :fetch-suggestions="queryCustomerSearch"
            placeholder="输入姓名搜索，支持下拉选择"
            style="width: 100%;"
            clearable
            :trigger-on-focus="true"
            @select="onCustomerSelect"
            @clear="onCustomerSearchClear"
            @blur="onCustomerSearchBlur"
          >
            <template slot-scope="{ item }">
              <div class="customer-suggest-item">
                <span class="customer-suggest-name">{{ item.username || '—' }}</span>
                <span class="customer-suggest-phone">{{ item.phone || '—' }}</span>
                <span v-if="item.address" class="customer-suggest-addr">{{ item.address }}</span>
              </div>
            </template>
          </el-autocomplete>
          <div v-if="orderForm.user_name" class="form-item-hint">
            已选顾客：{{ orderForm.user_name }}
          </div>
        </el-form-item>
        <el-form-item label="手机号码">
          <el-input
            v-model="orderForm.user_phone"
            placeholder="选填，输入11位手机号可自动关联小程序账号"
            maxlength="11"
            clearable
            @blur="onCustomerPhoneBlur"
            @clear="onCustomerPhoneClear"
          />
          <div v-if="orderForm.user_id && isValidCustomerPhone(orderForm.user_phone)" class="form-item-hint customer-bind-ok">
            {{ customerPhoneBindHint || ('已绑定账号：' + (orderForm.user_name || '—') + ' · ' + orderForm.user_phone) }}
          </div>
          <div v-else-if="customerPhoneLookupHint" class="form-item-hint period-hint-error">
            {{ customerPhoneLookupHint }}
          </div>
        </el-form-item>
        <el-form-item label="收货人">
          <el-input
            v-model="orderForm.consignee_name"
            placeholder="选填，默认可与顾客姓名相同"
            clearable
          />
          <div class="form-item-hint">配送小票上的收货人；不填则使用顾客姓名</div>
        </el-form-item>
        <el-form-item label="收货地址">
          <el-input v-model="orderForm.address" placeholder="请输入收货地址" @blur="onOrderAddressBlur"></el-input>
        </el-form-item>
        <el-form-item label="配送区域">
          <el-select
            v-model="orderForm.delivery_region"
            placeholder="请选择配送区域（与骑手管理一致）"
            style="width: 100%;"
            filterable
            allow-create
            default-first-option
            clearable
            @change="onOrderDeliveryRegionChange"
          >
            <el-option
              v-for="r in mergedDeliveryRegionOptions"
              :key="r"
              :label="r"
              :value="r"
            />
          </el-select>
          <div v-if="orderFormRiderPreview" class="form-item-hint order-form-rider-preview">
            当前骑手：{{ orderFormRiderPreview.region ? orderFormRiderPreview.region + ' · ' : '' }}{{ orderFormRiderPreview.name }}<span v-if="orderFormRiderPreview.phone"> · {{ orderFormRiderPreview.phone }}</span>
          </div>
          <div v-else class="form-item-hint order-form-rider-empty">
            未分配骑手：请选择配送区域，或填写含「太平」等片区关键词的地址
          </div>
          <div class="form-item-hint">
            选项与「骑手个人信息」区域配置对齐；选择区域后会自动带出对应骑手
          </div>
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input
            v-model="orderForm.order_note"
            type="textarea"
            :rows="2"
            maxlength="500"
            show-word-limit
            placeholder="打印小票「备注」栏会显示；与上方订餐周期（remark）无关"
          />
        </el-form-item>
        <el-form-item label="商品名称">
          <el-input
            v-model="orderForm.product_name"
            placeholder="手动输入商品名称"
            clearable
            @input="onManualProductNameInput"
          />
          <el-select
            v-if="isPackageDelivery"
            v-model="orderForm.productSelectKey"
            filterable
            clearable
            default-first-option
            :filter-method="filterOrderProductOptions"
            placeholder="从商品库快速选择（可选）"
            style="width: 100%; margin-top: 8px;"
            @visible-change="onOrderProductSelectVisible"
            @change="onOrderProductChange"
          >
            <el-option
              v-for="p in orderProductSelectOptions"
              :key="p.id"
              :label="formatOrderProductOptionLabel(p)"
              :value="p.id"
            />
          </el-select>
          <el-select
            v-else
            v-model="orderForm.productSelectKey"
            filterable
            clearable
            default-first-option
            :filter-method="filterOrderProductOptions"
            placeholder="输入名称筛选，或下拉选择（含饮品/随心订等）"
            style="width: 100%;"
            @visible-change="onOrderProductSelectVisible"
            @change="onOrderProductChange"
          >
            <el-option
              v-for="p in orderProductSelectOptions"
              :key="p.id"
              :label="formatOrderProductOptionLabel(p)"
              :value="p.id"
            />
          </el-select>
          <div class="form-item-hint">
            随心订录单可直接填写名称；从商品库选择时会自动带出价格与餐次配置。
          </div>
        </el-form-item>
        <el-form-item
          v-if="needsSubscriptionPeriodInDialog"
          label="套餐总餐次"
        >
          <el-input-number
            v-model="orderForm.manual_target_total_meals"
            :min="1"
            :max="9999"
            size="small"
            :controls="true"
            @change="onManualTargetTotalMealsChange"
          />
          <div class="form-item-hint">
            <template v-if="orderDialogMode === 'edit'">
              读取自本单已保存的订阅计划，可修正后保存。
            </template>
            <template v-else>
              请手动填写本单总餐次；从商品库选择时会自动带出，可再修改。
            </template>
          </div>
        </el-form-item>
        <el-form-item :label="needsSubscriptionPeriodInDialog ? '套餐总价' : '单价'">
          <el-input
            v-model.number="orderForm.unit_price"
            :placeholder="needsSubscriptionPeriodInDialog ? '套餐总价' : '单价'"
          ></el-input>
          <div
            v-if="needsSubscriptionPeriodInDialog && orderForm.productSelectKey && catalogPackagePriceMismatch"
            class="form-item-hint period-hint-error"
          >
            商品现价 ¥{{ catalogPackagePrice }}，当前订单记录 ¥{{ orderForm.unit_price || 0 }}。改价请在此修改后保存。
          </div>
        </el-form-item>
        <el-form-item label="数量">
          <el-input v-model.number="orderForm.quantity" placeholder="数量" @change="onOrderQuantityChange"></el-input>
        </el-form-item>
        <el-form-item label="运费">
          <el-input v-model.number="orderForm.shipping_fee" placeholder="运费"></el-input>
        </el-form-item>
        <el-form-item label="订餐周期" v-if="needsSubscriptionPeriodInDialog">
          <el-date-picker
            v-model="orderForm.subscription_period_start"
            type="date"
            placeholder="排餐起始日（跳过周末与法定节假日）"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
            @change="onSubscriptionStartChange"
          />
          <div class="form-item-hint">
            结束日期按商品「总购买餐次」自动推算，起止区间仅作展示备注。
            <span :class="{ 'period-hint-error': subscriptionPeriodError }">{{ subscriptionPeriodHint || '请选择起始日并填写商品信息' }}</span>
          </div>
        </el-form-item>
        <el-form-item
          v-if="orderDialogMode === 'create' && needsSubscriptionPeriodInDialog"
          label="排餐计划"
        >
          <el-radio-group v-model="orderForm.plan_link_mode" size="small">
            <el-radio-button label="new">新建计划（默认）</el-radio-button>
            <el-radio-button label="link">关联已有计划</el-radio-button>
          </el-radio-group>
          <el-input
            v-if="orderForm.plan_link_mode === 'link'"
            v-model="orderForm.link_subscription_id"
            placeholder="粘贴已有随心订计划 ID"
            clearable
            style="margin-top: 8px;"
          />
          <div class="form-item-hint">
            默认新建计划，避免复用顾客旧计划的延期记录与已用餐次。
          </div>
        </el-form-item>
        <el-form-item v-if="isPackageDelivery" label="历史订单">
          <el-checkbox v-model="orderForm.historical_order" @change="onHistoricalOrderToggle">
            线下历史订单（部分餐次已配送）
          </el-checkbox>
          <div v-if="orderForm.historical_order" class="historical-order-options">
            <el-radio-group
              v-model="orderForm.historical_delivery_mode"
              @change="onHistoricalDeliveryModeChange"
            >
              <el-radio label="past_date">按日期：起始日至昨日全部已配送</el-radio>
              <el-radio label="count">按餐数：指定已配送餐次</el-radio>
            </el-radio-group>
            <div v-if="orderForm.historical_delivery_mode === 'count'" class="historical-count-panel">
              <div class="historical-count-row">
                <span>套餐总餐次</span>
                <el-input-number
                  v-model="orderForm.historical_total_meals"
                  :min="1"
                  :max="9999"
                  size="small"
                  @change="onHistoricalMealsChange"
                />
                <span>已配送</span>
                <el-input-number
                  v-model="orderForm.historical_meals_delivered"
                  :min="0"
                  :max="historicalDeliveredMax"
                  size="small"
                  @change="onHistoricalMealsChange"
                />
                <span>餐</span>
              </div>
              <div class="historical-count-remaining">
                剩余 <strong>{{ historicalRemainingMeals }}</strong> 餐，将从起始日往后自动排餐
              </div>
            </div>
          </div>
          <div class="form-item-hint">
            历史已配送餐次入库后标记为「已核销」，剩余「未用餐」餐次仍可按现有规则延期、扣减与配送。
          </div>
        </el-form-item>
        <el-form-item label="餐次">
          <el-select
            v-model="orderForm.meal_slots"
            :multiple="productMealsMode === 'both'"
            :disabled="mealSlotsSelectDisabled"
            placeholder="请选择餐次"
            style="width: 100%;"
            @change="onOrderMealSlotsChange"
          >
            <el-option label="早餐" value="早餐" :disabled="mealSlotOptionDisabled('早餐')" />
            <el-option label="午餐" value="午餐" :disabled="mealSlotOptionDisabled('午餐')" />
            <el-option label="晚餐" value="晚餐" :disabled="mealSlotOptionDisabled('晚餐')" />
          </el-select>
          <div class="form-item-hint">{{ productMealConfigHint }}</div>
        </el-form-item>
        <el-form-item label="配送餐次类型">
          <el-select
            v-model="orderForm.meal_config"
            placeholder="请选择"
            style="width: 100%;"
            :disabled="productMealsMode === 'lunch' || productMealsMode === 'dinner'"
            @change="onOrderMealConfigChange"
          >
            <el-option label="仅午餐" value="only_lunch" />
            <el-option label="仅晚餐" value="only_dinner" />
            <el-option label="午晚都有" value="both" />
          </el-select>
          <div class="form-item-hint">
            决定排餐算法：仅午餐/仅晚餐每天 1 餐；午晚都有每天 2 餐。修改后请在日历中「重新排餐」生效。
          </div>
        </el-form-item>
        <el-form-item label="配送方式">
          <el-select v-model="orderForm.delivery_method" placeholder="请选择" style="width: 100%;">
            <el-option label="同城配送" value="同城配送" />
            <el-option label="到店自取" value="到店自取" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select v-model="orderForm.payment_method" placeholder="请选择" style="width: 100%;">
            <el-option label="小程序下单" value="mini_order" />
            <el-option label="微信支付" value="wechat" />
            <el-option label="支付宝支付" value="alipay" />
            <el-option label="现金" value="cash" />
            <el-option label="银行卡支付" value="bank_card" />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="orderDialogMode === 'create' && isOrderSituation"
          label="订单状态"
        >
          <el-select v-model="orderForm.order_status" placeholder="请选择" style="width: 100%;">
            <el-option label="待支付" value="待支付" />
            <el-option label="待发货" value="待发货" />
          </el-select>
          <div class="form-item-hint">说明：选「待发货」保存后列表显示为「已下单」，点击发货后显示「进行中」。</div>
        </el-form-item>
      </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="needsSubscriptionPeriodInDialog"
          :loading="schedulePreviewLoading"
          @click="openSchedulePreview"
        >排餐预览</el-button>
        <el-button @click="orderDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitOrder">确定</el-button>
      </span>
    </el-dialog>

    <!-- 随心订排餐预览 -->
    <el-dialog
      title="排餐预览"
      :visible.sync="schedulePreviewVisible"
      width="860px"
      append-to-body
      custom-class="schedule-preview-dialog"
    >
      <div v-if="schedulePreviewData" class="schedule-preview-summary">
        <div>订餐周期：{{ schedulePreviewData.period_text || '—' }}</div>
        <div>总餐次：{{ schedulePreviewData.total_meals }} 餐（单份 {{ schedulePreviewData.target_total_meals }} 餐 × {{ schedulePreviewData.quantity || 1 }} 份）</div>
        <div v-if="schedulePreviewData.historical_delivered_count > 0">
          历史已配送：{{ schedulePreviewData.historical_delivered_count }} 餐 · 待配送：{{ schedulePreviewData.historical_remaining_count }} 餐
        </div>
        <div>餐次模式：{{ (schedulePreviewData.meal_slots || []).join('、') || '—' }}</div>
        <div v-if="schedulePreviewData.total_count">共 {{ schedulePreviewData.total_count }} 条排餐明细</div>
      </div>
      <el-alert
        v-for="(w, wi) in (schedulePreviewData && schedulePreviewData.warnings) || []"
        :key="'pw-' + wi"
        :title="w"
        type="warning"
        show-icon
        :closable="false"
        style="margin-top: 10px;"
      />
      <el-table
        v-loading="schedulePreviewLoading"
        v-if="schedulePreviewData && schedulePreviewData.list && schedulePreviewData.list.length"
        :data="schedulePreviewPagedList"
        size="small"
        border
        max-height="360"
        class="schedule-preview-table"
      >
        <el-table-column label="#" width="56" align="center">
          <template slot-scope="scope">
            {{ (schedulePreviewPage - 1) * schedulePreviewPageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="meal_date" label="排餐日期" min-width="120" />
        <el-table-column prop="meal_slot" label="餐次" min-width="88" align="center" />
        <el-table-column prop="copy_index" label="份数序号" min-width="96" align="center" />
        <el-table-column label="状态" min-width="120">
          <template slot-scope="scope">
            <span :class="scope.row._warning ? 'preview-row-warn' : ''">{{ scope.row.status || '未用餐' }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div v-else-if="!schedulePreviewLoading" class="form-item-hint" style="margin-top: 12px;">暂无排餐数据，请检查起始日与商品配置</div>
      <admin-pagination
        v-if="schedulePreviewData && schedulePreviewData.total_count > 0"
        :total="schedulePreviewData.total_count"
        :page.sync="schedulePreviewPage"
        :page-size.sync="schedulePreviewPageSize"
        @change="loadSchedulePreviewPage"
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="schedulePreviewVisible = false">关闭</el-button>
        <el-button
          v-if="orderDialogMode === 'create' && schedulePreviewPendingSubmit"
          type="primary"
          @click="confirmSchedulePreviewSubmit"
        >确认并下单</el-button>
      </span>
    </el-dialog>

    <!-- 随心订退单 -->
    <el-dialog custom-class="app-admin-dialog" title="退单" :visible.sync="chargebackDialogVisible" width="480px">
      <el-form label-width="90px">
        <el-form-item label="订单号">
          <span>{{ chargebackTargetOrder && chargebackTargetOrder.order_no }}</span>
        </el-form-item>
        <el-form-item label="退单原因" required>
          <el-input
            v-model="chargebackForm.reason"
            type="textarea"
            :rows="3"
            placeholder="请填写退单原因"
          />
        </el-form-item>
        <el-form-item label="打印小票">
          <el-checkbox v-model="chargebackForm.print_receipt">提交映美云打印退单小票</el-checkbox>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="chargebackDialogVisible = false">取消</el-button>
        <el-button type="danger" class="btn-primary" @click="submitChargeback">确认退单</el-button>
      </span>
    </el-dialog>

    <!-- 随心订当日延期（新排餐引擎） -->
    <el-dialog custom-class="app-admin-dialog" title="延期操作" :visible.sync="extendDialogVisible" width="480px">
      <el-form label-width="120px">
        <el-form-item label="延期日期">
          <span>{{ deferTodayLabel }}</span>
        </el-form-item>
        <el-form-item label="延期餐次">
          <el-radio-group v-model="extendForm.meal_slot">
            <el-radio label="午餐">中餐</el-radio>
            <el-radio label="晚餐">晚餐</el-radio>
          </el-radio-group>
        </el-form-item>
        <div class="extend-hint">
          仅可延期<strong>当日</strong>未过期、未核销、未延期的餐次。从被延期餐次的<strong>下一餐段</strong>起找槽；仅移动该餐，其余未核销保持原位；已核销/未核销均不占槽，日历可<strong>同格多 tag</strong>并列显示（灰✓ + 绿/橙）。
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="extendDialogVisible = false">取消</el-button>
        <el-button type="primary" class="btn-primary" @click="submitMealDefer">确定延期</el-button>
      </span>
    </el-dialog>

    <!-- 旧版延期弹窗保留（高级/manual） -->
    <el-dialog custom-class="app-admin-dialog" title="随心订订单延期（旧版）" :visible.sync="legacyExtendDialogVisible" width="520px">
      <el-form label-width="110px">
        <el-form-item label="原计划日期">
          <el-date-picker
            v-model="extendForm.original_date"
            type="date"
            placeholder="请选择原计划配送日期"
            value-format="yyyy-MM-dd"
            :picker-options="extendOriginalDatePickerOptions"
            style="width: 100%;"
            @change="ensureExtendNewDate"
          />
        </el-form-item>
        <el-form-item label="新日期">
          <el-date-picker
            v-model="extendForm.new_date"
            type="date"
            placeholder="请选择新的配送日期"
            value-format="yyyy-MM-dd"
            :picker-options="extendNewDatePickerOptions"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="餐型">
          <el-select v-model="extendForm.meal_type" placeholder="请选择餐型" style="width: 100%;" @change="ensureExtendNewDate">
            <el-option label="早餐" value="breakfast" />
            <el-option label="午餐" value="lunch" />
            <el-option label="晚餐" value="dinner" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="extendShowAlsoDinnerOption" label=" ">
          <el-checkbox v-model="extendForm.also_dinner">同时将原计划日的晚餐延期到同一新日期</el-checkbox>
        </el-form-item>
        <div class="extend-hint">
          当前动态周期结束日：<strong>{{ extendCycleEndHint }}</strong>；新日期须 ≥ 结束日次日（休息日不可选）。
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="extendDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExtend">确定</el-button>
      </span>
    </el-dialog>

    <!-- 订单详情（MySQL 实时拉取；可编辑配送区域/骑手） -->
    <el-dialog
      :visible.sync="orderDetailVisible"
      width="680px"
      append-to-body
      custom-class="app-admin-dialog order-detail-modal"
      :show-close="false"
    >
      <template slot="title">
        <div v-if="orderDetailCard" class="odm-header">
          <div class="odm-header-top">
            <div class="odm-header-left">
              <h3 class="odm-title">订单详情</h3>
              <el-tag size="small" :type="getStatusType(orderDetailCard.order_status)" effect="light" class="odm-status-tag">
                {{ orderDetailCard.order_status || '待支付' }}
              </el-tag>
            </div>
            <button type="button" class="odm-close" aria-label="关闭" @click="orderDetailVisible = false">
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div class="odm-order-no">{{ orderDetailCard.order_no }}</div>
          <div class="odm-header-meta">
            <span v-if="orderDetailCard.createdAt"><i class="el-icon-time"></i>{{ orderDetailCard.createdAt }}</span>
            <span v-if="orderDetailCard.channel"><i class="el-icon-mobile-phone"></i>{{ orderDetailCard.channel || '随心订小程序' }}</span>
          </div>
        </div>
        <div v-else class="odm-header">
          <h3 class="odm-title">订单详情</h3>
        </div>
      </template>

      <div v-loading="orderDetailLoading" class="order-detail-dialog">
        <template v-if="orderDetailCard">
          <div class="odm-amount-strip">
            <div class="odm-amount-item odm-amount-main">
              <span class="odm-amount-label">实收金额</span>
              <span class="odm-amount-value">
                ¥{{ orderDetailCard.display_paid_amount != null ? orderDetailCard.display_paid_amount : orderDetailCard.total_price }}
              </span>
            </div>
            <div
              v-if="orderDetailCard.order_type === '随心订' && orderDetailCard.delivery_unit_price != null"
              class="odm-amount-item"
            >
              <span class="odm-amount-label">本配送单</span>
              <span class="odm-amount-value odm-amount-sub">¥{{ orderDetailCard.delivery_unit_price }}</span>
            </div>
            <div class="odm-amount-item">
              <span class="odm-amount-label">套餐单价</span>
              <span class="odm-amount-value odm-amount-sub">¥{{ orderDetailCard.unit_price }} × {{ orderDetailCard.quantity }}</span>
            </div>
          </div>

          <div class="odm-grid">
            <section class="odm-section">
              <div class="odm-section-head">
                <i class="el-icon-user"></i>
                <span>顾客信息</span>
              </div>
              <div class="odm-section-body">
                <div class="odm-field">
                  <span class="odm-field-k">姓名</span>
                  <span class="odm-field-v">{{ orderDetailCard.user_name || '—' }}</span>
                </div>
                <div class="odm-field">
                  <span class="odm-field-k">电话</span>
                  <span class="odm-field-v odm-phone">{{ orderDetailCard.user_phone || '—' }}</span>
                </div>
                <div class="odm-field odm-field-block">
                  <span class="odm-field-k">地址</span>
                  <span class="odm-field-v">{{ orderDetailCard.address || '—' }}</span>
                </div>
              </div>
            </section>

            <section class="odm-section">
              <div class="odm-section-head">
                <i class="el-icon-goods"></i>
                <span>套餐信息</span>
              </div>
              <div class="odm-section-body">
                <div class="odm-product-name">{{ orderDetailCard.product_name || '—' }}</div>
                <div class="odm-field">
                  <span class="odm-field-k">配送餐次类型</span>
                  <el-select
                    v-if="orderDetailCard.order_type === '随心订'"
                    v-model="detailMealConfig"
                    size="small"
                    placeholder="请选择"
                    style="width: 160px;"
                  >
                    <el-option label="仅午餐" value="only_lunch" />
                    <el-option label="仅晚餐" value="only_dinner" />
                    <el-option label="午晚都有" value="both" />
                  </el-select>
                  <span v-else class="odm-field-v">{{ mealConfigLabel(orderDetailCard.meal_config) }}</span>
                </div>
                <div class="odm-field">
                  <span class="odm-field-k">餐次</span>
                  <span class="odm-field-v">{{ formatOrderMealSlotDisplay(orderDetailCard) }}</span>
                </div>
                <div class="odm-field">
                  <span class="odm-field-k">配送方式</span>
                  <span class="odm-field-v">{{ orderDetailCard.delivery_method || '同城配送' }}</span>
                </div>
                <div class="odm-field">
                  <span class="odm-field-k">支付方式</span>
                  <span class="odm-field-v">{{ orderDetailCard.payment_method_display || '—' }}</span>
                </div>
              </div>
            </section>
          </div>

          <section class="odm-section odm-section-full">
            <div class="odm-section-head">
              <i class="el-icon-date"></i>
              <span>订餐周期</span>
            </div>
            <div class="odm-section-body">
              <div class="odm-period-bar">{{ orderDetailCard.subscription_period || '—' }}</div>
              <div v-if="orderDetailCard.order_type === '随心订'" class="odm-meal-stats">
                <span>总餐 <strong>{{ orderDetailCard.total_meals != null ? orderDetailCard.total_meals : '—' }}</strong></span>
                <span>已用 <strong>{{ orderDetailCard.used_meals != null ? orderDetailCard.used_meals : '—' }}</strong></span>
                <span>剩余 <strong>{{ orderDetailCard.remaining_meals != null ? orderDetailCard.remaining_meals : '—' }}</strong></span>
                <span v-if="orderDetailCard.expected_end_date">预计结束 <strong>{{ orderDetailCard.expected_end_date }}</strong></span>
              </div>
              <div v-if="orderDetailCard.delay_log_summary" class="odm-delay-note">
                <i class="el-icon-warning-outline"></i>{{ orderDetailCard.delay_log_summary }}
              </div>
            </div>
          </section>

          <section class="odm-delivery-card">
            <div class="odm-section-head">
              <i class="el-icon-truck"></i>
              <span>配送与骑手</span>
            </div>
            <el-form label-position="top" size="small" class="odm-delivery-form">
              <div class="odm-delivery-grid">
                <el-form-item label="配送区域">
                  <el-select
                    v-model="detailDeliveryRegion"
                    placeholder="自动识别或手动选择"
                    filterable
                    clearable
                    style="width: 100%;"
                    @change="onDetailDeliveryRegionChange"
                  >
                    <el-option v-for="r in mergedDeliveryRegionOptions" :key="r" :label="r" :value="r" />
                  </el-select>
                </el-form-item>
                <el-form-item label="配送骑手">
                  <el-select
                    v-model="detailRiderId"
                    placeholder="待分配"
                    filterable
                    clearable
                    style="width: 100%;"
                  >
                    <el-option label="待分配" value="" />
                    <el-option
                      v-for="r in adminRiderOptions"
                      :key="r.id"
                      :label="detailRiderOptionLabel(r)"
                      :value="String(r.id)"
                    />
                  </el-select>
                </el-form-item>
              </div>
              <el-form-item label="小票手机号保护" class="odm-mask-phone-item">
                <el-switch
                  v-model="detailReceiptMaskPhone"
                  active-text="打印时隐藏顾客手机号中间四位"
                />
                <div class="odm-mask-phone-hint">开启后小票/映美打印脱敏；骑手管理小程序仍显示完整号码。</div>
              </el-form-item>
              <el-form-item v-if="orderDetailCard.order_type === '随心订'" label="本餐消耗餐次">
                <el-input-number v-model="detailMealCreditsCount" :min="1" :max="99" />
                <div class="odm-mask-phone-hint">
                  顾客一餐需多份出餐时设为 2 及以上；打印时将出对应张数小票，并核销相同数量的餐次。
                  <span v-if="orderDetailCard.remaining_meals != null">当前剩余 {{ orderDetailCard.remaining_meals }} 餐。</span>
                </div>
              </el-form-item>
            </el-form>
            <div v-if="!detailRiderId && orderDetailCard.rider_name" class="odm-current-rider">
              <i class="el-icon-user"></i>
              当前骑手：{{ orderDetailCard.rider_name }}
              <span v-if="orderDetailCard.rider_phone">（{{ orderDetailCard.rider_phone }}）</span>
            </div>
          </section>
        </template>
      </div>
      <span slot="footer" class="dialog-footer odm-footer">
        <el-button class="odm-btn-cancel" @click="orderDetailVisible = false">关闭</el-button>
        <el-button type="primary" class="odm-btn-save" :loading="orderDetailSaving" @click="saveOrderDetailDelivery">
          保存配送信息
        </el-button>
      </span>
    </el-dialog>

    <!-- 延期记录查看（平铺卡片） -->
    <el-dialog
      title="延期记录"
      :visible.sync="delayRecordDialogVisible"
      width="960px"
      top="5vh"
      custom-class="app-admin-dialog delay-record-modal"
    >
      <div v-loading="delayRecordLoading" class="delay-record-dialog-content">
        <div class="delay-record-dialog-hint">
          按当前订单关联的随心订计划拉取延期快照。删除请从<strong>最近一条</strong>开始逐条撤销。
        </div>
        <div v-if="delayRecordList.length" class="delay-record-tiles">
          <div v-for="(row, idx) in delayRecordList" :key="row.id || idx" class="delay-record-tile">
            <div class="drt-head">
              <span class="drt-date">{{ row.operation_date || '—' }}</span>
              <span class="drt-operator">{{ row.operator_name || '—' }}</span>
              <el-button
                v-if="canAdminDeleteSession && idx === 0"
                type="text"
                size="mini"
                class="danger-text drt-delete"
                @click="deleteDelayRecordItem(row)"
              >删除</el-button>
            </div>
            <div class="drt-body">
              <div class="drt-cell">
                <span class="drt-k">延期餐次</span>
                <span class="drt-v">{{ row.deferred_meal_date }} {{ row.deferred_meal_slot }}</span>
              </div>
              <div class="drt-cell">
                <span class="drt-k">顺延至</span>
                <span class="drt-v highlight">{{ row.new_meal_date || '—' }} {{ row.new_meal_slot || '' }}</span>
              </div>
              <div class="drt-cell">
                <span class="drt-k">延期前末餐</span>
                <span class="drt-v">{{ row.last_meal_date_before || '—' }} {{ row.last_meal_slot_before || '' }}</span>
              </div>
              <div class="drt-cell">
                <span class="drt-k">末餐顺延至</span>
                <span class="drt-v">{{ row.last_meal_date_after || '—' }} {{ row.last_meal_slot_after || '' }}</span>
              </div>
            </div>
          </div>
        </div>
        <admin-pagination
          :total="delayRecordPagination.total"
          :page.sync="delayRecordPagination.page"
          :page-size.sync="delayRecordPagination.pageSize"
          @change="onDelayRecordPaginationChange"
        />
        <div v-if="!delayRecordLoading && !delayRecordList.length" class="detail-line text-muted">
          暂无延期记录
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="exportDelayRecordForOrder">导出本单延期</el-button>
        <el-button type="primary" @click="delayRecordDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 发货备注编辑（大弹窗，按内容撑开） -->
    <el-dialog
      title="发货备注"
      :visible.sync="remarkDialogVisible"
      width="820px"
      top="6vh"
      custom-class="app-admin-dialog"
    >
      <div class="delay-record-dialog-hint">保存后将展示在列表与小票的备注区域（与订餐周期无关）。</div>
      <el-input
        v-model="remarkDraft"
        type="textarea"
        :autosize="{ minRows: 12, maxRows: 40 }"
        placeholder="请输入发货备注"
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="remarkDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="remarkSaving" @click="saveRemarkDialog">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量打印预览"
      :visible.sync="batchPreviewDialogVisible"
      width="760px"
      custom-class="app-admin-dialog"
    >
      <template v-if="batchPreview.mode === 'today'">
        <div class="delay-record-dialog-hint">
          今日：{{ batchPreview.today || '-' }}；餐次：{{ batchPreview.mealLabel || '-' }}；命中 {{ batchPreview.included.length }} 单，排除 {{ batchPreview.excluded.length }} 单
        </div>
        <el-divider content-position="left">会被打印</el-divider>
        <div v-if="!batchPreview.included.length" class="detail-line text-muted">暂无命中订单</div>
        <div v-else class="preview-list">
          <div v-for="item in batchPreview.included" :key="'in-' + item.order_id" class="preview-line">
            {{ item.order_no }} ｜ {{ item.user_name || '-' }} ｜ {{ formatMealSlotWithWindow(item.meal_slot || '-') }}
          </div>
        </div>
        <el-divider content-position="left">被排除（含原因）</el-divider>
        <div v-if="!batchPreview.excluded.length" class="detail-line text-muted">暂无排除订单</div>
        <div v-else class="preview-list">
          <div v-for="item in batchPreview.excluded" :key="'ex-' + item.order_id" class="preview-line">
            {{ item.order_no }} ｜ {{ item.user_name || '-' }} ｜ {{ item.reason || '-' }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="delay-record-dialog-hint">
          区间 {{ batchPreview.from }} ～ {{ batchPreview.to }}；餐次：{{ batchPreview.mealLabel || '-' }}；有餐日
          {{ batchPreview.periodDates.length }} 天（已跳过无餐日 {{ batchPreview.skippedCount }} 天）
        </div>
        <el-divider content-position="left">按日汇总（仅含至少一单的日子）</el-divider>
        <div v-if="!batchPreview.periodDates.length" class="detail-line text-muted">该区间内无待打印日</div>
        <div v-else class="preview-list period-preview-blocks">
          <div v-for="block in batchPreview.periodDates" :key="'pd-' + block.date" class="period-day-block">
            <div class="period-day-title">{{ block.date }}（{{ block.count }} 单）</div>
            <div v-for="item in block.orders" :key="block.date + '-' + item.order_id" class="preview-line">
              {{ item.order_no }} ｜ {{ item.user_name || '-' }} ｜ {{ formatMealSlotWithWindow(item.meal_slot || '-') }}
            </div>
          </div>
        </div>
      </template>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchPreviewDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 映美打印 / 手动核销 -->
    <el-dialog
      :title="printDialogMode === 'verify' ? '手动核销餐次' : '打印发货小票'"
      :visible.sync="printDialogVisible"
      width="520px"
      top="8vh"
      custom-class="app-admin-dialog print-receipt-dialog"
      :close-on-click-modal="false"
      @closed="resetPrintDialog"
    >
      <div v-if="printDialogOrder" class="print-dialog-body">
        <div class="print-order-card">
          <div class="print-order-card-top">
            <span class="print-order-product">{{ printDialogOrder.product_name || '—' }}</span>
            <span class="print-order-no">#{{ printDialogOrder.order_no || '—' }}</span>
          </div>
          <div class="print-order-user">
            {{ printDialogOrder.user_name || '—' }}
            <span v-if="printDialogOrder.user_phone" class="print-order-phone">{{ printDialogOrder.user_phone }}</span>
          </div>
          <div v-if="printDialogOrder.order_type === '随心订'" class="print-meal-stats">
            <span class="print-stat-chip">总 {{ printDialogOrder.total_meals != null ? printDialogOrder.total_meals : '—' }}</span>
            <span class="print-stat-chip used">用 {{ printDialogOrder.used_meals != null ? printDialogOrder.used_meals : '—' }}</span>
            <span class="print-stat-chip remain">剩 {{ printDialogOrder.remaining_meals != null ? printDialogOrder.remaining_meals : '—' }}</span>
            <span v-if="printDialogOrder.remaining_meals != null" class="print-stat-chip verify-pool">
              还可核销 {{ printDialogOrder.remaining_meals }} 餐
            </span>
          </div>
          <div v-if="printDialogOrder.address" class="print-order-address">{{ printDialogOrder.address }}</div>
        </div>

        <template v-if="printDialogOrder.order_type === '随心订'">
          <div class="print-section">
            <div class="print-section-label">
              <span>核销日期</span>
              <span class="print-section-hint">可选今日或过去漏核日期</span>
            </div>
            <el-date-picker
              v-model="printDialogForm.mealDate"
              type="date"
              placeholder="选择核销日期"
              value-format="yyyy-MM-dd"
              :picker-options="verifyPastDatePickerOptions"
              style="width: 100%;"
            />
          </div>

          <div v-if="printDialogSlots.length > 1" class="print-section">
            <div class="print-section-label">配送餐次（第 1 餐扣减位置）</div>
            <div v-loading="printDialogLoading" class="print-slot-grid">
              <button
                v-for="opt in printDialogSlotOptions"
                :key="opt.value"
                type="button"
                class="print-slot-card"
                :class="{ active: printDialogForm.mealSlot === opt.value }"
                @click="printDialogForm.mealSlot = opt.value"
              >
                <div class="print-slot-name">{{ opt.label }}</div>
                <div class="print-slot-window">{{ opt.window }}</div>
              </button>
            </div>
          </div>
          <div v-else-if="printDialogSlots.length === 1" class="print-section print-single-slot">
            <div class="print-section-label">配送餐次</div>
            <div class="print-single-slot-val">
              {{ formatMealSlotWithWindow(printDialogSlots[0]) }}
            </div>
          </div>

          <div class="print-section">
            <div class="print-section-label">{{ printDialogMode === 'verify' ? '核销餐次' : '打印张数' }}</div>
            <div class="print-count-row">
              <el-input-number
                v-model="printDialogForm.mealCreditsCount"
                :min="1"
                :max="printDialogMaxCredits"
                controls-position="right"
                size="medium"
              />
              <span class="print-count-hint">
                <template v-if="printDialogMode === 'verify'">
                  共核销 {{ printDialogForm.mealCreditsCount }} 餐，不打印小票
                </template>
                <template v-else>
                  每张小票核销 1 餐次，共核销 {{ printDialogForm.mealCreditsCount }} 餐
                </template>
              </span>
            </div>
            <div v-if="printDialogCrossDayHint" class="print-cross-day-hint">
              <i class="el-icon-info"></i>
              {{ printDialogCrossDayHint }}
            </div>
            <div v-if="printDialogForm.mealCreditsCount > 1" class="print-cross-day-hint">
              <i class="el-icon-info"></i>
              将一次核销 {{ printDialogForm.mealCreditsCount }} 餐：第 1 餐锚定配送日 {{ printDialogForm.mealSlot }}，其余按排餐顺序扣减（仅午餐/仅晚餐时只扣对应餐段）；连带餐保留各自排餐日；日历配送日显示 ×{{ printDialogForm.mealCreditsCount }}
            </div>
          </div>

          <div v-if="printDialogWarning" class="print-warning">
            <i class="el-icon-warning-outline"></i>
            {{ printDialogWarning }}
          </div>
        </template>

        <div v-else class="print-section print-non-flex-hint">
          非随心订订单，将直接提交飞鹅云打印（不核销餐次）。进单支付成功后也会自动出单。
        </div>
      </div>

      <span slot="footer" class="dialog-footer print-dialog-footer">
        <el-button @click="printDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :icon="printDialogMode === 'verify' ? 'el-icon-circle-check' : 'el-icon-printer'"
          :loading="printDialogSubmitting"
          :disabled="!printDialogCanSubmit"
          @click="submitPrintDialog"
        >{{ printDialogMode === 'verify' ? '确认核销' : '确认打印' }}</el-button>
      </span>
    </el-dialog>

    <!-- 随心订：周期内日历（餐次 / 延期） -->
    <el-dialog
      title="随心订订餐日历"
      :visible.sync="subCalendarVisible"
      width="1120px"
      top="5vh"
      custom-class="app-admin-dialog sub-calendar-dialog"
      @opened="onSubCalendarOpened"
    >
      <div v-loading="subCalendarLoading" class="sub-cal-wrap">
        <el-tabs v-model="subCalendarTab" @tab-click="onSubCalendarTabClick">
          <el-tab-pane label="订餐日历" name="calendar" />
          <el-tab-pane label="餐次明细" name="queue" />
          <el-tab-pane label="操作记录" name="ops" />
        </el-tabs>
        <template v-if="subCalendarTab === 'calendar'">
        <div class="sub-cal-toolbar">
          <el-radio-group v-model="subCalendarView" size="small" @change="loadSubscriptionCalendar">
            <el-radio-button label="month">月</el-radio-button>
            <el-radio-button label="week">周</el-radio-button>
          </el-radio-group>
          <el-button-group class="sub-cal-nav">
            <el-button size="small" icon="el-icon-arrow-left" @click="subCalendarPrev" />
            <el-button size="small" disabled>{{ subCalendarTitle }}</el-button>
            <el-button size="small" icon="el-icon-arrow-right" @click="subCalendarNext" />
          </el-button-group>
          <el-tag v-if="subCalendarMealConfigLabel" size="mini" type="info" effect="plain" class="sub-cal-meal-config-tag">
            配送餐次：{{ subCalendarMealConfigLabel }}
          </el-tag>
          <span v-if="subCalendarStats" class="muted sub-cal-stats">
            总{{ subCalendarStats.total }} · 剩{{ subCalendarStats.pending }} · 已核销{{ subCalendarStats.checked }}
            <template v-if="subCalendarStats.current_last_meal_date">
              · 末餐 {{ subCalendarStats.current_last_meal_date }} {{ subCalendarStats.current_last_meal_type_label || '' }}
            </template>
          </span>
          <p v-if="subCalendarTab === 'calendar'" class="sub-cal-stats-hint muted">
            日历按「当前排餐」显示；明细保留「原始排餐 / 当前排餐 / 核销时间」。同一日期+餐段可并列多个 tag（如 第3餐✓ + 第8餐 源）；已核销为历史只读，未核销可延期/核销
          </p>
          <el-button
            size="small"
            type="warning"
            plain
            :loading="subCalendarRegenerating"
            @click="openRegenerateMealDialog"
          >重新排餐</el-button>
        </div>
        <div class="sub-cal-legend">
          <span><span class="dot initial" /> 待核销（绿）</span>
          <span><span class="dot delayed" /> 已延期（橙）</span>
          <span><span class="dot delay-source" /> 延期源（橙+红框）</span>
          <span><span class="dot verified" /> 已核销（灰✓，只读）</span>
          <span><span class="dot rest" /> 休息日</span>
          <span class="muted">周期 {{ subCalendarMeta.start }} ～ {{ subCalendarMeta.end }} · {{ subCalendarEngine }}</span>
        </div>
        <div v-if="subCalendarTab === 'calendar'" class="sub-cal-coexist-rules">
          <strong>延期规则：</strong>
          {{ subCalendarDeferRuleHint }}
        </div>
        <div class="sub-cal-scroll">
        <div class="sub-cal-weekhead">
          <span v-for="w in subCalendarWeekLabels" :key="w">{{ w }}</span>
        </div>
        <div class="sub-cal-grid">
          <div
            v-for="cell in subCalendarCells"
            :key="cell.key"
            :class="['sub-cal-cell', cell.className]"
            @click="cell.day && onSubCalendarPickDay(cell.day)"
          >
            <template v-if="cell.day">
              <div class="sub-cal-dnum">{{ cell.dayNum }}</div>
              <div class="sub-cal-meal-slots">
                <template v-if="cell.queueMode">
                  <template v-if="cell.showLunch && cell.showDinner">
                    <div
                      v-for="tag in cell.lunchTags"
                      :key="'l-' + tag.id"
                      :class="[
                        'sub-cal-qtag',
                        'sub-cal-qtag-lunch',
                        'kind-' + tag.kind,
                        tag.skip_ahead ? 'is-skip-ahead' : '',
                        cell.lunchTags.length > 1 ? 'is-compact' : '',
                        queueTagInteractionClass(tag)
                      ]"
                      :title="queueTagTooltip(tag, cell.lunchTags)"
                      :style="tag.color ? { backgroundColor: tag.color, borderColor: tag.color } : null"
                      @click.stop="onQueueTagClick(tag)"
                    >{{ tag.label }}</div>
                    <div
                      v-for="tag in cell.dinnerTags"
                      :key="'d-' + tag.id"
                      :class="[
                        'sub-cal-qtag',
                        'sub-cal-qtag-dinner',
                        'kind-' + tag.kind,
                        tag.skip_ahead ? 'is-skip-ahead' : '',
                        cell.dinnerTags.length > 1 ? 'is-compact' : '',
                        queueTagInteractionClass(tag)
                      ]"
                      :title="queueTagTooltip(tag, cell.dinnerTags)"
                      :style="tag.color ? { backgroundColor: tag.color, borderColor: tag.color } : null"
                      @click.stop="onQueueTagClick(tag)"
                    >{{ tag.label }}</div>
                  </template>
                  <template v-else>
                    <div
                      v-for="tag in cell.queueTags"
                      :key="tag.id"
                      :class="[
                        'sub-cal-qtag',
                        'kind-' + tag.kind,
                        tag.skip_ahead ? 'is-skip-ahead' : '',
                        cell.queueTags.length > 2 ? 'is-compact' : '',
                        queueTagInteractionClass(tag)
                      ]"
                      :title="queueTagTooltip(tag, cell.queueTags)"
                      :style="tag.color ? { backgroundColor: tag.color, borderColor: tag.color } : null"
                      @click.stop="onQueueTagClick(tag)"
                    >{{ tag.label }}</div>
                  </template>
                </template>
                <template v-else>
                  <div
                    v-if="cell.showLunch"
                    :class="['sub-cal-slot', 'lunch', cell.lunchClass]"
                  >{{ cell.lunchLabel }}</div>
                  <div
                    v-if="cell.showDinner"
                    :class="['sub-cal-slot', 'dinner', cell.dinnerClass]"
                  >{{ cell.dinnerLabel }}</div>
                </template>
                <div
                  v-if="cell.day.is_rest && !cell.hasQueueTags && cell.lunchClass === 'empty' && cell.dinnerClass === 'empty'"
                  class="sub-cal-rest-mark"
                >{{ cell.day.day_name || '休' }}</div>
              </div>
            </template>
          </div>
        </div>
        </div>
        </template>
        <template v-else-if="subCalendarTab === 'queue'">
          <div class="sub-cal-queue-toolbar">
            <el-button
              type="primary"
              size="small"
              :disabled="!subCalendarQueueSelection.length"
              :loading="mealQueueVerifySubmitting"
              @click="batchVerifyMealQueue"
            >批量核销（{{ subCalendarQueueSelection.length }}）</el-button>
            <el-button
              size="small"
              type="warning"
              plain
              :loading="subCalendarRegenerating"
              @click="openRegenerateMealDialog"
            >生成 / 重新排餐</el-button>
            <el-button
              size="small"
              plain
              :loading="subCalendarUndoLastLoading"
              @click="undoLastMealQueueOp"
            >撤销上一步</el-button>
            <span class="muted">可跨日期勾选任意未核销餐次；已核销为历史事实不可延期</span>
          </div>
          <div v-if="!subCalendarMealQueueLoading && !subCalendarMealQueue.length" class="sub-cal-queue-empty">
            <p>暂无排餐明细，请先生成排餐</p>
            <el-button type="primary" size="small" :loading="subCalendarRegenerating" @click="openRegenerateMealDialog">生成排餐</el-button>
          </div>
          <div v-else class="sub-cal-queue-table-wrap">
          <el-table
            ref="mealQueueTable"
            class="meal-queue-table"
            v-loading="subCalendarMealQueueLoading"
            :data="subCalendarMealQueue"
            size="mini"
            border
            max-height="420"
            style="width: 100%; min-width: 880px"
            @selection-change="onMealQueueSelectionChange"
          >
            <el-table-column type="selection" width="46" fixed="left" :selectable="mealQueueRowSelectable" />
            <el-table-column label="餐次" width="80" align="center" class-name="meal-queue-idx-col">
              <template slot-scope="scope">第{{ scope.row.meal_index }}餐</template>
            </el-table-column>
            <el-table-column label="原始排餐" min-width="132" show-overflow-tooltip class-name="meal-queue-slot-col">
              <template slot-scope="scope">{{ formatMealQueueSlot(scope.row, 'original') }}</template>
            </el-table-column>
            <el-table-column label="当前排餐" min-width="132" show-overflow-tooltip class-name="meal-queue-slot-col">
              <template slot-scope="scope">{{ formatMealQueueSlot(scope.row, 'current') }}</template>
            </el-table-column>
            <el-table-column label="状态" width="88" align="center" class-name="meal-queue-status-col">
              <template slot-scope="scope">
                <el-tag size="mini" :type="mealQueueStatusTagType(scope.row)">{{ mealQueueStatusLabel(scope.row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="核销时间" width="172" show-overflow-tooltip class-name="meal-queue-time-col">
              <template slot-scope="scope">{{ $formatDateTime(scope.row.checked_at) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="180" fixed="right" class-name="meal-queue-op-col">
              <template slot-scope="scope">
                <el-button
                  v-if="mealQueueRowSelectable(scope.row)"
                  type="text"
                  size="mini"
                  :disabled="mealQueueDeferSubmitting"
                  @click="deferMealQueueRow(scope.row)"
                >延期</el-button>
                <el-button
                  v-if="mealQueueRowSelectable(scope.row)"
                  type="text"
                  size="mini"
                  @click="verifyMealQueueRow(scope.row)"
                >核销</el-button>
                <el-button
                  v-if="scope.row.status === 'checked'"
                  type="text"
                  size="mini"
                  class="danger-text"
                  @click="undoMealQueueCheck(scope.row)"
                >撤销核销</el-button>
                <el-button
                  v-if="scope.row.is_delay_source || scope.row.status === 'delayed'"
                  type="text"
                  size="mini"
                  class="danger-text"
                  @click="undoMealQueueDelay(scope.row)"
                >撤销延期</el-button>
              </template>
            </el-table-column>
          </el-table>
          </div>
        </template>
        <template v-else-if="subCalendarTab === 'ops'">
          <div class="sub-cal-queue-toolbar">
            <el-select v-model="mealOpsFilter" size="small" clearable placeholder="全部类型" style="width: 140px;" @change="loadMealOps">
              <el-option label="全部" value="" />
              <el-option label="核销" value="check" />
              <el-option label="延期" value="delay" />
              <el-option label="撤销核销" value="undo_check" />
              <el-option label="撤销延期" value="undo_delay" />
              <el-option label="重新排餐" value="regenerate" />
              <el-option label="撤销上一步" value="undo_last" />
            </el-select>
            <el-button size="small" icon="el-icon-refresh" @click="loadMealOps">刷新</el-button>
          </div>
          <el-table
            class="meal-ops-table"
            :data="mealOpsList"
            size="mini"
            border
            max-height="420"
            style="width: 100%"
            v-loading="mealOpsLoading"
          >
            <el-table-column label="时间" width="150">
              <template slot-scope="scope">{{ $formatDateTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="类型" width="100">
              <template slot-scope="scope">
                <el-tag size="mini" :type="mealOpTypeTagType(scope.row.operation_type)">
                  {{ mealOpTypeLabel(scope.row.operation_type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作人" width="100">
              <template slot-scope="scope">{{ scope.row.operator_name || scope.row.operator || '—' }}</template>
            </el-table-column>
            <el-table-column prop="summary" label="摘要" min-width="100" />
            <el-table-column label="涉及餐次 / 变更" min-width="200" class-name="meal-ops-change-col">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.touched_meals && scope.row.touched_meals.length"
                  effect="dark"
                  placement="top"
                  :open-delay="300"
                >
                  <div slot="content" class="meal-ops-tip">
                    <div v-for="(t, i) in scope.row.touched_meals" :key="'tip'+i" class="meal-ops-tip-item">
                      <div>第{{ t.meal_index }}餐</div>
                      <div>变更前：{{ t.before || '—' }}</div>
                      <div>变更后：{{ t.after || '—' }}</div>
                    </div>
                  </div>
                  <div class="meal-ops-change">
                    <div
                      v-for="(t, i) in (scope.row.touched_meals || []).slice(0, 4)"
                      :key="i"
                      class="meal-ops-change-block"
                    >
                      <div class="meal-ops-idx">第{{ t.meal_index }}餐</div>
                      <div class="meal-ops-before">变更前：{{ t.before || '—' }}</div>
                      <div class="meal-ops-after">变更后：{{ t.after || '—' }}</div>
                    </div>
                  </div>
                </el-tooltip>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="!mealOpsLoading && !mealOpsList.length" class="sub-cal-queue-empty muted">
            暂无操作记录
          </div>
        </template>
        <div
          v-if="subCalendarTab === 'calendar' && subCalendarDeferActionHint"
          class="sub-cal-defer-action-hint"
        >
          {{ subCalendarDeferActionHint }}
        </div>
        <div
          v-if="subCalendarTab === 'calendar' && subCalendarRegenerateSummary"
          class="sub-cal-regen-summary"
        >
          {{ subCalendarRegenerateSummary }}
        </div>
        <div
          v-else-if="subCalendarTab === 'calendar' && !isQueueCalendarEngine && subCalendarDeferralList.length"
          class="sub-cal-deferral-all"
        >
          <div class="sub-cal-detail-title">延期说明</div>
          <div
            v-for="d in subCalendarDeferralList"
            :key="'all-d-' + d.id"
            class="sub-cal-delay-tile"
          >
            <div class="scd-main">
              <span class="scd-label">顺延餐次</span>
              {{ d.deferred_meal_date }} {{ d.deferred_meal_slot }}
              <i class="el-icon-right"></i>
              {{ d.new_meal_date || '—' }} {{ d.new_meal_slot || d.deferred_meal_slot }}
              <template v-if="d.landing_consumed">（已核销 · 该补餐已被配送扣减）</template>
              <template v-else-if="String(d.operator_name || '').indexOf('回填') >= 0">（绿色 · 回填末餐）</template>
              <template v-else>（绿色 · 正常补餐）</template>
            </div>
            <div
              v-if="d.last_meal_date_after && !d.landing_at_plan_end && (d.last_meal_date_after !== d.new_meal_date || d.last_meal_slot_after !== d.new_meal_slot)"
              class="scd-sub muted"
            >
              原末餐顺延至 {{ d.last_meal_date_after }} {{ d.last_meal_slot_after || '' }}（绿色）
            </div>
          </div>
        </div>
        <div v-if="subCalendarTab === 'calendar' && subCalendarDayDetail" class="sub-cal-detail sub-cal-detail-flat">
          <div class="sub-cal-detail-title">选中：{{ subCalendarDayDetail.date }} · {{ formatCalendarDaySummary(subCalendarDayDetail) }}</div>
          <div v-if="subCalendarDayDetail.verify_records && subCalendarDayDetail.verify_records.length" class="sub-cal-verify-tiles">
            <div
              v-for="vr in subCalendarDayDetail.verify_records"
              :key="'vr-' + (vr.schedule_id || vr.batch_id || vr.meal_slot)"
              class="sub-cal-verify-tile"
              :class="'role-' + (vr.role || 'single')"
            >
              <div class="scv-main">
                {{ vr.meal_slot }}
                <template v-if="vr.batch_total > 1">
                  · 一次配送核销 {{ vr.batch_total }} 餐
                  <span class="scv-tag">{{ vr.role === 'anchor' ? '配送日' : '连带核销' }}</span>
                </template>
                <template v-else> · 已核销</template>
              </div>
              <div v-if="vr.batch_total > 1 && vr.role !== 'anchor'" class="scv-sub muted">
                归属配送日 {{ vr.anchor_date }} {{ vr.anchor_slot }}（第 {{ vr.batch_index }}/{{ vr.batch_total }} 餐）
                <template v-if="vr.deduct_date"> · 扣减 {{ vr.deduct_date }} {{ vr.deduct_slot || vr.meal_slot }}</template>
              </div>
              <div v-else-if="vr.batch_total > 1 && vr.role === 'anchor'" class="scv-sub muted">
                配送日共核销 {{ vr.batch_total }} 餐 · 第 1 餐扣 {{ vr.anchor_date }} {{ vr.anchor_slot }}，其余从周期末往前扣
                <template v-if="vr.deducts && vr.deducts.length">
                  ：{{
                    vr.deducts
                      .map(
                        (d) =>
                          d.meal_date +
                          ' ' +
                          d.meal_slot +
                          (d.absorbed ? '（已回填）' : '')
                      )
                      .join('、')
                  }}
                </template>
              </div>
              <el-button
                v-if="canAdminDeleteSession && vr.role !== 'linked' && (vr.schedule_id || vr.batch_id)"
                type="text"
                size="mini"
                class="danger-text"
                @click="restoreCalendarVerify(vr)"
              >撤销核销</el-button>
            </div>
          </div>
          <div v-if="subCalendarDayDetail.delays.length" class="sub-cal-delay-tiles">
            <div
              v-for="d in subCalendarDayDetail.delays"
              :key="d.id"
              class="sub-cal-delay-tile"
            >
              <div class="scd-main">
                <span class="scd-label">顺延餐次</span>
                {{ d.original_date }} {{ d.meal_type }}
                <i class="el-icon-right"></i>
                {{ d.new_date || '—' }} {{ d.new_meal_slot || d.meal_type }}
              </div>
              <div
                v-if="d.last_shift_date && !d.landing_at_plan_end && (d.last_shift_date !== d.new_date || d.last_shift_slot !== d.new_meal_slot)"
                class="scd-sub muted"
              >
                原末餐顺延至 {{ d.last_shift_date }} {{ d.last_shift_slot || '' }}（绿色，非紫色顺延餐）
              </div>
              <el-button
                v-if="canAdminDeleteSession"
                type="text"
                size="mini"
                class="danger-text"
                @click="deleteSubscriptionDelayRow(d)"
              >删除</el-button>
            </div>
          </div>
          <div
            v-if="subCalendarDayHasPendingVerify(subCalendarDayDetail)"
            class="sub-cal-day-actions"
            style="margin-top: 12px;"
          >
            <el-button
              type="primary"
              size="small"
              icon="el-icon-circle-check"
              :loading="batchVerifyPastSubmitting"
              @click="verifyCalendarDayPending(subCalendarDayDetail)"
            >核销本日未用餐</el-button>
            <span class="form-item-hint" style="margin-left: 8px;">将本订单该日仍为绿色的餐次补核销</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="subCalendarVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 重新排餐：显式选择餐次类型 -->
    <el-dialog
      title="重新排餐"
      :visible.sync="regenerateMealDialogVisible"
      width="460px"
      append-to-body
      custom-class="app-admin-dialog"
      :close-on-click-modal="false"
    >
      <p class="form-item-hint" style="margin: 0 0 12px;">
        请手动选择本单配送餐次类型（须与订单实际配置一致）。日历按<strong>当前排餐</strong>显示；明细中的「原始排餐」仅供参考。
      </p>
      <p
        v-if="regenerateMealSuggestedConfig"
        class="form-item-hint"
        style="margin: 0 0 12px; color: #606266;"
      >
        系统识别订单配置为：{{ mealConfigLabel(regenerateMealSuggestedConfig) }}（已自动选中，请核对后确认）
      </p>
      <el-alert
        v-if="!regenerateMealForm.meal_config"
        type="info"
        :closable="false"
        title="请选择配送餐次类型后再确认重新排餐"
        style="margin-bottom: 12px;"
      />
      <el-radio-group v-model="regenerateMealForm.meal_config">
        <el-radio label="only_lunch">仅午餐（每天 1 餐）</el-radio>
        <el-radio label="only_dinner">仅晚餐（每天 1 餐）</el-radio>
        <el-radio label="both">午晚都有（每天 2 餐）</el-radio>
      </el-radio-group>
      <p class="form-item-hint" style="margin: 12px 0 0;">
        将从订餐周期起始日全部重新排餐：已核销、延期记录都会清空，按所选类型重新生成全部待核销餐次。
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="regenerateMealDialogVisible = false">取消</el-button>
        <el-button
          type="warning"
          :loading="subCalendarRegenerating"
          @click="confirmRegenerateSubscriptionMeals"
        >确认重新排餐</el-button>
      </span>
    </el-dialog>

    <!-- 延期：选择目标日期/餐别 -->
    <el-dialog
      title="延期餐次"
      :visible.sync="deferMealDialogVisible"
      width="480px"
      append-to-body
      custom-class="app-admin-dialog"
      :close-on-click-modal="false"
    >
      <p class="form-item-hint" style="margin: 0 0 12px;">
        第 {{ deferMealForm.meal_index }} 餐：当前计划
        {{ formatMealQueueDate(deferMealForm.current_date) }}
        {{ deferMealForm.current_meal_type === 'dinner' ? '晚餐' : '午餐' }}
      </p>
      <el-form label-width="96px" size="small">
        <el-form-item label="目标日期">
          <el-date-picker
            v-model="deferMealForm.targetDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择延期目标日期"
            :disabled="deferMealForm.autoSlot"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="目标餐别">
          <el-radio-group v-model="deferMealForm.targetPeriod" :disabled="deferMealForm.autoSlot">
            <el-radio label="lunch">午餐</el-radio>
            <el-radio label="dinner">晚餐</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="自动顺延">
          <el-checkbox v-model="deferMealForm.autoSlot">自动找下一个同餐别空槽</el-checkbox>
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="deferMealForm.reason" type="textarea" :rows="2" placeholder="可选" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deferMealDialogVisible = false">取消</el-button>
        <el-button
          type="warning"
          :loading="mealQueueDeferSubmitting"
          @click="confirmDeferMealQueue"
        >确认延期</el-button>
      </span>
    </el-dialog>

    <!-- 一键补核销（过去漏核日期） -->
    <el-dialog
      title="一键补核销"
      :visible.sync="batchVerifyPastVisible"
      width="920px"
      top="6vh"
      custom-class="app-admin-dialog"
      :close-on-click-modal="false"
    >
      <div class="print-dialog-body">
        <p class="form-item-hint" style="margin: 0 0 12px;">
          选择漏核的配送日期，系统会将该日所有仍为「未用餐」的随心订餐次一次性核销（不打印小票）。例如 7 号忘记核销，选 2026-08-07 即可。
        </p>
        <div class="print-section">
          <div class="print-section-label">补核销日期</div>
          <el-date-picker
            v-model="batchVerifyPastForm.date"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            :picker-options="verifyPastDatePickerOptions"
            style="width: 100%;"
            @change="previewBatchVerifyPast"
          />
        </div>
        <div class="print-section">
          <div class="print-section-label">餐次筛选</div>
          <MealSlotSegment v-model="batchVerifyPastForm.mealSlot" @change="previewBatchVerifyPast" />
        </div>
        <div v-loading="batchVerifyPastLoading" class="print-section">
          <div class="print-section-label">待核销预览</div>
          <div v-if="batchVerifyPastPreview">
            <p style="margin: 0 0 8px;">
              {{ batchVerifyPastPreview.message || (`共 ${batchVerifyPastPreview.pending || 0} 笔`) }}
            </p>
            <el-table
              v-if="batchVerifyPastPreview.items && batchVerifyPastPreview.items.length"
              :data="batchVerifyPastPreview.items"
              size="mini"
              max-height="320"
              stripe
            >
              <el-table-column prop="order_no" label="订单号" min-width="150" show-overflow-tooltip />
              <el-table-column prop="user_name" label="顾客姓名" width="96" show-overflow-tooltip />
              <el-table-column prop="user_phone" label="联系方式" width="120" show-overflow-tooltip />
              <el-table-column prop="product_name" label="套餐类型" min-width="120" show-overflow-tooltip />
              <el-table-column prop="package_meal_slots" label="套餐餐次" width="110" show-overflow-tooltip />
              <el-table-column prop="meal_slot" label="本次餐次" width="80" />
              <el-table-column prop="count" label="餐数" width="64" />
            </el-table>
          </div>
          <div v-else class="form-item-hint">请选择日期后自动预览</div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchVerifyPastVisible = false">取消</el-button>
        <el-button
          type="primary"
          icon="el-icon-circle-check"
          :loading="batchVerifyPastSubmitting"
          :disabled="!batchVerifyPastCanSubmit"
          @click="submitBatchVerifyPast"
        >确认补核销</el-button>
      </span>
    </el-dialog>

    <!-- 批量延期 -->
    <el-dialog
      title="批量延期"
      :visible.sync="batchDelayVisible"
      width="520px"
      custom-class="app-admin-dialog"
      :close-on-click-modal="false"
    >
      <div class="print-dialog-body">
        <p class="batch-dialog-summary">已选择 <strong>{{ selectedOrderIds.length }}</strong> 个订单</p>
        <div class="print-section">
          <div class="print-section-label">延期餐次</div>
          <el-radio-group v-model="batchDelayForm.delayMode" @change="onBatchDelayModeChange">
            <el-radio label="1">延期 1 餐</el-radio>
            <el-radio label="2">延期 2 餐</el-radio>
            <el-radio label="custom">自定义餐数</el-radio>
          </el-radio-group>
          <el-input-number
            v-if="batchDelayForm.delayMode === 'custom'"
            v-model="batchDelayForm.customMeals"
            :min="1"
            :max="30"
            size="small"
            style="margin-top: 10px;"
          />
        </div>
        <p class="form-item-hint" style="margin-top: 12px;">
          所选订单的下一餐将从其后一餐段起顺延，跳过休息日。仅移动延期源，其余未核销保持原位，可与已有餐次同格显示。
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchDelayVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="batchDelaySubmitting"
          :disabled="batchDelaySubmitting"
          @click="submitBatchDelay"
        >确认延期</el-button>
      </span>
    </el-dialog>

    <!-- 批量核销（选中订单） -->
    <el-dialog
      title="批量核销"
      :visible.sync="batchCheckVisible"
      width="920px"
      top="6vh"
      custom-class="app-admin-dialog"
      :close-on-click-modal="false"
    >
      <div class="print-dialog-body">
        <p class="batch-dialog-summary">已选择 <strong>{{ selectedOrderIds.length }}</strong> 个订单</p>
        <div class="print-section">
          <div class="print-section-label">核销日期</div>
          <el-date-picker
            v-model="batchCheckForm.checkDate"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
            @change="previewBatchCheck"
          />
        </div>
        <div class="print-section">
          <div class="print-section-label">餐次筛选</div>
          <MealSlotSegment v-model="batchCheckForm.mealType" @change="previewBatchCheck" />
        </div>
        <div v-loading="batchCheckLoading" class="print-section">
          <div class="print-section-label">待核销预览</div>
          <div v-if="batchCheckPreview">
            <p style="margin: 0 0 8px;">
              {{ batchCheckPreview.message || (`共 ${batchCheckPreview.pending || 0} 笔可核销`) }}
            </p>
            <el-table
              v-if="batchCheckPreview.items && batchCheckPreview.items.length"
              :data="batchCheckPreview.items"
              size="mini"
              max-height="320"
              stripe
            >
              <el-table-column prop="order_no" label="订单号" min-width="150" show-overflow-tooltip />
              <el-table-column prop="user_name" label="顾客姓名" width="96" show-overflow-tooltip />
              <el-table-column prop="meal_slot" label="核销餐次" width="88" />
              <el-table-column prop="message" label="说明" min-width="180" show-overflow-tooltip />
            </el-table>
          </div>
          <div v-else class="form-item-hint">请选择日期后自动预览</div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchCheckVisible = false">取消</el-button>
        <el-button
          type="primary"
          icon="el-icon-circle-check"
          :loading="batchCheckSubmitting"
          :disabled="!batchCheckCanSubmit"
          @click="submitBatchCheck"
        >确认核销</el-button>
      </span>
    </el-dialog>

    <transition name="batch-bar-fade">
      <div v-if="isPackageDeliveryMain && selectedOrderIds.length" class="order-batch-float-bar">
        <span class="order-batch-float-count">已选 {{ selectedOrderIds.length }} 项</span>
        <el-button
          size="small"
          :loading="batchDelaySubmitting"
          :disabled="batchDelaySubmitting || batchCheckSubmitting || batchDeleteSubmitting"
          @click="openBatchDelayDialog"
        >批量延期</el-button>
        <el-button
          size="small"
          type="primary"
          :loading="batchCheckSubmitting"
          :disabled="batchDelaySubmitting || batchCheckSubmitting || batchDeleteSubmitting"
          @click="openBatchCheckDialog"
        >批量核销</el-button>
        <el-button
          size="small"
          :loading="subscriptionExportLoading"
          :disabled="subscriptionExportLoading || batchDeleteSubmitting"
          @click="exportSubscriptionOrders"
        >批量导出</el-button>
        <el-button
          v-if="canAdminDeleteSession"
          size="small"
          type="danger"
          plain
          class="order-batch-float-delete"
          :loading="batchDeleteSubmitting"
          :disabled="batchDelaySubmitting || batchCheckSubmitting || batchDeleteSubmitting"
          @click="batchDeleteSelectedOrders"
        >批量删除</el-button>
        <el-button size="small" type="text" @click="clearOrderSelection">取消选择</el-button>
      </div>
    </transition>
  </div>
</template>

<script>
import { isRestDay, businessDaysInclusiveEnd, formatDate as fmtCalDate } from '../utils/calendarUtils';
import { formatDateTime, formatDate as formatBjDate } from '../utils/dateTimeUtils';
import axios from 'axios';
import { apiBase } from '../api/client';
import { getUserInfo, canAdminDelete, getAdminToken, getAdminAuthHeader } from '../utils/auth';
import appConfirm from '../utils/appConfirm';
import { guardUploadFile, formatImportErrors, showImportResult } from '../utils/fileImport';
import { downloadBlobResponse } from '../utils/fileExport';
import MealSlotSegment from '../components/MealSlotSegment.vue';
import {
  validateDeferSuccessResponse,
  extractDeferErrorMessage,
  validateBatchDeferResponse
} from '../utils/deferResponse';

/** 收银台订单列表：收银台单 + 小程序零售单（取餐叫号同一页展示，不含随心订） */
const SITUATION_ORDER_NO_RE = /^Y\d{11}$/

function isLikelyMiniProgramRetail(o) {
  if (!o) return false
  if (String(o.order_type || '').trim() === '随心订') return false
  const note = String(o.order_note || '').trim()
  if (note === '小程序') return true
  const no = String(o.order_no || '')
  // 小程序支付页订单号以 E 开头
  if (/^E\d/i.test(no)) return true
  const pm = String(o.payment_method || '').trim()
  if (pm === 'mini_order' || pm === 'voucher' || pm.indexOf('mini') >= 0) return true
  return false
}

function rawOrderIsCashierSituationRow(o) {
  if (!o) return false
  // 随心订只进「随心订订单」
  if (String(o.order_type || '').trim() === '随心订') return false

  const note = String(o.order_note || '').trim()
  if (note === '收银台') return true
  if (Number(o.is_display) === 1) return true
  const ps = String(o.pickup_status || '')
  if (ps === 'preparing' || ps === 'ready' || (ps === 'completed' && o.pickup_code)) return true
  const no = String(o.order_no || '')
  if (SITUATION_ORDER_NO_RE.test(no)) return true
  if (/^ORD\d/.test(no) && String(o.order_type || '').trim() === '普通') {
    if (String(o.pickup_method || '').includes('店') || String(o.pickup_method || '').includes('自取')) {
      return true
    }
  }
  return false
}

/** 门店出餐板：收银台 + 小程序零售（同一列表） */
function rawOrderIsSituationBoardRow(o) {
  if (!o) return false
  if (String(o.order_type || '').trim() === '随心订') return false
  if (rawOrderIsCashierSituationRow(o)) return true
  if (isLikelyMiniProgramRetail(o)) return true
  const ot = String(o.order_type || '').trim()
  return ot === '普通' || ot === '零售' || ot === ''
}

/** 已退款 / 已退单 / 待处理退款（含审核中） */
function rawOrderIsRefundRelatedRow(o) {
  const s = String((o && o.order_status) || '').trim()
  return s === '已退款' || s === '已退单' || s === '退款审核中'
}

/** 线上「小程序订单」：非随心订的小程序零售单（仍保留完整小程序列表） */
function rawOrderIsRetailMiniRow(o) {
  if (!o) return false
  if (String(o.order_type || '').trim() === '随心订') return false
  // 纯收银台单不进「小程序订单」页
  if (rawOrderIsCashierSituationRow(o) && !isLikelyMiniProgramRetail(o)) return false
  if (isLikelyMiniProgramRetail(o)) return true
  const ot = String(o.order_type || '').trim()
  return ot === '普通' || ot === '零售' || ot === ''
}

function rawOrderIsSubscriptionMiniRow(o) {
  if (!o) return false
  return String(o.order_type || '').trim() === '随心订' && String(o.order_status || '').trim() !== '已取消'
}

export default {
  name: 'OrderManagement',
  components: { MealSlotSegment },
  data() {
    return {
      orders: [],
      allOrders: [],
      total: 0,
      pageSize: 30,
      currentPage: 1,
      searchForm: {
        orderSearchType: 'order_no',
        keyword: '',
        timeSearchType: 'create_time',
        startDate: '',
        endDate: '',
        userName: '',
        userPhone: '',
        orderStatus: '',
        deliveryMethod: '',
        paymentMethod: '',
        mealSlot: ''
      },
      datePickerOptions: {
        cellClassName(time) {
          let d = time;
          if (time && typeof time === 'object' && time.date) d = time.date;
          if (!(d instanceof Date)) return '';
          try {
            if (isRestDay(d)) return 'rest-day-cell';
          } catch (e) {}
          return '';
        }
      },
      orderDialogVisible: false,
      orderFormLoading: false,
      orderDialogMode: 'create',
      currentOrderIndex: null,
      customerSearchQuery: '',
      customerUserList: [],
      customerPhoneLookupHint: '',
      customerPhoneBindHint: '',
      orderForm: {
        order_no: '',
        user_id: '',
        user_name: '',
        consignee_name: '',
        user_phone: '',
        address: '',
        product_name: '',
        productSelectKey: '',
        manual_target_total_meals: null,
        unit_price: 0,
        quantity: 1,
        total_price: 0,
        order_type: '随心订',
        order_status: '待发货',
        subscription_period: '',
        subscription_date_range: null,
        subscription_period_start: '',
        meal_slots: ['午餐'],
        meal_config: 'both',
        delivery_method: '同城配送',
        payment_method: 'mini_order',
        shipping_fee: 0,
        order_note: '',
        delivery_region: '',
        rider_id: '',
        rider_name: '',
        rider_phone: '',
        createdAt: '',
        historical_order: false,
        historical_delivery_mode: 'past_date',
        historical_total_meals: 0,
        historical_meals_delivered: 0,
        plan_link_mode: 'new',
        link_subscription_id: ''
      },
      extendDialogVisible: false,
      legacyExtendDialogVisible: false,
      scheduleFilterDate: '',
      scheduleFilterActive: false,
      scheduleFilterMatchKeys: [],
      scheduleFilterOrderIds: [],
      subscriptionExportLoading: false,
      todayMealOrders: [],
      showScheduleTable: false,
      mealWs: null,
      extendForm: {
        original_date: '',
        new_date: '',
        meal_type: 'lunch',
        meal_slot: '午餐',
        also_dinner: false
      },
      extendTargetOrder: null,
      chargebackDialogVisible: false,
      chargebackForm: { reason: '', print_receipt: true },
      chargebackTargetOrder: null,
      productMealsMode: 'both',
      productTotalMeals: 0,
      productAllowSingleMeal: false,
      subscriptionPeriodHint: '',
      subscriptionPeriodError: '',
      schedulePreviewVisible: false,
      schedulePreviewData: null,
      schedulePreviewPendingSubmit: false,
      schedulePreviewLoading: false,
      schedulePreviewPage: 1,
      schedulePreviewPageSize: 30,
      jolimarkPeriodRange: null,
      subCalendarVisible: false,
      subCalendarLoading: false,
      subCalendarTab: 'calendar',
      subCalendarView: 'month',
      subCalendarYear: 2026,
      subCalendarMonth: 1,
      subCalendarWeekStart: '',
      subCalendarData: null,
      subCalendarMealQueueList: [],
      subCalendarMealQueueLoading: false,
      subCalendarOrder: null,
      subCalendarDayDetail: null,
      subCalendarQueueSelection: [],
      subCalendarRegenerating: false,
      subCalendarUndoLastLoading: false,
      subCalendarRegenerateSummary: '',
      regenerateMealDialogVisible: false,
      regenerateMealForm: { meal_config: '' },
      regenerateMealSuggestedConfig: '',
      mealQueueVerifySubmitting: false,
      mealQueueDeferSubmitting: false,
      deferMealDialogVisible: false,
      deferMealForm: {
        meal_id: '',
        meal_index: 0,
        current_date: '',
        current_meal_type: 'lunch',
        targetDate: '',
        targetPeriod: 'lunch',
        autoSlot: false,
        reason: ''
      },
      subCalendarDeferActionHint: '',
      mealOpsList: [],
      mealOpsLoading: false,
      mealOpsFilter: '',
      orderProductList: [],
      orderProductFilterQuery: '',
      /** 与 RiderInfo.vue 骑手管理区域选项一致 */
      deliveryRegionOptions: [
        '温岭太平',
        '温岭城东',
        '温岭城西',
        '温岭城北',
        '温岭横峰',
        '温岭城南',
        '温岭泽国',
        '温岭温西'
      ],
      orderDetailVisible: false,
      orderDetailLoading: false,
      orderDetailSaving: false,
      orderDetailCard: null,
      detailDeliveryRegion: '',
      detailRiderId: '',
      detailReceiptMaskPhone: false,
      detailMealCreditsCount: 1,
      detailMealConfig: '',
      detailMealConfigOriginal: '',
      adminDeliveryRegions: [],
      adminRiderOptions: [],
      delayRecordDialogVisible: false,
      delayRecordList: [],
      delayRecordLoading: false,
      delayRecordPagination: { page: 1, pageSize: 30, total: 0 },
      delayRecordOrder: null,
      batchPreviewDialogVisible: false,
      batchPreview: {
        mode: 'today',
        today: '',
        meal: 'both',
        mealLabel: '午餐+晚餐',
        included: [],
        excluded: [],
        from: '',
        to: '',
        periodDates: [],
        skippedCount: 0
      },
      printDialogVisible: false,
      printDialogMode: 'print',
      printDialogOrder: null,
      printDialogLoading: false,
      printDialogSubmitting: false,
      printDialogSlots: [],
      printDialogForm: {
        mealCreditsCount: 1,
        mealSlot: '午餐',
        mealDate: ''
      },
      printDialogAvailability: {
        today: '',
        lunch: 0,
        dinner: 0,
        lunchPool: 0,
        dinnerPool: 0
      },
      batchVerifyPastVisible: false,
      batchVerifyPastLoading: false,
      batchVerifyPastSubmitting: false,
      batchVerifyPastForm: {
        date: '',
        mealSlot: ''
      },
      batchVerifyPastPreview: null,
      selectedOrderIds: [],
      batchDelayVisible: false,
      batchDelaySubmitting: false,
      batchDelayForm: {
        delayMode: '1',
        customMeals: 3
      },
      batchCheckVisible: false,
      batchCheckLoading: false,
      batchCheckSubmitting: false,
      batchCheckForm: {
        checkDate: '',
        mealType: ''
      },
      batchCheckPreview: null,
      batchDeleteSubmitting: false,
      verifyPastDatePickerOptions: {
        disabledDate(time) {
          const end = new Date()
          end.setHours(23, 59, 59, 999)
          return time.getTime() > end.getTime()
        }
      }
    }
  },
  mounted() {
    this.getOrders();
    this.loadOrderProducts();
    this.loadCustomerUsers();
    this.loadAdminDeliveryMeta();
    if (this.isPackageDeliveryMain) {
      this.scheduleFilterDate = this.formatDatePickerVal(new Date());
      this.connectMealWebSocket();
    }
  },
  watch: {
    '$route.fullPath'() {
      this.currentPage = 1
      this.getOrders()
      if (this.isPackageDeliveryMain && !this.mealWs) {
        this.scheduleFilterDate = this.scheduleFilterDate || this.formatDatePickerVal(new Date())
        this.connectMealWebSocket()
      }
    }
  },
  beforeDestroy() {
    if (this.mealWs) {
      try { this.mealWs.close(); } catch (e) { /* ignore */ }
      this.mealWs = null;
    }
  },
  computed: {
    isPackageDelivery() {
      return this.$route.path === '/orders'
    },
    isOrderQueryTab() {
      return this.isPackageDelivery && String(this.$route.query.tab || 'order') === 'search'
    },
    isRefundTab() {
      return this.isPackageDelivery && String(this.$route.query.tab || '') === 'refund'
    },
    isPackageDeliveryMain() {
      return (
        this.isPackageDelivery &&
        !this.isOrderQueryTab &&
        !this.isRefundTab
      )
    },
    deferTodayLabel() {
      return this.getChinaTodayStr()
    },
    isOrderSituation() {
      return this.$route.path === '/orders/situation'
    },
    canAdminDeleteSession() {
      return canAdminDelete()
    },
    listTitle() {
      if (this.$route.path === '/orders/situation') return '收银台订单（含小程序）'
      if (this.isRefundTab) return '退款订单'
      return '随心订订单'
    },
    catalogPackagePrice() {
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      return p ? this.resolveProductPackagePrice(p) : 0
    },
    catalogPackagePriceMismatch() {
      if (!this.isPackageDeliveryMain || !this.orderForm.productSelectKey) return false
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      if (!this.isSubscriptionCatalogProduct(p)) return false
      const catalog = this.catalogPackagePrice
      const current = Number(this.orderForm.unit_price) || 0
      return catalog > 0 && Math.abs(catalog - current) > 0.01
    },
    activeStatusBox() {
      return this.searchForm.orderStatus || ''
    },
    statusBoxItems() {
      const list = Array.isArray(this.allOrders) ? this.allOrders : []
      const countBy = (val) => {
        if (!val) return list.length
        if (val === '延期') return list.filter((o) => this.hasOrderDelay(o)).length
        return list.filter((o) => String(o.order_status || '') === val).length
      }
      if (this.isRefundTab) {
        return [
          { label: '全部', value: '', count: countBy('') },
          { label: '退款审核中', value: '退款审核中', count: countBy('退款审核中') },
          { label: '已退款', value: '已退款', count: countBy('已退款') },
          { label: '已退单', value: '已退单', count: countBy('已退单') }
        ]
      }
      if (this.isOrderQueryTab) {
        return [
          { label: '全部', value: '', count: countBy('') },
          { label: '已支付', value: '已支付', count: countBy('已支付') },
          { label: '进行中', value: '进行中', count: countBy('进行中') },
          { label: '待支付', value: '待支付', count: countBy('待支付') },
          { label: '已完成', value: '已完成', count: countBy('已完成') },
          { label: '已取消', value: '已取消', count: countBy('已取消') }
        ]
      }
      return [
        { label: '全部', value: '', count: countBy('') },
        { label: '进行中', value: '进行中', count: countBy('进行中') },
        { label: '延期', value: '延期', count: countBy('延期') },
        { label: '已完成', value: '已完成', count: countBy('已完成') },
        { label: '待支付', value: '待支付', count: countBy('待支付') }
      ]
    },
    extendOriginalDatePickerOptions () {
      return {
        disabledDate: (time) => {
          // 禁用休息日
          try {
            if (isRestDay(time)) return true;
          } catch (e) {}
          
          // 只能选择当前订餐周期内的日期
          const range = this.getExtendSubscriptionRange();
          if (range) {
            if (time < range.start || time > range.end) {
              return true;
            }
          }
          
          return false;
        },
        cellClassName: this.extendCellClass
      }
    },
    extendNewDatePickerOptions () {
      return {
        disabledDate: (time) => {
          try {
            if (isRestDay(time)) return true;
          } catch (e) {}
          const minStr = this.getExtendMinNewDateStr()
          if (!minStr) return true
          const tStr = this.formatDatePickerVal(time)
          if (tStr < minStr) return true
          return false;
        },
        cellClassName: this.extendCellClass
      }
    },
    extendShowAlsoDinnerOption () {
      const sub = this.extendTargetOrder && this.extendTargetOrder.subscription_id
      if (!sub || typeof sub !== 'object') return false
      if (this.extendForm.meal_type !== 'lunch') return false
      const slots = Array.isArray(sub.meal_slots) ? sub.meal_slots : []
      return slots.some((s) => String(s).includes('晚餐'))
    },
    extendCycleEndHint () {
      return this.getExtendCycleEndStr() || '—'
    },
    subCalendarMeta () {
      const d = this.subCalendarData || {}
      return {
        start: d.start_date || '—',
        end: d.end_date || '—'
      }
    },
    subCalendarStats () {
      const calStats = this.subCalendarData && this.subCalendarData.stats
      if (calStats && typeof calStats === 'object') {
        return {
          total:
            calStats.package_total != null
              ? calStats.package_total
              : calStats.total != null
                ? calStats.total
                : 0,
          pending: calStats.pending != null ? calStats.pending : 0,
          checked: calStats.checked != null ? calStats.checked : 0,
          current_last_meal_date: calStats.current_last_meal_date
            ? this.formatMealQueueDate(calStats.current_last_meal_date)
            : null,
          current_last_meal_type_label:
            calStats.current_last_meal_type_label ||
            (calStats.current_last_meal_type === 'dinner'
              ? '晚餐'
              : calStats.current_last_meal_type === 'lunch'
                ? '午餐'
                : calStats.current_last_meal_type || null)
        }
      }
      const fromList = this.subCalendarMealQueueList
      if (Array.isArray(fromList) && fromList.length) {
        const checked = fromList.filter((m) => m.status === 'checked').length
        const pending = fromList.filter((m) => m.status !== 'checked' && m.status !== 'cancelled').length
        return {
          total: fromList.length,
          pending,
          checked,
          current_last_meal_date: null,
          current_last_meal_type_label: null
        }
      }
      return null
    },
    subCalendarEngine () {
      return (this.subCalendarData && this.subCalendarData.calendar_engine) || 'legacy'
    },
    isQueueCalendarEngine () {
      const e = String(this.subCalendarEngine || '')
      return e === 'meal-queue-v1' || e === 'meal-sequence-v2'
    },
    subCalendarMealConfigLabel () {
      const cal = this.subCalendarData || {}
      if (cal.meal_config_label) return cal.meal_config_label
      const calCfg = cal.meal_config
      if (calCfg) return this.mealConfigLabel(calCfg)
      const order = this.subCalendarOrder || {}
      return this.mealConfigLabel(this.resolveOrderMealConfigFromRow(order)) || ''
    },
    subCalendarDeferRuleHint () {
      const cal = this.subCalendarData || {}
      if (cal.defer_rule) return cal.defer_rule
      const order = this.subCalendarOrder || {}
      const cfg = String(
        cal.meal_config || this.resolveOrderMealConfigFromRow(order) || 'both'
      ).trim().toLowerCase()
      if (cfg === 'only_lunch' || cfg === 'only_dinner') {
        return '延期：将所选餐次移动到指定日期/餐别，总餐数不变；核销不顺延。'
      }
      return '延期：将所选餐次移动到指定日期/餐别，总餐数不变；核销不顺延。'
    },
    subCalendarMealQueue () {
      const list = Array.isArray(this.subCalendarMealQueueList) ? this.subCalendarMealQueueList : []
      const max = this.subCalendarPackageTotal
      const filtered =
        max > 0
          ? list.filter((m) => this.isMealIndexWithinPackage(m))
          : list
      return filtered.slice().sort((a, b) => Number(a.meal_index) - Number(b.meal_index))
    },
    subCalendarPackageTotal () {
      const cal = this.subCalendarData || {}
      const fromCal = Math.floor(Number(cal.package_total) || 0)
      if (fromCal > 0) return fromCal
      const st =
        cal.stats && cal.stats.package_total != null
          ? Math.floor(Number(cal.stats.package_total) || 0)
          : 0
      if (st > 0) return st
      return this.resolveSubCalendarPackageTotal(this.subCalendarOrder)
    },
    subCalendarDeferralList () {
      const d = this.subCalendarData || {}
      return d.deferral_log || d.delay_log || []
    },
    subCalendarTitle () {
      if (this.subCalendarView === 'week') {
        const days = (this.subCalendarData && this.subCalendarData.days) || []
        if (days.length) {
          return `${days[0].date} ～ ${days[days.length - 1].date}`
        }
        return this.subCalendarWeekStart || '周视图'
      }
      return `${this.subCalendarYear}年${this.subCalendarMonth}月`
    },
    subCalendarWeekLabels () {
      return ['日', '一', '二', '三', '四', '五', '六']
    },
    subCalendarCells () {
      const days = (this.subCalendarData && this.subCalendarData.days) || []
      if (!days.length) return []
      if (this.subCalendarView === 'week') {
        return days.map((d, i) => this.subCalendarDayToCell(d, i))
      }
      const ms = `${this.subCalendarYear}-${String(this.subCalendarMonth).padStart(2, '0')}-01`
      const parts = ms.split('-').map(Number)
      const firstDate = new Date(parts[0], parts[1] - 1, parts[2])
      const pad = firstDate.getDay()
      const cells = []
      for (let i = 0; i < pad; i++) {
        cells.push({ key: 'pad-' + i, day: null, className: 'sub-cal-pad' })
      }
      days.forEach((d, i) => {
        cells.push(this.subCalendarDayToCell(d, i))
      })
      while (cells.length % 7 !== 0) {
        cells.push({ key: 'trail-' + cells.length, day: null, className: 'sub-cal-pad' })
      }
      return cells
    },
    needsSubscriptionPeriodInDialog() {
      if (String(this.orderForm.order_type || '') === '随心订') return true
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      return this.isSubscriptionCatalogProduct(p)
    },
    orderProductSelectOptions() {
      const list = Array.isArray(this.orderProductList) ? this.orderProductList : []
      const q = String(this.orderProductFilterQuery || '').trim().toLowerCase()
      if (!q) return list
      return list.filter((p) => {
        const name = String((p && p.name) || '').toLowerCase()
        const cat = String((p && p.category) || '').toLowerCase()
        const label = this.formatOrderProductOptionLabel(p).toLowerCase()
        return name.includes(q) || cat.includes(q) || label.includes(q)
      })
    },
    mealSlotsSelectDisabled() {
      return this.productMealsMode === 'lunch' || this.productMealsMode === 'dinner'
    },
    productMealConfigHint() {
      if (this.productMealsMode === 'lunch') return '当前商品为仅午餐，餐次已锁定为午餐'
      if (this.productMealsMode === 'dinner') return '当前商品为仅晚餐，餐次已锁定为晚餐'
      const slots = Array.isArray(this.orderForm.meal_slots) ? this.orderForm.meal_slots : []
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      if (p && this.productTotalMeals > 0) {
        const effective = this.resolveEffectiveTotalMealsForForm(p, slots)
        const slotText = slots.length ? slots.join('、') : '午+晚'
        return `已选餐次：${slotText}；本单总餐次 ${effective} 餐（商品基数 ${this.productTotalMeals} 餐）`
      }
      if (!p && this.resolveScheduleTargetTotalMeals() > 0) {
        const slotText = slots.length ? slots.join('、') : '午+晚'
        return `手动录单：已选餐次 ${slotText}，本单总餐次 ${this.resolveScheduleTargetTotalMeals()} 餐`
      }
      if (this.productTotalMeals > 0) {
        return `商品总购买餐次 ${this.productTotalMeals} 餐；午晚双配时每个工作日计 2 餐`
      }
      return '请填写商品名称与套餐总餐次，或从商品库快速选择'
    },
    historicalDeliveredMax() {
      const total = Math.floor(Number(this.orderForm.historical_total_meals) || 0)
      if (total > 0) return total
      const fallback = this.historicalTotalMealsDefault
      return fallback > 0 ? fallback : 999
    },
    historicalTotalMealsDefault() {
      const manual = Math.floor(Number(this.orderForm.manual_target_total_meals) || 0)
      if (manual > 0 && !this.orderForm.productSelectKey) return manual
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      if (!p) return manual > 0 ? manual : 0
      const slots = Array.isArray(this.orderForm.meal_slots) ? this.orderForm.meal_slots : []
      const perCopy = this.resolveEffectiveTotalMealsForForm(p, slots)
      const qty = Math.max(1, Math.floor(Number(this.orderForm.quantity) || 1))
      return perCopy > 0 ? perCopy * qty : 0
    },
    historicalRemainingMeals() {
      const total = Math.max(
        0,
        Math.floor(Number(this.orderForm.historical_total_meals) || this.historicalDeliveredMax || 0)
      )
      const delivered = Math.max(0, Math.floor(Number(this.orderForm.historical_meals_delivered) || 0))
      return Math.max(0, total - delivered)
    },
    schedulePreviewPagedList() {
      const rows = (this.schedulePreviewData && this.schedulePreviewData.list) || []
      return rows.map((r) => ({ ...r, status: r.status || '未用餐' }))
    },
    /** 接口片区 + 本地兜底去重 */
    mergedDeliveryRegionOptions() {
      const fromApi = Array.isArray(this.adminDeliveryRegions) ? this.adminDeliveryRegions : []
      const local = Array.isArray(this.deliveryRegionOptions) ? this.deliveryRegionOptions : []
      return Array.from(new Set([...fromApi, ...local].filter(Boolean)))
    },
    orderFormRiderPreview() {
      const name = String(this.orderForm.rider_name || '').trim()
      if (!name) return null
      return {
        name,
        phone: String(this.orderForm.rider_phone || '').trim(),
        region: String(this.orderForm.delivery_region || '').trim()
      }
    },
    printDialogSlotOptions() {
      const av = this.printDialogAvailability
      const windowMap = {
        午餐: '10:30 – 12:30',
        晚餐: '16:00 – 17:30',
        早餐: '—'
      }
      return this.printDialogSlots.map((slot) => ({
        value: slot,
        label: slot,
        window: windowMap[slot] || '—',
        available: slot === '午餐' ? av.lunch : slot === '晚餐' ? av.dinner : 0,
        pool: slot === '午餐' ? av.lunchPool : slot === '晚餐' ? av.dinnerPool : 0
      }))
    },
    printDialogMaxCredits() {
      if (!this.printDialogOrder || this.printDialogOrder.order_type !== '随心订') return 99
      const remain = Number(this.printDialogOrder.remaining_meals)
      if (Number.isFinite(remain) && remain > 0) return remain
      return 1
    },
    printDialogCrossDayHint() {
      const count = this.printDialogForm.mealCreditsCount
      const slot = this.printDialogForm.mealSlot
      const remain = Number(this.printDialogOrder && this.printDialogOrder.remaining_meals)
      if (!slot || count <= 1) return ''
      if (Number.isFinite(remain) && count > remain) return ''
      return `一次核销 ${count} 餐：第 1 餐锚定 ${this.printDialogAvailability.today || '今日'} ${slot}，其余按排餐顺序扣减（仅午餐/仅晚餐时只扣对应餐段）；连带餐保留各自排餐日`
    },
    printDialogWarning() {
      if (!this.printDialogOrder || this.printDialogOrder.order_type !== '随心订') return ''
      const count = this.printDialogForm.mealCreditsCount
      const remain = Number(this.printDialogOrder.remaining_meals)
      if (!Number.isFinite(remain) || remain <= 0) return '该订单暂无可核销餐次'
      if (count > remain) return `该订单最多还可核销 ${remain} 餐，请减少核销数量`
      if (count > 1 && !this.printDialogForm.mealSlot) return '请选择配送餐次'
      return ''
    },
    printDialogCanSubmit() {
      if (this.printDialogSubmitting || this.printDialogLoading) return false
      if (!this.printDialogOrder) return false
      if (this.printDialogMode === 'verify') {
        if (this.printDialogOrder.order_type !== '随心订') return false
        const count = this.printDialogForm.mealCreditsCount
        if (!count || count < 1) return false
        if (!this.printDialogForm.mealDate) return false
        return !this.printDialogWarning
      }
      if (this.printDialogOrder.order_type !== '随心订') return true
      const count = this.printDialogForm.mealCreditsCount
      if (!count || count < 1) return false
      return !this.printDialogWarning
    },
    batchVerifyPastCanSubmit() {
      if (this.batchVerifyPastSubmitting || this.batchVerifyPastLoading) return false
      if (!this.batchVerifyPastForm.date) return false
      const pending = Number(this.batchVerifyPastPreview && this.batchVerifyPastPreview.pending)
      return pending > 0
    },
    selectableOrdersOnPage() {
      if (!this.isPackageDeliveryMain) return []
      return (this.orders || []).filter((o) => this.isOrderBatchSelectable(o))
    },
    isAllSelectableOrdersSelected() {
      const selectable = this.selectableOrdersOnPage
      if (!selectable.length) return false
      return selectable.every((o) => this.isOrderSelected(o))
    },
    isOrderSelectionIndeterminate() {
      const selectable = this.selectableOrdersOnPage
      if (!selectable.length) return false
      const selectedCount = selectable.filter((o) => this.isOrderSelected(o)).length
      return selectedCount > 0 && selectedCount < selectable.length
    },
    batchCheckCanSubmit() {
      if (this.batchCheckSubmitting || this.batchCheckLoading) return false
      if (!this.batchCheckForm.checkDate) return false
      const pending = Number(this.batchCheckPreview && this.batchCheckPreview.pending)
      return pending > 0
    },
    batchDelayMealsValue() {
      if (this.batchDelayForm.delayMode === '2') return 2
      if (this.batchDelayForm.delayMode === 'custom') {
        return Math.max(1, Math.floor(Number(this.batchDelayForm.customMeals) || 1))
      }
      return 1
    }
  },
  methods: {
    splitOrderNote(note) {
      const lines = String(note || '')
        .split(/\r?\n/)
        .map((x) => String(x || '').trim())
        .filter(Boolean)
      const delayLines = lines.filter((x) => /^\[延期\]/.test(x))
      const remarkLines = lines.filter((x) => !/^\[延期\]/.test(x))
      return { delayLines, remarkLines }
    },
    extractDelayText(note) {
      const { delayLines } = this.splitOrderNote(note)
      return delayLines.join('\n')
    },
    getDelayRecordCount(note) {
      const { delayLines } = this.splitOrderNote(note)
      return delayLines.length
    },
    canMaintainDelay(order) {
      if (!order) return false
      const sidRaw = order.subscription_id
      if (sidRaw == null || sidRaw === '') return false
      if (typeof sidRaw === 'object') {
        const sid = sidRaw._id || sidRaw.id
        return sid != null && String(sid).trim() !== ''
      }
      return String(sidRaw).trim() !== ''
    },
    extractRemarkText(note) {
      const { remarkLines } = this.splitOrderNote(note)
      return remarkLines.join('\n')
    },
    composeOrderNote(delayText, remarkText) {
      const d = String(delayText || '')
        .split(/\r?\n/)
        .map((x) => x.trim())
        .filter(Boolean)
      const r = String(remarkText || '')
        .split(/\r?\n/)
        .map((x) => x.trim())
        .filter(Boolean)
      return [...d, ...r].join('\n')
    },
    detailRiderOptionLabel(r) {
      if (!r) return ''
      const reg = r.regions_label
        || (Array.isArray(r.regions) ? r.regions.join(' / ') : '')
        || [r.region1, r.region2].filter(Boolean).join(' / ')
      const base = `${r.name || ''} ${r.phone || ''}`.trim()
      return reg ? `${base}（${reg}）` : base
    },
    addressImpliesRegion(addr, label) {
      const a = String(addr || '')
      const lb = String(label || '').trim()
      if (!a || !lb) return false
      if (a.includes(lb)) return true
      if (lb.startsWith('温岭') && lb.length > 2) {
        const tail = lb.slice(2)
        if (tail && a.includes('温岭') && a.includes(tail)) return true
      }
      return false
    },
    splitRiderRegionKeywords(regionStr) {
      if (regionStr == null || regionStr === '') return []
      return String(regionStr)
        .split(/[,，、;；\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    },
    findRiderByDeliveryRegion(region) {
      const explicit = String(region || '').trim()
      if (!explicit) return null
      const riders = Array.isArray(this.adminRiderOptions) ? this.adminRiderOptions : []
      for (const r of riders) {
        const regions = Array.isArray(r.regions)
          ? r.regions
          : [r.region1, r.region2].filter(Boolean)
        for (const reg of regions) {
          const keywords = this.splitRiderRegionKeywords(reg)
          const matched = keywords.some(
            (kw) => kw === explicit || explicit.includes(kw) || kw.includes(explicit)
          )
          if (matched) return r
        }
      }
      return null
    },
    findDeliveryRegionFromAddress(address) {
      const addr = String(address || '').trim()
      if (!addr) return null
      const landmarkRegions = [
        {
          region: '温岭太平',
          keywords: ['万昌中路', '万昌路', '博星商厦', '泰隆银行', '泰隆商业银行', '稠州商业银行', '温岭支行']
        }
      ]
      for (const row of landmarkRegions) {
        if ((row.keywords || []).some((kw) => kw && addr.includes(kw))) {
          return {
            delivery_region: row.region,
            rider: this.findRiderByDeliveryRegion(row.region)
          }
        }
      }
      const riders = Array.isArray(this.adminRiderOptions) ? this.adminRiderOptions : []
      let best = null
      for (const r of riders) {
        const regions = Array.isArray(r.regions)
          ? r.regions
          : [r.region1, r.region2].filter(Boolean)
        for (const reg of regions) {
          const keywords = this.splitRiderRegionKeywords(reg)
          for (const kw of keywords) {
            if (kw.length < 1) continue
            if (addr.includes(kw) || this.addressImpliesRegion(addr, kw)) {
              const score = kw.length
              if (!best || score > best.score) {
                best = { score, delivery_region: String(reg).trim(), rider: r }
              }
            }
          }
        }
      }
      if (best) return { delivery_region: best.delivery_region, rider: best.rider }
      const labels = this.mergedDeliveryRegionOptions || []
      for (const label of labels) {
        if (this.addressImpliesRegion(addr, label)) {
          return {
            delivery_region: label,
            rider: this.findRiderByDeliveryRegion(label)
          }
        }
      }
      return null
    },
    applyRiderToOrderForm(rider, deliveryRegion) {
      if (deliveryRegion != null && String(deliveryRegion).trim() !== '') {
        this.$set(this.orderForm, 'delivery_region', String(deliveryRegion).trim())
      }
      if (rider) {
        this.$set(this.orderForm, 'rider_id', rider.id != null ? String(rider.id) : '')
        this.$set(this.orderForm, 'rider_name', rider.name || '')
        this.$set(this.orderForm, 'rider_phone', rider.phone || '')
      }
    },
    clearOrderFormRider() {
      this.$set(this.orderForm, 'rider_id', '')
      this.$set(this.orderForm, 'rider_name', '')
      this.$set(this.orderForm, 'rider_phone', '')
    },
    async resolveOrderFormDelivery(opts = {}) {
      await this.loadAdminDeliveryMeta()
      const region = String(this.orderForm.delivery_region || '').trim()
      const addr = String(this.orderForm.address || '').trim()
      if (region) {
        const rider = this.findRiderByDeliveryRegion(region)
        if (rider) {
          this.applyRiderToOrderForm(rider, region)
          return
        }
        if (opts.clearRiderIfNoMatch) {
          this.clearOrderFormRider()
        }
        return
      }
      if (opts.skipAddressMatch || !addr) return
      const hit = this.findDeliveryRegionFromAddress(addr)
      if (hit) {
        this.applyRiderToOrderForm(hit.rider, hit.delivery_region)
      }
    },
    async onOrderDeliveryRegionChange() {
      await this.resolveOrderFormDelivery({ clearRiderIfNoMatch: true, skipAddressMatch: true })
    },
    async onOrderAddressBlur() {
      if (String(this.orderForm.delivery_region || '').trim()) return
      await this.resolveOrderFormDelivery({ skipAddressMatch: false })
    },
    async onDetailDeliveryRegionChange() {
      await this.loadAdminDeliveryMeta()
      const region = String(this.detailDeliveryRegion || '').trim()
      if (!region) {
        this.detailRiderId = ''
        return
      }
      const rider = this.findRiderByDeliveryRegion(region)
      if (rider && rider.id != null) {
        this.detailRiderId = String(rider.id)
      }
    },
    parseMealSlotsToArray(s) {
      const allowed = ['早餐', '午餐', '晚餐']
      if (s == null || s === '') return ['午餐']
      if (typeof s === 'object' && s.meal_config) {
        return this.mealConfigToSlots(s.meal_config)
      }
      if (typeof s === 'object' && s.meal_slot) {
        s = s.meal_slot
      }
      const parts = String(s).split(/[,，、\s]+/).map((x) => x.trim()).filter(Boolean)
      const out = parts.filter((p) => allowed.includes(p))
      const uniq = [...new Set(out)]
      return uniq.length ? uniq : ['午餐']
    },
    mealConfigFromMode(mode) {
      if (mode === 'lunch') return 'only_lunch'
      if (mode === 'dinner') return 'only_dinner'
      return 'both'
    },
    mealConfigLabel(config) {
      const c = String(config || '').trim().toLowerCase()
      if (!c) return '待确认'
      if (c === 'only_lunch') return '仅午餐'
      if (c === 'only_dinner') return '仅晚餐'
      if (c === 'both') return '午晚都有'
      if (c === 'pending_confirm' || c === '待确认') return '待确认'
      return '待确认'
    },
    resolveOrderMealConfigFromRow(order) {
      const o = order || {}
      const cfg = String(o.meal_config || '').trim().toLowerCase()
      if (cfg === 'only_lunch' || cfg === 'only_dinner' || cfg === 'both') return cfg
      const fromSlot = this.mealConfigFromSlots(this.parseMealSlotsToArray(o.meal_slot))
      if (fromSlot === 'only_lunch' || fromSlot === 'only_dinner') return fromSlot
      if (fromSlot === 'both') return 'both'
      return ''
    },
    mealConfigToSlots(config) {
      const c = String(config || '').trim().toLowerCase()
      if (c === 'only_lunch') return ['午餐']
      if (c === 'only_dinner') return ['晚餐']
      return ['午餐', '晚餐']
    },
    mealConfigFromSlots(slots) {
      const arr = Array.isArray(slots) ? slots.filter(Boolean) : []
      const hasL = arr.includes('午餐')
      const hasD = arr.includes('晚餐')
      if (hasL && !hasD) return 'only_lunch'
      if (hasD && !hasL) return 'only_dinner'
      return 'both'
    },
    resolveRegenerateMealConfig() {
      const order = this.subCalendarOrder || {}
      const fromSlot = this.mealConfigFromSlots(this.parseMealSlotsToArray(order.meal_slot))
      if (fromSlot === 'only_lunch' || fromSlot === 'only_dinner') return fromSlot
      const cfg = String(order.meal_config || '').trim().toLowerCase()
      if (cfg === 'only_lunch' || cfg === 'only_dinner' || cfg === 'both') return cfg
      const cal = this.subCalendarData || {}
      const calCfg = String(cal.meal_config || '').trim().toLowerCase()
      if (calCfg === 'only_lunch' || calCfg === 'only_dinner' || calCfg === 'both') return calCfg
      if (fromSlot === 'both') return 'both'
      return ''
    },
    openRegenerateMealDialog() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      const suggested = this.resolveRegenerateMealConfig() || ''
      this.regenerateMealSuggestedConfig = suggested
      this.regenerateMealForm.meal_config = suggested
      this.regenerateMealDialogVisible = true
    },
    mealsModeFromProduct(meals) {
      const arr = Array.isArray(meals) ? meals : []
      let hasL = false
      let hasD = false
      arr.forEach((s) => {
        const t = String(s).trim().toLowerCase()
        const z = String(s).trim()
        if (t === 'lunch' || z === '午餐') hasL = true
        if (t === 'dinner' || z === '晚餐') hasD = true
      })
      if (hasL && hasD) return 'both'
      if (hasL) return 'lunch'
      if (hasD) return 'dinner'
      return 'both'
    },
    productMealsToSlots(meals) {
      const arr = Array.isArray(meals) ? meals : []
      const out = []
      arr.forEach((s) => {
        const t = String(s).trim().toLowerCase()
        const z = String(s).trim()
        if (t === 'lunch' || z === '午餐') out.push('午餐')
        if (t === 'dinner' || z === '晚餐') out.push('晚餐')
        if (z === '早餐') out.push('早餐')
      })
      const uniq = [...new Set(out)]
      if (!uniq.length) return ['午餐']
      return uniq
    },
    resolveTotalMealsFromProduct(product) {
      const direct = Math.floor(Number(product && product.subscription_total_meals))
      if (direct >= 1) return direct
      return 0
    },
    findOrderProductByKey(productId) {
      if (productId == null || productId === '') return null
      return this.orderProductList.find((x) => String(x.id) === String(productId)) || null
    },
    isSubscriptionCatalogProduct(p) {
      return !!(p && String(p.category || '').trim() === '随心订')
    },
    formatOrderProductOptionLabel(p) {
      if (!p) return ''
      const name = String(p.name || '未命名').trim() || '未命名'
      const cat = String(p.category || '').trim()
      if (this.isSubscriptionCatalogProduct(p)) {
        return `${name}（${cat || '随心订'} · 套餐¥${this.resolveProductPackagePrice(p)}）`
      }
      const price = Number(p.price)
      const priceText = Number.isFinite(price) ? price : 0
      return `${name}（${cat || '商品'} · ¥${priceText}）`
    },
    filterOrderProductOptions(query) {
      this.orderProductFilterQuery = query != null ? String(query) : ''
    },
    onOrderProductSelectVisible(visible) {
      if (!visible) this.orderProductFilterQuery = ''
    },
    /** 随心订：商品库套餐总价（优先 computed_price，即单餐价×总餐次） */
    resolveProductPackagePrice(p) {
      if (!p) return 0
      const computed = Number(p.computed_price)
      const price = Number(p.price)
      if (Number.isFinite(computed) && computed > 0) return computed
      return Number.isFinite(price) ? price : 0
    },
    resolveScheduleTargetTotalMeals() {
      const manual = Math.floor(Number(this.orderForm.manual_target_total_meals) || 0)
      if (manual > 0) return manual
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      const slots = Array.isArray(this.orderForm.meal_slots) ? this.orderForm.meal_slots : []
      if (p && this.isSubscriptionCatalogProduct(p)) {
        return this.resolveEffectiveTotalMealsForForm(p, slots)
      }
      if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
        return Math.max(1, Math.floor(Number(this.orderForm.historical_total_meals) || 0))
      }
      return 0
    },
    resolveSubCalendarPackageTotal(order) {
      if (!order) return 0
      const cal = this.subCalendarData || {}
      const fromCal = Math.floor(Number(cal.package_total) || 0)
      if (fromCal > 0) return fromCal
      const sub = order.subscription || order._subscription || null
      const productTotal = Math.floor(
        Number(
          (order.product && order.product.subscription_total_meals) ||
            (sub && sub.product && sub.product.subscription_total_meals)
        ) || 0
      )
      const qty = Math.max(1, Number(sub && sub.quantity) || Number(order.quantity) || 1)
      if (productTotal > 0) return productTotal * qty
      const targetPerCopy = Math.floor(Number(sub && sub.target_total_meals) || 0)
      if (targetPerCopy > 0) return targetPerCopy * qty
      const fromOrderTarget = Math.floor(Number(order.target_total_meals) || 0)
      if (fromOrderTarget > 0) return fromOrderTarget
      const used = Math.max(0, Number(order.used_meals) || 0)
      const remain = Math.max(0, Number(order.remaining_meals) || 0)
      const sum = used + remain
      if (sum > 0 && used > 0) return sum
      return targetPerCopy > 0 ? targetPerCopy * qty : 0
    },
    isDeferAppendMealRow(row) {
      const max = this.subCalendarPackageTotal
      if (!max || !row) return false
      const idx = Math.floor(Number(row.meal_index) || 0)
      if (idx <= max) return false
      const st = String(row.status || '').trim()
      if (st === 'checked' || row.kind === 'checked') return true
      return st === 'pending' && !row.is_delay_source && row.kind !== 'delayed' && row.kind !== 'delay_source'
    },
    isMealIndexWithinPackage(row) {
      const max = this.subCalendarPackageTotal
      if (!max) return true
      const idx = Math.floor(Number(row && row.meal_index) || 0)
      if (idx >= 1 && idx <= max) return true
      return this.isDeferAppendMealRow(row)
    },
    filterQueueTagsWithinPackage(tags) {
      const max = this.subCalendarPackageTotal
      if (!max) return tags || []
      return (tags || []).filter((t) => {
        const idx = Math.floor(Number(t.meal_index) || 0)
        if (idx >= 1 && idx <= max) return true
        if (idx > max && (t.status === 'checked' || t.kind === 'checked')) return true
        const st = String(t.status || '').trim()
        return idx > max && st === 'pending' && !t.is_delay_source && t.kind === 'pending'
      })
    },
    filterMealQueueListWithinPackage(list) {
      const max = this.subCalendarPackageTotal
      if (!max) return list || []
      return (list || []).filter((m) => this.isMealIndexWithinPackage(m))
    },
    normalizeMealStatsForOrderCard(o, subscription) {
      const usedRaw = Math.max(0, Number(o.used_meals) || 0)
      const pendingRaw = Math.max(0, Number(o.remaining_meals) || 0)
      const scheduleTotal = usedRaw + pendingRaw
      const sub = subscription && typeof subscription === 'object' ? subscription : null
      const qty = Math.max(1, Number(sub && sub.quantity) || 1)
      const targetPerCopy = Math.floor(Number(sub && sub.target_total_meals) || 0)
      const targetTotal = targetPerCopy > 0 ? targetPerCopy * qty : 0

      // 后端已从 meal_queue 现场统计 used/remaining，优先信任明细合计
      if (scheduleTotal > 0) {
        let total = scheduleTotal
        if (targetTotal > 0 && targetTotal !== scheduleTotal && usedRaw <= targetTotal) {
          total = targetTotal
        }
        return {
          total_meals: total,
          used_meals: usedRaw,
          remaining_meals: pendingRaw
        }
      }

      if (targetTotal > 0) {
        return {
          total_meals: targetTotal,
          used_meals: usedRaw,
          remaining_meals: Math.max(0, targetTotal - usedRaw)
        }
      }

      return {
        total_meals: 0,
        used_meals: usedRaw,
        remaining_meals: pendingRaw
      }
    },
    validateProductMealConfig(showMessage = true) {
      const productId = this.orderForm.productSelectKey
      const manualName = String(this.orderForm.product_name || '').trim()
      if (!productId) {
        if (!manualName) {
          if (showMessage) this.$message.error('请填写商品名称')
          return { ok: false, code: 'NO_PRODUCT' }
        }
        if (this.needsSubscriptionPeriodInDialog) {
          const total = this.resolveScheduleTargetTotalMeals()
          if (!total || total < 1) {
            if (showMessage) this.$message.error('请填写套餐总餐次（≥1）')
            return { ok: false, code: 'NO_TOTAL_MEALS' }
          }
          return { ok: true, manual: true, totalMeals: total }
        }
        return { ok: true, manual: true, totalMeals: 0 }
      }
      const p = this.findOrderProductByKey(productId)
      if (!p) {
        if (showMessage) this.$message.error('商品数据未加载，请关闭弹窗后重试')
        return { ok: false, code: 'PRODUCT_NOT_LOADED' }
      }
      if (!this.isSubscriptionCatalogProduct(p)) {
        return { ok: true, product: p, totalMeals: 0 }
      }
      const total = Math.floor(Number(p.subscription_total_meals))
      if (!Number.isFinite(total) || total < 1) {
        if (showMessage) {
          this.$message.error(`商品「${p.name}」未配置总购买餐次，请在商品管理中填写≥1的正整数`)
        }
        return { ok: false, code: 'NO_TOTAL_MEALS', product: p }
      }
      return { ok: true, product: p, totalMeals: total }
    },
    buildSchedulePreviewPayload(page = 1) {
      const meal_slot = (Array.isArray(this.orderForm.meal_slots) ? this.orderForm.meal_slots : [])
        .filter((x) => ['早餐', '午餐', '晚餐'].includes(x))
        .join('、') || '午餐'
      return {
        start_date: this.orderForm.subscription_period_start,
        product_id: this.orderForm.productSelectKey || undefined,
        product_name: this.orderForm.product_name,
        target_total_meals: this.resolveScheduleTargetTotalMeals() || undefined,
        quantity: this.orderForm.quantity,
        meal_slot,
        page,
        page_size: this.schedulePreviewPageSize,
        historical_order: this.orderForm.historical_order,
        historical_delivery_mode: this.orderForm.historical_delivery_mode,
        historical_total_meals:
          this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count'
            ? Math.max(1, Math.floor(Number(this.orderForm.historical_total_meals) || this.historicalTotalMealsDefault || 1))
            : undefined,
        historical_meals_delivered: this.orderForm.historical_meals_delivered
      }
    },
    syncHistoricalTotalMealsFromProduct() {
      const def = this.historicalTotalMealsDefault
      if (def > 0) {
        this.$set(this.orderForm, 'historical_total_meals', def)
      }
    },
    onHistoricalMealsChange() {
      const max = this.historicalDeliveredMax
      const delivered = Math.max(0, Math.floor(Number(this.orderForm.historical_meals_delivered) || 0))
      if (delivered > max) {
        this.$set(this.orderForm, 'historical_meals_delivered', max)
      }
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    onHistoricalDeliveryModeChange(mode) {
      if (mode === 'count') {
        this.syncHistoricalTotalMealsFromProduct()
        if (this.orderForm.historical_meals_delivered == null) {
          this.$set(this.orderForm, 'historical_meals_delivered', 0)
        }
      }
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    onHistoricalOrderToggle(checked) {
      if (checked) {
        if (this.orderForm.historical_delivery_mode === 'count') {
          this.syncHistoricalTotalMealsFromProduct()
          if (!this.orderForm.historical_meals_delivered) {
            this.$set(this.orderForm, 'historical_meals_delivered', 0)
          }
        }
      }
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    async refreshSubscriptionPeriodPreview(showError = false, page = 1) {
      const start = this.orderForm.subscription_period_start
      const productId = this.orderForm.productSelectKey
      const manualName = String(this.orderForm.product_name || '').trim()
      this.subscriptionPeriodError = ''
      if (!start || (!productId && !manualName)) {
        this.subscriptionPeriodHint = ''
        this.$set(this.orderForm, 'subscription_date_range', null)
        if (!showError) this.schedulePreviewData = null
        return null
      }
      const cfg = this.validateProductMealConfig(showError)
      if (!cfg.ok) {
        this.subscriptionPeriodError = true
        this.$set(this.orderForm, 'subscription_date_range', null)
        return null
      }
      try {
        const payload = this.buildSchedulePreviewPayload(page)
        // eslint-disable-next-line no-console
        console.log('[排餐预览] POST /admin/meal-schedules/preview', payload)
        const resp = await this.$api.post('/admin/meal-schedules/preview', payload)
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          this.schedulePreviewData = d
          this.schedulePreviewPage = d.page || page
          this.subscriptionPeriodHint = d.period_text ? `当前区间：${d.period_text}` : ''
          if (d.start_date && d.end_date) {
            this.$set(this.orderForm, 'subscription_date_range', [d.start_date, d.end_date])
          }
          return d
        }
        const msg = (resp && resp.message) || '排餐预览加载失败，请重试'
        this.subscriptionPeriodError = true
        if (showError) this.$message.error(msg)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[排餐预览] 请求失败', e && e.response ? e.response : e)
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          '排餐预览加载失败，请重试'
        this.subscriptionPeriodError = true
        if (showError) this.$message.error(msg)
      }
      return null
    },
    async loadSchedulePreviewPage(payload) {
      const page =
        payload && typeof payload === 'object' && payload.page != null
          ? payload.page
          : payload != null
            ? payload
            : this.schedulePreviewPage
      if (payload && typeof payload === 'object' && payload.pageSize != null) {
        this.schedulePreviewPageSize = payload.pageSize
      }
      this.schedulePreviewPage = page
      this.schedulePreviewLoading = true
      try {
        await this.refreshSubscriptionPeriodPreview(false, page)
      } finally {
        this.schedulePreviewLoading = false
      }
    },
    syncManualTargetTotalMealsFromForm(opts = {}) {
      if (this.orderDialogMode === 'edit') {
        if (opts.preserveManual && Math.floor(Number(this.orderForm.manual_target_total_meals) || 0) > 0) {
          return
        }
        return
      }
      if (opts.preserveManual && Math.floor(Number(this.orderForm.manual_target_total_meals) || 0) > 0) {
        return
      }
      const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
      const slots = Array.isArray(this.orderForm.meal_slots) ? this.orderForm.meal_slots : []
      if (p && this.isSubscriptionCatalogProduct(p)) {
        const total = this.resolveEffectiveTotalMealsForForm(p, slots)
        if (total > 0) {
          this.$set(this.orderForm, 'manual_target_total_meals', total)
        }
        return
      }
      const manual = Math.floor(Number(this.orderForm.manual_target_total_meals) || 0)
      if (manual > 0) return
      if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
        const hist = Math.max(1, Math.floor(Number(this.orderForm.historical_total_meals) || 0))
        if (hist > 0) this.$set(this.orderForm, 'manual_target_total_meals', hist)
      }
    },
    onManualTargetTotalMealsChange() {
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    applyProductMealConfig(product, opts = {}) {
      if (!product) {
        this.productMealsMode = 'both'
        this.productTotalMeals = 0
        this.productAllowSingleMeal = false
        return
      }
      this.productMealsMode = this.mealsModeFromProduct(product.subscription_meals)
      this.productTotalMeals = Math.floor(Number(product.subscription_total_meals) || 0)
      this.productAllowSingleMeal = !!(
        product.subscription_allow_single_meal === true ||
        product.subscription_allow_single_meal === 1
      )
      if (opts.preserveMealSlots) {
        this.syncManualTargetTotalMealsFromForm({ preserveManual: !!opts.preserveManualTarget })
        return
      }
      const slots = this.productMealsToSlots(product.subscription_meals)
      this.$set(this.orderForm, 'meal_slots', this.productMealsMode === 'both' ? slots : slots.slice(0, 1))
      this.$set(this.orderForm, 'meal_config', this.mealConfigFromMode(this.productMealsMode))
      this.syncManualTargetTotalMealsFromForm()
    },
    resolveEffectiveTotalMealsForForm(product, mealSlots) {
      const base = Math.floor(Number(product && product.subscription_total_meals) || 0)
      if (!base) return 0
      const configured = this.productMealsToSlots(product.subscription_meals).length
      const selected = Math.max(1, (mealSlots || []).length || configured)
      return Math.max(1, Math.floor((base * selected) / Math.max(1, configured)))
    },
    mealSlotOptionDisabled(slot) {
      if (this.productMealsMode === 'lunch') return slot !== '午餐'
      if (this.productMealsMode === 'dinner') return slot !== '晚餐'
      if (slot === '早餐') return true
      return false
    },
    defaultNextBusinessDateString() {
      let d = new Date()
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
      while (isRestDay(d)) {
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
      }
      return fmtCalDate(d)
    },
    async openSchedulePreview() {
      if (!this.orderForm.subscription_period_start) {
        this.$message.error('请选择订餐周期起始日')
        return
      }
      const cfg = this.validateProductMealConfig(true)
      if (!cfg.ok) return
      this.schedulePreviewLoading = true
      this.schedulePreviewPage = 1
      try {
        const data = await this.refreshSubscriptionPeriodPreview(true, 1)
        if (!data || !data.list || !data.list.length) {
          if (!this.subscriptionPeriodError) {
            this.$message.warning('无法生成排餐预览，请检查起始日与商品配置')
          }
          return
        }
        this.schedulePreviewPendingSubmit = false
        this.schedulePreviewVisible = true
      } finally {
        this.schedulePreviewLoading = false
      }
    },
    async validateSubscriptionForm(showMessage = true) {
      if (!this.needsSubscriptionPeriodInDialog) return true
      if (!this.orderForm.subscription_period_start) {
        if (showMessage) this.$message.error('请选择订餐周期起始日')
        return false
      }
      const cfg = this.validateProductMealConfig(showMessage)
      if (!cfg.ok) return false
      const data = await this.refreshSubscriptionPeriodPreview(showMessage, 1)
      if (!data || !this.formatSubscriptionPeriodFromRange(this.orderForm.subscription_date_range)) {
        if (showMessage && !this.subscriptionPeriodError) {
          this.$message.error('无法推算订餐周期，请检查商品总餐次配置')
        }
        return false
      }
      if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
        const total = Math.max(1, Math.floor(Number(this.orderForm.historical_total_meals) || this.historicalTotalMealsDefault || 1))
        const delivered = Math.max(0, Math.floor(Number(this.orderForm.historical_meals_delivered) || 0))
        if (delivered > total) {
          if (showMessage) this.$message.error(`已配送餐次不能超过套餐总餐次 ${total}`)
          return false
        }
        if (this.historicalRemainingMeals < 1) {
          if (showMessage) this.$message.error(`已配送 ${delivered} 餐已达套餐总餐次 ${total}，无剩余可排餐`)
          return false
        }
      }
      return true
    },
    onSubscriptionStartChange() {
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    onOrderMealSlotsChange() {
      const slots = Array.isArray(this.orderForm.meal_slots)
        ? this.orderForm.meal_slots.filter((x) => ['早餐', '午餐', '晚餐'].includes(x))
        : []
      if (this.productMealsMode === 'lunch') {
        this.$set(this.orderForm, 'meal_slots', ['午餐'])
      } else if (this.productMealsMode === 'dinner') {
        this.$set(this.orderForm, 'meal_slots', ['晚餐'])
      } else if (slots.length) {
        this.$set(this.orderForm, 'meal_slots', slots)
      } else {
        this.$set(this.orderForm, 'meal_slots', ['午餐'])
      }
      this.$set(this.orderForm, 'meal_config', this.mealConfigFromSlots(this.orderForm.meal_slots))
      if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
        this.syncHistoricalTotalMealsFromProduct()
      }
      this.syncManualTargetTotalMealsFromForm()
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    onOrderMealConfigChange() {
      const config = this.orderForm.meal_config || 'both'
      this.$set(this.orderForm, 'meal_slots', this.mealConfigToSlots(config))
      this.syncManualTargetTotalMealsFromForm()
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    onOrderQuantityChange() {
      if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
        this.syncHistoricalTotalMealsFromProduct()
      }
      this.refreshSubscriptionPeriodPreview(false, 1)
    },
    async confirmSchedulePreviewSubmit() {
      this.schedulePreviewVisible = false
      this.schedulePreviewPendingSubmit = false
      await this.doSubmitOrder()
    },
    async loadOrderProducts() {
      try {
        // 加载全部商品（含饮品/小食/随心订），支持订单查询补全普通单商品名
        const resp = await this.$api.get('/products', {
          params: { page: 1, pageSize: 500 }
        })
        if (resp && resp.code === 200 && resp.data) {
          const list = Array.isArray(resp.data) ? resp.data : []
          // 随心订靠前，其余按分类+名称
          this.orderProductList = list.slice().sort((a, b) => {
            const aSub = String((a && a.category) || '') === '随心订' ? 0 : 1
            const bSub = String((b && b.category) || '') === '随心订' ? 0 : 1
            if (aSub !== bSub) return aSub - bSub
            const ca = String((a && a.category) || '')
            const cb = String((b && b.category) || '')
            if (ca !== cb) return ca.localeCompare(cb, 'zh-CN')
            return String((a && a.name) || '').localeCompare(String((b && b.name) || ''), 'zh-CN')
          })
          this.orderProductFilterQuery = ''
          return
        }
      } catch (e) {
        // ignore
      }
      this.orderProductList = []
    },
    async loadCustomerUsers() {
      try {
        const resp = await this.$api.get('/admin/users', { params: { _t: Date.now() } })
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.customerUserList = resp.data.map((u) => ({
            id: u.id || u._id,
            username: u.username || u.name || '',
            phone: u.phone || '',
            address: u.address || ''
          }))
          return
        }
      } catch (e) {
        /* ignore */
      }
      this.customerUserList = []
    },
    normalizeCustomerPhone(phone) {
      return String(phone || '').replace(/\s/g, '')
    },
    isValidCustomerPhone(phone) {
      return /^1\d{10}$/.test(this.normalizeCustomerPhone(phone))
    },
    mapCustomerUserRecord(u) {
      if (!u) return null
      return {
        id: u.id || u._id,
        username: u.username || u.name || '',
        phone: u.phone || '',
        address: u.address || ''
      }
    },
    async resolveCustomerByPhone(phone) {
      const ph = this.normalizeCustomerPhone(phone)
      if (!ph) return null
      try {
        const resp = await this.$api.post('/admin/users/resolve-by-phone', {
          phone: ph,
          username: String(this.orderForm.user_name || '').trim(),
          address: String(this.orderForm.address || '').trim()
        })
        if (resp && (resp.code === 200 || resp.code === 201) && resp.data) {
          const user = this.mapCustomerUserRecord(resp.data)
          if (user) {
            const exists = (this.customerUserList || []).some(
              (u) => String(u.id) === String(user.id)
            )
            if (!exists) {
              this.customerUserList.unshift(user)
            }
            return { user, created: !!resp.created }
          }
        }
      } catch (e) {
        /* ignore */
      }
      const localHit = (this.customerUserList || []).find(
        (u) => this.normalizeCustomerPhone(u.phone) === ph
      )
      if (localHit) {
        return { user: localHit, created: false }
      }
      return null
    },
    async bindCustomerAccountByPhone(options = {}) {
      const { silent = false } = options
      const phone = this.normalizeCustomerPhone(this.orderForm.user_phone)
      if (!phone) {
        this.customerPhoneLookupHint = ''
        this.customerPhoneBindHint = ''
        if (!String(this.customerSearchQuery || '').trim()) {
          this.orderForm.user_id = ''
        }
        return false
      }
      if (!this.isValidCustomerPhone(phone)) {
        this.customerPhoneLookupHint = '请输入11位有效手机号'
        this.customerPhoneBindHint = ''
        this.orderForm.user_id = ''
        return false
      }
      const resolved = await this.resolveCustomerByPhone(phone)
      if (resolved && resolved.user) {
        this.applyCustomerPick(resolved.user, { fromPhone: true })
        this.customerPhoneLookupHint = ''
        this.customerPhoneBindHint = resolved.created
          ? `已自动创建并绑定账号：${resolved.user.username || '—'} · ${resolved.user.phone || phone}`
          : `已绑定账号：${resolved.user.username || '—'} · ${resolved.user.phone || phone}`
        if (resolved.created && !silent) {
          this.$message.success('未找到已有账号，已自动创建并绑定')
        }
        return true
      }
      this.customerPhoneLookupHint = '绑定账号失败，请稍后重试'
      this.customerPhoneBindHint = ''
      this.orderForm.user_id = ''
      if (!silent) {
        this.$message.error('绑定账号失败，请稍后重试')
      }
      return false
    },
    async onCustomerPhoneBlur() {
      await this.bindCustomerAccountByPhone({ silent: true })
    },
    onCustomerPhoneClear() {
      this.orderForm.user_phone = ''
      this.customerPhoneLookupHint = ''
      this.customerPhoneBindHint = ''
      if (!String(this.customerSearchQuery || '').trim()) {
        this.orderForm.user_id = ''
        this.orderForm.user_name = ''
      }
    },
    async ensureCustomerAccountBound() {
      this.onCustomerSearchBlur()
      if (this.isValidCustomerPhone(this.orderForm.user_phone)) {
        await this.bindCustomerAccountByPhone({ silent: true })
      }
      return true
    },
    parseCustomerQueryText(text) {
      const s = String(text || '').trim()
      if (!s) return { name: '', phone: '' }
      const phoneMatch = s.match(/1\d{10}/)
      const phone = phoneMatch ? phoneMatch[0] : ''
      let name = s
      if (phone) {
        name = s.replace(phone, '').replace(/[,，|·\s]+/g, ' ').trim()
      }
      return { name, phone }
    },
    findCustomerByQuery(query) {
      const q = String(query || '').trim()
      if (!q) return null
      const qLower = q.toLowerCase()
      const qPhone = this.normalizeCustomerPhone(q)
      const list = this.customerUserList || []
      const exactPhone = list.find((u) => this.normalizeCustomerPhone(u.phone) === qPhone)
      if (exactPhone) return exactPhone
      const exactName = list.find((u) => String(u.username || '').trim() === q)
      if (exactName) return exactName
      return (
        list.find((u) => {
          const name = String(u.username || '').toLowerCase()
          const phone = this.normalizeCustomerPhone(u.phone)
          return name.includes(qLower) || (qPhone && phone.includes(qPhone))
        }) || null
      )
    },
    applyCustomerPick(user, options = {}) {
      if (!user) return
      const { fromPhone = false } = options
      this.orderForm.user_id = user.id != null ? String(user.id) : ''
      this.orderForm.user_name = user.username || ''
      if (!String(this.orderForm.consignee_name || '').trim()) {
        this.orderForm.consignee_name = user.username || ''
      }
      this.orderForm.user_phone = user.phone || this.orderForm.user_phone || ''
      if (user.address) {
        this.orderForm.address = user.address
      }
      this.customerSearchQuery = user.username || ''
      if (fromPhone || this.isValidCustomerPhone(this.orderForm.user_phone)) {
        this.customerPhoneLookupHint = ''
      }
      if (!fromPhone) {
        this.customerPhoneBindHint = ''
      }
    },
    syncCustomerSearchQueryFromForm() {
      this.customerSearchQuery = String(this.orderForm.user_name || '').trim()
      this.customerPhoneLookupHint = ''
      this.customerPhoneBindHint = ''
    },
    queryCustomerSearch(queryString, cb) {
      const q = String(queryString || '').trim().toLowerCase()
      const list = this.customerUserList || []
      let filtered = list
      if (q) {
        filtered = list.filter((u) => {
          const name = String(u.username || '').toLowerCase()
          const phone = this.normalizeCustomerPhone(u.phone)
          const qPhone = this.normalizeCustomerPhone(queryString)
          return name.includes(q) || (qPhone && phone.includes(qPhone))
        })
      }
      cb(
        filtered.slice(0, 30).map((u) => ({
          ...u,
          value: `${u.username || ''} ${u.phone || ''}`.trim()
        }))
      )
    },
    onCustomerSelect(item) {
      this.applyCustomerPick(item)
    },
    onCustomerSearchClear() {
      this.customerSearchQuery = ''
      this.orderForm.user_id = ''
      this.orderForm.user_name = ''
      if (!this.isValidCustomerPhone(this.orderForm.user_phone)) {
        this.orderForm.user_phone = ''
      }
      this.customerPhoneLookupHint = ''
      this.customerPhoneBindHint = ''
    },
    onCustomerSearchBlur() {
      const q = String(this.customerSearchQuery || '').trim()
      if (!q) {
        if (!this.isValidCustomerPhone(this.orderForm.user_phone)) {
          this.orderForm.user_id = ''
        }
        return
      }
      const hit = this.findCustomerByQuery(q)
      if (hit) {
        this.applyCustomerPick(hit)
        return
      }
      const parsed = this.parseCustomerQueryText(q)
      this.orderForm.user_name = parsed.name || q
      if (!String(this.orderForm.consignee_name || '').trim()) {
        this.orderForm.consignee_name = this.orderForm.user_name
      }
      if (parsed.phone && !this.orderForm.user_phone) {
        this.orderForm.user_phone = parsed.phone
      }
      if (!this.isValidCustomerPhone(this.orderForm.user_phone)) {
        this.orderForm.user_id = ''
      }
    },
    onManualProductNameInput() {
      if (this.orderForm.productSelectKey) {
        const p = this.findOrderProductByKey(this.orderForm.productSelectKey)
        const typed = String(this.orderForm.product_name || '').trim()
        if (!p || String(p.name || '').trim() !== typed) {
          this.orderForm.productSelectKey = ''
          this.applyProductMealConfig(null)
        }
      }
      if (this.isPackageDelivery) {
        this.orderForm.order_type = '随心订'
        if (this.orderDialogMode === 'create' && !/^ORD\d/.test(String(this.orderForm.order_no || ''))) {
          this.orderForm.order_no = 'ORD' + Date.now()
        }
      }
      if (this.needsSubscriptionPeriodInDialog && this.orderForm.subscription_period_start) {
        this.refreshSubscriptionPeriodPreview(false, 1)
      }
    },
    onOrderProductChange(productId) {
      if (productId == null || productId === '') {
        if (!String(this.orderForm.product_name || '').trim()) {
          this.orderForm.product_name = ''
        }
        this.orderForm.subscription_period_start = this.orderForm.subscription_period_start || ''
        this.applyProductMealConfig(null)
        this.subscriptionPeriodHint = ''
        this.schedulePreviewData = null
        if (this.needsSubscriptionPeriodInDialog && this.orderForm.subscription_period_start) {
          this.refreshSubscriptionPeriodPreview(false, 1)
        }
        return
      }
      const p = this.findOrderProductByKey(productId)
      if (p) {
        this.orderForm.product_name = p.name || ''
        if (this.isPackageDelivery) {
          this.orderForm.order_type = '随心订'
          if (this.orderDialogMode === 'create') {
            this.orderForm.order_no = 'ORD' + Date.now()
          }
        }
        if (this.isSubscriptionCatalogProduct(p)) {
          this.orderForm.unit_price = this.resolveProductPackagePrice(p)
          this.applyProductMealConfig(p)
          if (this.orderForm.historical_order && this.orderForm.historical_delivery_mode === 'count') {
            this.syncHistoricalTotalMealsFromProduct()
          }
          if (!this.orderForm.subscription_period_start) {
            this.$set(this.orderForm, 'subscription_period_start', this.defaultNextBusinessDateString())
          }
          this.refreshSubscriptionPeriodPreview()
        } else if (this.isPackageDelivery) {
          this.orderForm.unit_price = Number(p.price) || 0
          this.applyProductMealConfig(null)
          if (!this.orderForm.subscription_period_start) {
            this.$set(this.orderForm, 'subscription_period_start', this.defaultNextBusinessDateString())
          }
          this.refreshSubscriptionPeriodPreview(false, 1)
        } else {
          this.orderForm.order_type = '普通'
          if (this.orderDialogMode === 'create') {
            this.orderForm.order_no = this.generateSituationOrderNo()
          }
          this.orderForm.unit_price = Number(p.price) || 0
          this.applyProductMealConfig(null)
          this.subscriptionPeriodHint = ''
          this.schedulePreviewData = null
          this.$set(this.orderForm, 'subscription_period_start', '')
          this.$set(this.orderForm, 'subscription_date_range', null)
        }
      }
    },
    paymentMethodLabel(code) {
      const m = {
        mini_order: '小程序下单',
        voucher: '兑换券',
        wechat: '微信支付',
        alipay: '支付宝支付',
        cash: '现金',
        bank_card: '银行卡支付'
      }
      const c = String(code || '').trim()
      return m[c] || (c ? c : '-')
    },
    formatSubscriptionPeriodFromRange(range) {
      if (!range || range.length !== 2) return ''
      const a = range[0]
      const b = range[1]
      if (a == null || b == null || a === '' || b === '') return ''
      const s = (v) => {
        if (typeof v === 'string') return v.slice(0, 10)
        const d = v instanceof Date ? v : new Date(v)
        if (isNaN(d.getTime())) return ''
        const p = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
      }
      const x = s(a)
      const y = s(b)
      if (!x || !y) return ''
      return `${x} 至 ${y}`
    },
    parseSubscriptionPeriodToRange(text) {
      const s = String(text || '').trim()
      const m = s.match(/(\d{4}-\d{1,2}-\d{1,2})\s*至\s*(\d{4}-\d{1,2}-\d{1,2})/)
      if (!m) return null
      return [m[1], m[2]]
    },
    /** 与后端 tryParseJsonChain 一致：处理双重 JSON 字符串，避免 items 解析为空 */
    tryParseJsonChain(s) {
      let cur = String(s).trim()
      if (!cur) return null
      for (let depth = 0; depth < 5; depth++) {
        try {
          const next = JSON.parse(cur)
          if (typeof next === 'string') {
            cur = next.trim()
            continue
          }
          return next
        } catch (e) {
          return null
        }
      }
      return null
    },
    /** 与后端 normalizeOrderItems 对齐：接口偶发仍返回 JSON 字符串或单对象 */
    normalizeOrderItemsFromApi(raw) {
      if (Array.isArray(raw)) return raw
      if (raw == null || raw === '') return []
      if (typeof raw === 'string') {
        const p = this.tryParseJsonChain(raw)
        if (p == null) return []
        if (Array.isArray(p)) return p
        if (p && typeof p === 'object' && (p.name != null || p.product_id != null || p.quantity != null || p.price != null)) {
          return [p]
        }
        return []
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const keys = Object.keys(raw)
        if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
          const arr = keys
            .sort((a, b) => Number(a) - Number(b))
            .map((k) => raw[k])
            .filter((x) => x != null && typeof x === 'object')
          if (arr.length) return arr
        }
        if (raw.name != null || raw.product_id != null || raw.quantity != null || raw.price != null) {
          return [raw]
        }
      }
      return []
    },
    /** 明细行展示名：优先 name，禁止把商品 ID 当成名称展示 */
    lineItemDisplayName(it) {
      if (!it) return ''
      if (it.name != null && String(it.name).trim() !== '') return String(it.name).trim()
      const pid = it.product_id
      if (pid && typeof pid === 'object' && pid.name != null && String(pid.name).trim() !== '') {
        return String(pid.name).trim()
      }
      return ''
    },
    formatOrderListDateTime(d) {
      return formatDateTime(d)
    },
    formatOrderListDate(d) {
      return formatBjDate(d) === '—' ? '' : formatBjDate(d)
    },
    /**
     * 将接口返回的单条订单（含 items、display_*、subscription_id 对象）转为列表卡片模型
     */
    mapRawOrderToCard(o) {
      const formatDateTime = (d) => this.formatOrderListDateTime(d)
      const formatDate = (d) => this.formatOrderListDate(d)
      const mapPickupMethod = (pickup) => {
        if (!pickup) return '同城配送'
        if (pickup === '配送') return '同城配送'
        return pickup
      }
      const items = this.normalizeOrderItemsFromApi(o.items)
      const item0 = items.length ? items[0] : null
      const subscription = o.subscription_id || null
      const subscriptionPeriodFromSub =
        subscription && (subscription.start_date || subscription.end_date)
          ? `${formatDate(subscription.start_date)} 至 ${formatDate(subscription.end_date)}`
          : ''
      const subscriptionPeriodFromRemark = String(o.remark || '').trim()
      let subscriptionPeriod = subscriptionPeriodFromRemark || subscriptionPeriodFromSub

      let delay_log_summary = ''
      if (subscription && Array.isArray(subscription.delay_log) && subscription.delay_log.length) {
        delay_log_summary = subscription.delay_log
          .map((d) => {
            const mt = d.meal_type || d.meal || ''
            const a = d.original_date != null ? String(d.original_date).slice(0, 10) : ''
            const b = d.new_date != null ? String(d.new_date).slice(0, 10) : ''
            return `${a} ${mt} → ${b}`.trim()
          })
          .filter(Boolean)
          .join('；')
        if (delay_log_summary && !subscriptionPeriodFromSub) {
          subscriptionPeriod = subscriptionPeriodFromRemark || '-'
        }
      }

      const rawOrderStatus = o.order_status || ''
      const hasDelay = this.hasOrderDelay({
        order_note: o.order_note,
        delay_log_summary
      })
      let displayOrderStatus
      if (this.isOrderQueryTab || this.isRefundTab) {
        // 小程序订单 / 退款订单：保留真实状态
        displayOrderStatus = rawOrderStatus
      } else if (this.isPackageDelivery) {
        displayOrderStatus =
          rawOrderStatus === '待支付'
            ? '待支付'
            : rawOrderStatus === '配送完成' || rawOrderStatus === '已完成'
              ? '配送完成'
              : rawOrderStatus === '已支付' || rawOrderStatus === '待配送' || rawOrderStatus === '配送中'
                ? '进行中'
                : rawOrderStatus === '已完成'
                  ? '已完成'
                  : rawOrderStatus
      } else if (this.isOrderSituation) {
        // 门店出餐：保留真实状态（制作中/待取餐等），便于取餐操作
        displayOrderStatus = rawOrderStatus
      } else {
        displayOrderStatus = rawOrderStatus
      }
      if (hasDelay && !['已取消订阅', '已取消', '已退款'].includes(String(rawOrderStatus || '').trim())) {
        displayOrderStatus = '延期'
      }

      let productName = ''
      let unitPrice = 0
      let totalQty = 0

      if (this.isPackageDelivery) {
        const srvName = String(o.display_product_name != null ? o.display_product_name : '').trim()
        const srvUnit = Number(o.display_unit_price)
        const srvQty = Number(o.display_quantity)

        totalQty = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
        if (totalQty === 0 && item0) {
          totalQty = Number(item0.quantity) || 1
        }
        if (Number.isFinite(srvQty) && srvQty > 0) {
          totalQty = srvQty
        }
        const parts = items
          .map((it) => {
            const name = this.lineItemDisplayName(it)
            const qty = Number(it.quantity) || 0
            if (!name) return ''
            return `${name} x ${qty}`
          })
          .filter(Boolean)

        productName = srvName || parts.join(' + ') || String(o.product_name || '').trim() || ''
        const shipFee = Number(o.shipping_fee || 0)
        const totalNum = Number(o.total_price)
        const displayTotal = Number.isFinite(totalNum) ? totalNum : 0
        const lineUnit = item0 != null ? Number(item0.price) : NaN
        if (Number.isFinite(srvUnit) && srvUnit > 0) {
          unitPrice = srvUnit
        } else if (Number.isFinite(lineUnit) && lineUnit >= 0) {
          unitPrice = lineUnit
        } else if (totalQty > 0) {
          unitPrice = Math.max(0, displayTotal - shipFee) / totalQty
        } else {
          unitPrice = 0
        }
        if (!productName && items.length && item0) {
          productName = this.lineItemDisplayName(item0) || '（明细无商品名，请编辑订单补全）'
        }
      } else {
        const srvN = String(o.display_product_name != null ? o.display_product_name : '').trim()
        const srvU = Number(o.display_unit_price)
        const srvQ = Number(o.display_quantity)
        productName =
          srvN ||
          this.lineItemDisplayName(item0) ||
          (item0 && item0.product_id && item0.product_id.name) ||
          o.product_name ||
          (item0 && item0.product_id && (item0.product_id._id || item0.product_id)) ||
          ''
        unitPrice =
          Number.isFinite(srvU) && srvU > 0 ? srvU : Number((item0 && item0.price) || o.unit_price || 0)
        totalQty =
          Number.isFinite(srvQ) && srvQ > 0 ? srvQ : Number((item0 && item0.quantity) || o.quantity || 0)
      }

      const totalPriceDisplay = Number(o.total_price)
      const totalPriceSafe = Number.isFinite(totalPriceDisplay) ? totalPriceDisplay : 0
      const isFlexSub =
        String(o.order_type || '').trim() === '随心订' &&
        subscription &&
        typeof subscription === 'object'
      let packagePaid = 0
      if (isFlexSub) {
        const paid = Number(subscription.paid_amount)
        const total = Number(subscription.total_amount)
        if (Number.isFinite(paid) && paid > 0) packagePaid = paid
        else if (Number.isFinite(total) && total > 0) packagePaid = total
      }
      const displayPaidAmount = isFlexSub && packagePaid > 0 ? packagePaid : totalPriceSafe
      const deliveryUnitPrice = isFlexSub ? totalPriceSafe : null
      const mealStats = this.normalizeMealStatsForOrderCard(o, subscription)

      return {
        _id: o._id,
        id: o._id,
        subscription_id: o.subscription_id || null,
        order_no: o.order_no,
        user_name: (() => {
          const orderName = String(o.user_name || '').trim()
          const u = o.user_id && typeof o.user_id === 'object' ? o.user_id : null
          const liveName = u ? String(u.username || '').trim() : ''
          const livePhone = u ? String(u.phone || '').trim() : ''
          const isGuestAccount = livePhone === '__eatin_offline_guest__'
          // 订单快照优先；禁止用占位散客账号名覆盖真实顾客名
          if (orderName && !/店内散客/.test(orderName)) return orderName
          if (!isGuestAccount && liveName && !/店内散客/.test(liveName)) return liveName
          return orderName && !/店内散客/.test(orderName) ? orderName : ''
        })(),
        consignee_name: (() => {
          const consignee = String(o.consignee_name || '').trim()
          if (consignee && !/店内散客/.test(consignee)) return consignee
          const orderName = String(o.user_name || '').trim()
          if (orderName && !/店内散客/.test(orderName)) return orderName
          return consignee && !/店内散客/.test(consignee) ? consignee : ''
        })(),
        user_phone: (() => {
          const orderPhone = String(o.user_phone || '').trim()
          const livePhone =
            o.user_id && typeof o.user_id === 'object' ? String(o.user_id.phone || '').trim() : ''
          if (orderPhone && orderPhone !== '__eatin_offline_guest__') return orderPhone
          if (livePhone && livePhone !== '__eatin_offline_guest__') return livePhone
          return ''
        })(),
        address: o.user_address || '',
        delivery_region: o.delivery_region || '',
        rider_id: o.rider_id != null ? String(o.rider_id) : '',
        rider_name: o.rider_name || '',
        rider_phone: o.rider_phone || '',
        receipt_mask_phone: !!o.receipt_mask_phone,
        user_id: (() => {
          const u = o.user_id
          if (u == null || u === '') return ''
          if (typeof u === 'object') return String(u.id || u._id || '')
          return String(u)
        })(),
        meal_slot: (() => {
          const cfg = (() => {
            const oc = String(o.meal_config || '').trim().toLowerCase()
            if (oc === 'only_lunch' || oc === 'only_dinner' || oc === 'both') return oc
            const fromSlot = this.mealConfigFromSlots(this.parseMealSlotsToArray(o.meal_slot))
            if (fromSlot !== 'both') return fromSlot
            const sc = String((subscription && subscription.meal_config) || '').trim().toLowerCase()
            if (sc === 'only_lunch' || sc === 'only_dinner' || sc === 'both') return sc
            return fromSlot
          })()
          if (cfg) return this.mealConfigToSlots(cfg).join('、')
          return o.meal_slot || ''
        })(),
        meal_config: (() => {
          const oc = String(o.meal_config || '').trim().toLowerCase()
          if (oc === 'only_lunch' || oc === 'only_dinner' || oc === 'both') return oc
          const fromSlot = this.mealConfigFromSlots(this.parseMealSlotsToArray(o.meal_slot))
          if (fromSlot !== 'both') return fromSlot
          const sc = String((subscription && subscription.meal_config) || '').trim().toLowerCase()
          if (sc === 'only_lunch' || sc === 'only_dinner' || sc === 'both') return sc
          return fromSlot
        })(),
        total_price: totalPriceSafe,
        display_paid_amount: displayPaidAmount,
        delivery_unit_price: deliveryUnitPrice,
        package_order_no:
          subscription && subscription.order_no
            ? String(subscription.order_no)
            : '',
        scheduled_date: o.scheduled_date || '',
        order_type: o.order_type,
        raw_order_status: rawOrderStatus,
        order_status: displayOrderStatus,
        subscription_period: subscriptionPeriod,
        createdAt: formatDateTime(o.created_at || o.createdAt),
        channel: isLikelyMiniProgramRetail(o)
          ? '小程序'
          : String(o.order_note || '').trim() === '收银台' || /^ORD/i.test(String(o.order_no || ''))
            ? '收银台'
            : rawOrderIsCashierSituationRow(o)
              ? '收银台'
              : '小程序',
        product_name: productName,
        unit_price: unitPrice,
        quantity: totalQty,
        delivery_method: mapPickupMethod(o.pickup_method),
        shipping_fee: o.shipping_fee != null ? Number(o.shipping_fee) : 0,
        payment_method: o.payment_method || (this.isPackageDeliveryMain ? 'mini_order' : ''),
        payment_method_display: this.paymentMethodLabel(
          o.payment_method || (this.isPackageDeliveryMain ? 'mini_order' : '')
        ),
        remark: o.remark || '',
        order_note: (o.order_note != null && String(o.order_note).trim()) || '',
        delay_log_summary,
        total_meals: mealStats.total_meals,
        remaining_meals: mealStats.remaining_meals,
        used_meals: mealStats.used_meals,
        package_type_label: o.subscription_package_label || o.package_type_label || '',
        subscription_start:
          subscription && subscription.start_date
            ? formatDate(subscription.start_date)
            : '',
        expected_end_date:
          subscription && subscription.end_date ? formatDate(subscription.end_date) : '',
        meal_run_status: String(o.meal_run_status || 'active').toLowerCase(),
        meal_credits_count: Math.max(1, Number(o.meal_credits_count) || 1),
        is_cashier_order: rawOrderIsCashierSituationRow(o) && !isLikelyMiniProgramRetail(o),
        pickup_code: o.pickup_code || '',
        pickup_status: o.pickup_status || 'pending',
        is_display: Number(o.is_display) === 1 ? 1 : 0
      }
    },
    upsertOrderInList(card) {
      if (!card || card._id == null) return
      const idStr = String(card._id)
      const idx = this.allOrders.findIndex((x) => String(x._id || x.id) === idStr)
      if (idx >= 0) {
        this.$set(this.allOrders, idx, card)
      } else {
        this.allOrders.push(card)
      }
      if (this.isPackageDeliveryMain) {
        this.allOrders = this.mergeFlexOrdersForDisplay(this.allOrders)
      }
      this.applyFilters()
    },
    buildOrderSubmitPayload() {
      const f = { ...this.orderForm }
      const meal_config = f.meal_config || this.mealConfigFromMode(this.productMealsMode)
      const configSlots = this.mealConfigToSlots(meal_config)
      const formSlots = (Array.isArray(f.meal_slots) ? f.meal_slots : [])
        .filter((x) => ['早餐', '午餐', '晚餐'].includes(x))
      const meal_slot =
        (configSlots.length ? configSlots : formSlots.length ? formSlots : ['午餐']).join('、') || '午餐'
      const selectedMealSlots = meal_slot.split(/[,，、\s]+/).filter(Boolean)
      const periodFromRange = this.formatSubscriptionPeriodFromRange(f.subscription_date_range)
      const subscription_period =
        periodFromRange || String(f.subscription_period || '').trim()
      delete f.productSelectKey
      delete f.subscription_date_range
      delete f.subscription_period_start
      delete f.meal_slots

      const product_id =
        this.orderForm.productSelectKey != null && String(this.orderForm.productSelectKey).trim() !== ''
          ? String(this.orderForm.productSelectKey).trim()
          : ''

      const unit = Number(f.unit_price)
      const qty = Math.max(1, Math.floor(Number(f.quantity) || 1))
      const ship = Number(f.shipping_fee || 0)
      const computedTotal = (Number.isFinite(unit) ? unit : 0) * qty + (Number.isFinite(ship) ? ship : 0)
      const totalRaw = Number(f.total_price)
      const total_price = Number.isFinite(totalRaw) && !Number.isNaN(totalRaw) ? totalRaw : computedTotal

      const linkId =
        f.plan_link_mode === 'link' && String(f.link_subscription_id || '').trim()
          ? String(f.link_subscription_id).trim()
          : ''
      delete f.plan_link_mode
      delete f.link_subscription_id

      const resolvedTargetMeals = this.resolveScheduleTargetTotalMeals()
      const historicalCountTotal =
        f.historical_order && f.historical_delivery_mode === 'count'
          ? Math.max(1, Math.floor(Number(f.historical_total_meals) || 0))
          : 0
      let target_total_meals
      if (historicalCountTotal > 0) {
        target_total_meals = historicalCountTotal
      } else if (resolvedTargetMeals > 0) {
        target_total_meals = resolvedTargetMeals
      } else {
        target_total_meals = undefined
      }

      return {
        ...f,
        product_id,
        product_name: String(f.product_name || '').trim(),
        unit_price: Number.isFinite(unit) ? unit : 0,
        quantity: qty,
        total_price,
        meal_slot,
        meal_config,
        subscription_period,
        remark: subscription_period,
        address: f.address,
        pickup_method: f.delivery_method,
        delivery_region: String(f.delivery_region != null ? f.delivery_region : '').trim(),
        rider_id: String(f.rider_id != null ? f.rider_id : '').trim(),
        rider_name: String(f.rider_name != null ? f.rider_name : '').trim(),
        rider_phone: String(f.rider_phone != null ? f.rider_phone : '').trim(),
        historical_order: !!f.historical_order,
        historical_delivery_mode: f.historical_delivery_mode || 'past_date',
        historical_total_meals:
          f.historical_order && f.historical_delivery_mode === 'count'
            ? Math.max(1, Math.floor(Number(f.historical_total_meals) || 0))
            : undefined,
        historical_meals_delivered:
          f.historical_delivery_mode === 'count'
            ? Math.max(0, Math.floor(Number(f.historical_meals_delivered) || 0))
            : undefined,
        ...(target_total_meals ? { target_total_meals } : {}),
        ...(linkId ? { link_subscription_id: linkId } : {})
      }
    },
    getOrders() {
      return this.$api.get('/admin/orders', { params: { _t: Date.now() } }).then((resp) => {
        if (!resp || resp.code !== 200) {
          this.allOrders = []
          this.orders = []
          this.total = 0
          return
        }

        if (
          resp.message &&
          /db:sync|表缺失|eating_orders/i.test(String(resp.message)) &&
          (!resp.data || !resp.data.length)
        ) {
          this.$message.warning(String(resp.message).slice(0, 220))
        }

        const rawList = resp.data || []
        let filteredRawList = rawList
        if (this.isRefundTab) {
          filteredRawList = rawList.filter((o) => rawOrderIsRefundRelatedRow(o))
        } else if (this.isOrderQueryTab) {
          filteredRawList = rawList.filter(
            (o) => rawOrderIsRetailMiniRow(o) && !rawOrderIsRefundRelatedRow(o)
          )
        } else if (this.isPackageDelivery) {
          filteredRawList = rawList.filter(
            (o) => rawOrderIsSubscriptionMiniRow(o) && !rawOrderIsRefundRelatedRow(o)
          )
        } else if (this.isOrderSituation) {
          filteredRawList = rawList.filter(
            (o) => rawOrderIsSituationBoardRow(o) && !rawOrderIsRefundRelatedRow(o)
          )
        }

        this.allOrders = filteredRawList.map((o) => this.mapRawOrderToCard(o))
        if (this.isPackageDeliveryMain) {
          this.allOrders = this.mergeFlexOrdersForDisplay(this.allOrders)
        }

        // 保存/核销等刷新列表时保留当前页，避免从第2页被打回第1页
        this.applyFilters()
        localStorage.setItem('dashboard_order_count', String(this.allOrders.length))
      }).catch((err) => {
        this.allOrders = []
        this.orders = []
        this.total = 0
        localStorage.setItem('dashboard_order_count', '0')
        const hint =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err._networkHint) ||
          (err && err.message) ||
          '加载订单失败，请检查后端是否已重启、文件是否上传完整'
        this.$message.error(String(hint).slice(0, 200))
      })
    },
    async loadAdminDeliveryMeta() {
      try {
        const [r1, r2] = await Promise.all([
          this.$api.get('/admin/delivery-regions', { params: { _t: Date.now() } }),
          this.$api.get('/admin/riders/options', { params: { _t: Date.now() } })
        ])
        if (r1 && r1.code === 200 && Array.isArray(r1.data)) {
          this.adminDeliveryRegions = r1.data
            .map((x) => (typeof x === 'string' ? x : x.label))
            .filter(Boolean)
        }
        if (r2 && r2.code === 200 && Array.isArray(r2.data)) {
          this.adminRiderOptions = r2.data
        }
      } catch (e) {
        /* 静默失败，仍可用本地片区选项 */
      }
    },
    searchOrders() {
      this.currentPage = 1;
      this.applyFilters();
    },
    resetForm() {
      this.searchForm = {
        orderSearchType: 'order_no',
        keyword: '',
        timeSearchType: 'create_time',
        startDate: '',
        endDate: '',
        userName: '',
        userPhone: '',
        orderStatus: '',
        deliveryMethod: '',
        paymentMethod: '',
        mealSlot: ''
      };
    },
    clearScheduleDayFilter() {
      this.scheduleFilterActive = false
      this.scheduleFilterMatchKeys = []
      this.scheduleFilterOrderIds = []
      this.todayMealOrders = []
    },
    orderMatchesScheduleDayFilter(order) {
      if (!this.scheduleFilterActive) return true
      const keys = this.scheduleFilterMatchKeys || []
      const orderIds = this.scheduleFilterOrderIds || []
      if (!keys.length && !orderIds.length) return false
      const oid = String((order && (order._id || order.id)) || '')
      if (oid && orderIds.includes(oid)) return true
      const sid = this.getSubscriptionIdFromOrder(order)
      if (!sid) return false
      const slots = this.parseMealSlotParts(order && order.meal_slot)
      return slots.some((slot) => keys.includes(`${sid}|${slot}`))
    },
    orderMatchesMealSlotFilter(order) {
      const want = String((this.searchForm && this.searchForm.mealSlot) || '').trim()
      if (!want) return true
      return this.parseMealSlotParts(order && order.meal_slot).includes(want)
    },
    resetFilters() {
      this.resetForm();
      this.clearScheduleDayFilter();
      this.currentPage = 1;
      this.applyFilters();
    },
    applyFilters() {
      let filtered = [...this.allOrders];

      if (this.searchForm.keyword) {
        const kw = this.searchForm.keyword.trim();
        const type = this.searchForm.orderSearchType;
        filtered = filtered.filter(o => {
          if (type === 'order_no') return o.order_no.includes(kw);
          if (type === 'pickup_code') {
            return String(o.pickup_code || '').toUpperCase().includes(kw.toUpperCase())
          }
          if (type === 'product_name') return (o.product_name || '').includes(kw);
          if (type === 'receiver') return (o.user_name || '').includes(kw);
          return true;
        });
      }

      if (this.searchForm.userName) {
        const nameKw = this.searchForm.userName.trim()
        filtered = filtered.filter((o) => String(o.user_name || '').includes(nameKw))
      }

      if (this.searchForm.userPhone) {
        const phoneKw = this.searchForm.userPhone.trim()
        filtered = filtered.filter((o) => String(o.user_phone || o.phone || '').includes(phoneKw))
      }

      if (this.searchForm.orderStatus) {
        if (this.searchForm.orderStatus === '延期') {
          filtered = filtered.filter((o) => this.hasOrderDelay(o))
        } else {
          filtered = filtered.filter(o => o.order_status === this.searchForm.orderStatus);
        }
      }

      if ((this.isPackageDelivery || this.isOrderQueryTab) && this.searchForm.paymentMethod) {
        filtered = filtered.filter(o => o.payment_method === this.searchForm.paymentMethod);
      }

      if (this.isPackageDeliveryMain && this.searchForm.mealSlot) {
        filtered = filtered.filter((o) => this.orderMatchesMealSlotFilter(o))
      }

      if (this.isPackageDeliveryMain && this.scheduleFilterActive) {
        filtered = filtered.filter((o) => this.orderMatchesScheduleDayFilter(o))
      }

      this.total = filtered.length;
      const maxPage = Math.max(1, Math.ceil(this.total / this.pageSize) || 1)
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage
      }
      const start = (this.currentPage - 1) * this.pageSize;
      this.orders = filtered.slice(start, start + this.pageSize);
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.applyFilters();
    },
    onOrdersPaginationChange({ page, pageSize }) {
      this.currentPage = page
      this.pageSize = pageSize
      this.applyFilters()
    },
    onDelayRecordPaginationChange({ page, pageSize }) {
      this.delayRecordPagination.page = page
      this.delayRecordPagination.pageSize = pageSize
      this.loadDelayRecordPage(page)
    },
    getStatusType(status) {
      switch (status) {
        case '待支付': return 'warning';
        case '已支付': return 'primary';
        case '待配送': return 'info';
        case '配送中': return 'primary';
        case '配送完成': return 'success';
        case '延期': return 'danger';
        case '已完成': return 'success';
        case '已取消': return 'danger';
        case '已取消订阅': return 'danger';
        case '已退款': return 'danger';
        case '已退单': return 'danger';
        case '退款审核中': return 'warning';
        case '进行中': return 'primary';
        case '待发货': return 'warning';
        case '已下单': return 'info';
        case '制作中': return 'success';
        case '待取餐': return 'danger';
        default: return '';
      }
    },
    parseMealSlotParts(text) {
      return String(text || '')
        .split(/[,，、\s]+/)
        .map((x) => x.trim())
        .filter((x) => ['早餐', '午餐', '晚餐'].includes(x))
    },
    mergeMealSlotLabels(a, b) {
      const set = new Set([...this.parseMealSlotParts(a), ...this.parseMealSlotParts(b)])
      const order = ['早餐', '午餐', '晚餐']
      return order.filter((x) => set.has(x)).join('、')
    },
    mergeFlexRawStatus(a, b) {
      const sa = String(a || '').trim()
      const sb = String(b || '').trim()
      if (sa === sb) return sa
      if (sa === '已取消' || sb === '已取消') return '已取消'
      if (sa === '配送中' || sb === '配送中') return '配送中'
      if (sa === '待配送' || sb === '待配送') return '待配送'
      if ((sa === '配送完成' || sa === '已完成') && (sb === '配送完成' || sb === '已完成')) {
        return '配送完成'
      }
      return '进行中'
    },
    mergeFlexOrdersForDisplay(cards) {
      const others = []
      const bucket = new Map()
      for (const c of cards || []) {
        if (c.order_type !== '随心订') {
          others.push(c)
          continue
        }
        const sid = this.getSubscriptionIdFromOrder(c)
        const day = this.formatOrderListDate(c.scheduled_date || c.subscription_start || c.createdAt)
        const key = `${sid || c.order_no}|${day}`
        if (!bucket.has(key)) {
          bucket.set(key, { ...c })
          continue
        }
        const prev = bucket.get(key)
        prev.meal_slot = this.mergeMealSlotLabels(prev.meal_slot, c.meal_slot)
        prev.raw_order_status = this.mergeFlexRawStatus(prev.raw_order_status, c.raw_order_status)
        if (this.isPackageDelivery) {
          prev.order_status =
            prev.raw_order_status === '配送完成' || prev.raw_order_status === '已完成'
              ? '配送完成'
              : prev.raw_order_status === '待支付'
                ? '待支付'
                : ['已支付', '待配送', '配送中', '进行中', '待发货', '已下单'].includes(prev.raw_order_status)
                  ? '进行中'
                  : prev.raw_order_status
        } else {
          prev.order_status = prev.raw_order_status
        }
        prev.total_price = Math.max(Number(prev.total_price) || 0, Number(c.total_price) || 0)
        if (Number(c.delivery_unit_price) > Number(prev.delivery_unit_price)) {
          prev.delivery_unit_price = c.delivery_unit_price
        }
        if (!String(prev.delivery_region || '').trim() && String(c.delivery_region || '').trim()) {
          prev.delivery_region = c.delivery_region
        }
        if (String(c.rider_id || '').trim()) {
          if (!String(prev.rider_id || '').trim()) {
            prev.rider_id = c.rider_id
            prev.rider_name = c.rider_name || ''
            prev.rider_phone = c.rider_phone || ''
          }
        }
        if (!String(prev.rider_name || '').trim() && String(c.rider_name || '').trim()) {
          prev.rider_id = c.rider_id || prev.rider_id || ''
          prev.rider_name = c.rider_name || ''
          prev.rider_phone = c.rider_phone || prev.rider_phone || ''
        }
      }
      return others.concat(Array.from(bucket.values()))
    },
    hasOrderDelay(order) {
      const delaySummary = String((order && order.delay_log_summary) || '').trim()
      if (delaySummary) return true
      return this.getDelayRecordCount((order && order.order_note) || '') > 0
    },
    changeStatusBox(status) {
      this.searchForm.orderStatus = status || ''
      this.currentPage = 1
      this.applyFilters()
    },
    /** 与后台 refundOrder 一致：发货前可退（含制作中/待取餐）；收银台已完成单可退；随心订进行中可整单退 */
    showRefundButton(order) {
      const r = order.raw_order_status || order.order_status
      const s = String(r || '').trim()
      if (s === '已取消' || s === '已退款') return false
      if (['待支付', '已支付', '已下单', '待发货', '制作中', '待取餐'].includes(s)) return true
      if (
        this.isPackageDelivery &&
        order.order_type === '随心订' &&
        ['进行中', '待配送', '配送中'].includes(s)
      ) {
        return true
      }
      if (this.isOrderSituation && order.is_cashier_order && s === '已完成') return true
      return false
    },
    showChargebackButton(order) {
      if (!this.isPackageDelivery) return false
      if (order.order_type !== '随心订') return false
      const s = String(order.raw_order_status || order.order_status || '').trim()
      if (s === '已退单' || s === '已取消' || s === '已退款') return false
      return ['已支付', '已下单', '待发货', '进行中', '待配送', '配送中'].includes(s)
    },
    openChargebackDialog(order) {
      this.chargebackTargetOrder = order
      this.chargebackForm = { reason: '', print_receipt: true }
      this.chargebackDialogVisible = true
    },
    async submitChargeback() {
      const order = this.chargebackTargetOrder
      if (!order) return
      const reason = String(this.chargebackForm.reason || '').trim()
      if (!reason) {
        this.$message.warning('请填写退单原因')
        return
      }
      try {
        await appConfirm({
          title: '退单确认',
          message: '退单后将停止自动扣餐，剩余未用餐次将顺延排期。确定退单？',
          type: 'warning',
          confirmText: '确定退单'
        })
        const id = order._id || order.id
        const resp = await this.$api.post(`/admin/orders/${id}/chargeback`, {
          reason,
          print_receipt: !!this.chargebackForm.print_receipt
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '退单成功')
          this.chargebackDialogVisible = false
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '退单失败')
        }
      } catch (e) {
        if (e === 'cancel') return
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '退单失败'
        this.$message.error(msg)
      }
    },
    /** 随心订：已支付/待发货可发货；收银台订单：待发货/已下单/已支付可发货（发货后变为进行中） */
    showShipButton(order) {
      const r = order.raw_order_status
      if (this.isPackageDelivery) return r === '已支付' || r === '待发货'
      if (this.isOrderSituation) {
        return r === '待发货' || r === '已下单' || r === '已支付'
      }
      return false
    },
    viewOrder(order) {
      this.openOrderDetail(order)
    },
    handleOrderMoreAction(command, order) {
      const map = {
        remark: () => this.remarkOrder(order),
        extend: () => this.extendOrder(order),
        pause: () => this.confirmPauseOrder(order),
        resume: () => this.confirmResumeOrder(order),
        ship: () => this.shipOrder(order),
        printBrowser: () => this.printReceipt(order),
        chargeback: () => this.openChargebackDialog(order),
        refund: () => this.refundOrder(order),
        cancelSub: () => this.adminCancelSubscription(order),
        complete: () => this.confirmCompleteOrder(order),
        delete: () => this.deleteOrder(order)
      }
      const fn = map[command]
      if (fn) fn()
    },
    async completePickupPrepare(order) {
      try {
        const resp = await this.$api.post('/display/complete-prepare', {
          order_id: order._id || order.id
        })
        if (resp && resp.code === 200) {
          this.$message.success('已完成制作，进入请取餐')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '操作失败')
        }
      } catch (e) {
        this.$message.error(
          (e && e.response && e.response.data && e.response.data.message) || '操作失败'
        )
      }
    },
    async confirmPickupOrder(order) {
      try {
        const resp = await this.$api.post('/display/confirm-pickup', {
          order_id: order._id || order.id
        })
        if (resp && resp.code === 200) {
          this.$message.success('已确认取餐')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '操作失败')
        }
      } catch (e) {
        this.$message.error(
          (e && e.response && e.response.data && e.response.data.message) || '操作失败'
        )
      }
    },
    openOrderDetail(order) {
      const id = order._id || order.id
      if (id == null) {
        this.$message.error('缺少订单 ID')
        return
      }
      this.orderDetailVisible = true
      this.orderDetailCard = { ...order }
      this.detailDeliveryRegion = order.delivery_region || ''
      const rid = order.rider_id
      this.detailRiderId = rid != null && String(rid).trim() !== '' ? String(rid) : ''
      this.detailReceiptMaskPhone = !!order.receipt_mask_phone
      this.detailMealCreditsCount = Math.max(1, Number(order.meal_credits_count) || 1)
      this.detailMealConfig = this.resolveOrderMealConfigFromRow(order) || ''
      this.detailMealConfigOriginal = this.detailMealConfig
      this.fetchOrderDetail(id)
    },
    async fetchOrderDetail(id) {
      if (id == null || id === '') return
      this.orderDetailLoading = true
      try {
        const resp = await this.$api.get(`/admin/orders/${id}`, { params: { _t: Date.now() } })
        if (resp && resp.code === 200 && resp.data) {
          this.orderDetailCard = this.mapRawOrderToCard(resp.data)
          this.detailDeliveryRegion = this.orderDetailCard.delivery_region || ''
          const rid = this.orderDetailCard.rider_id
          this.detailRiderId = rid != null && String(rid).trim() !== '' ? String(rid) : ''
          this.detailReceiptMaskPhone = !!(this.orderDetailCard.receipt_mask_phone)
          this.detailMealCreditsCount = Math.max(1, Number(this.orderDetailCard.meal_credits_count) || 1)
          this.detailMealConfig = this.resolveOrderMealConfigFromRow(this.orderDetailCard) || ''
          this.detailMealConfigOriginal = this.detailMealConfig
        } else {
          this.$message.error((resp && resp.message) || '加载订单失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '加载订单失败'
        this.$message.error(msg)
      } finally {
        this.orderDetailLoading = false
      }
    },
    async saveOrderDetailDelivery() {
      const id = this.orderDetailCard && (this.orderDetailCard._id || this.orderDetailCard.id)
      if (!id) return
      const nextCfg = String(this.detailMealConfig || '').trim()
      const prevCfg = String(this.detailMealConfigOriginal || '').trim()
      if (
        this.orderDetailCard &&
        this.orderDetailCard.order_type === '随心订' &&
        nextCfg &&
        prevCfg &&
        nextCfg !== prevCfg
      ) {
        try {
          await this.$confirm(
            '切换餐次类型会清理对应时段历史未核销排程，是否确认？',
            '确认切换配送餐次',
            { type: 'warning', confirmButtonText: '确认切换', cancelButtonText: '取消' }
          )
        } catch (_e) {
          return
        }
      }
      this.orderDetailSaving = true
      try {
        const resp = await this.$api.put(`/admin/orders/${id}/delivery`, {
          delivery_region: this.detailDeliveryRegion != null ? String(this.detailDeliveryRegion).trim() : '',
          rider_id: this.detailRiderId || '',
          receipt_mask_phone: !!this.detailReceiptMaskPhone,
          meal_credits_count: Math.max(1, Number(this.detailMealCreditsCount) || 1),
          meal_config: this.detailMealConfig || undefined
        })
        if (resp && resp.code === 200) {
          await this.fetchOrderDetail(id)
          this.upsertOrderInList(this.orderDetailCard)
          if (this.orderDetailCard) {
            this.$set(this.orderDetailCard, 'meal_config', this.detailMealConfig)
            this.$set(this.orderDetailCard, 'meal_slot', this.mealConfigToSlots(this.detailMealConfig).join('、'))
          }
          this.detailMealConfigOriginal = this.detailMealConfig
          if (this.subCalendarVisible && this.subCalendarOrder) {
            const oid = this.subCalendarOrder._id != null ? this.subCalendarOrder._id : this.subCalendarOrder.id
            if (String(oid) === String(id)) {
              this.$set(this.subCalendarOrder, 'meal_config', this.detailMealConfig)
              this.$set(this.subCalendarOrder, 'meal_slot', this.mealConfigToSlots(this.detailMealConfig).join('、'))
              await this.loadSubscriptionCalendar()
            }
          }
          this.$message.success((resp && resp.message) || '配送信息已保存')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '保存失败'
        this.$message.error(msg)
      } finally {
        this.orderDetailSaving = false
      }
    },
    async remarkOrder(order) {
      const id = order && (order._id || order.id)
      if (id == null || id === '') {
        this.$message.error('缺少订单 ID')
        return
      }
      this.remarkOrderId = String(id)
      this.remarkDraft = this.extractRemarkText(order && order.order_note != null ? String(order.order_note) : '')
      this.remarkDialogVisible = true
    },
    async saveRemarkDialog() {
      if (!this.remarkOrderId) return
      this.remarkSaving = true
      try {
        const idx = this.allOrders.findIndex((x) => String(x._id || x.id) === String(this.remarkOrderId))
        const currentNote = idx >= 0 ? String(this.allOrders[idx].order_note || '') : ''
        const currentDelay = this.extractDelayText(currentNote)
        const mergedNote = this.composeOrderNote(currentDelay, this.remarkDraft)
        const resp = await this.$api.put(`/admin/orders/${this.remarkOrderId}/note`, {
          order_note: mergedNote
        })
        if (resp && resp.code === 200) {
          const saved =
            (resp.data && resp.data.order_note != null ? String(resp.data.order_note) : '') ||
            mergedNote
          if (idx >= 0) {
            this.$set(this.allOrders[idx], 'order_note', saved.trim())
            this.applyFilters()
          }
          this.remarkDialogVisible = false
          this.$message.success('发货备注已保存')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '保存失败'
        this.$message.error(msg)
      } finally {
        this.remarkSaving = false
      }
    },
    async showDelayRecord(order) {
      const id = order && (order._id || order.id)
      if (id == null || id === '') {
        this.$message.error('缺少订单 ID')
        return
      }
      this.delayRecordOrder = order
      this.delayRecordPagination.page = 1
      this.delayRecordDialogVisible = true
      await this.loadDelayRecordPage(1)
    },
    async loadDelayRecordPage(page) {
      const order = this.delayRecordOrder
      const id = order && (order._id || order.id)
      if (!id) return
      this.delayRecordLoading = true
      try {
        const resp = await this.$api.get('/admin/meal-schedules/deferrals', {
          params: {
            order_id: String(id),
            page: page || this.delayRecordPagination.page,
            pageSize: this.delayRecordPagination.pageSize
          }
        })
        if (resp && resp.code === 200 && resp.data) {
          this.delayRecordList = resp.data.list || []
          this.delayRecordPagination.total = resp.data.total || 0
          this.delayRecordPagination.page = resp.data.page || page || 1
        } else {
          this.$message.error((resp && resp.message) || '加载延期记录失败')
        }
      } catch (e) {
        this.$message.error((e && e._networkHint) || '加载延期记录失败')
      } finally {
        this.delayRecordLoading = false
      }
    },
    exportDelayRecordForOrder() {
      const order = this.delayRecordOrder
      const id = order && (order._id || order.id)
      if (!id) return
      const token = getAdminToken() || ''
      const base = this.$api.defaults.baseURL || '/api'
      window.open(
        `${base}/admin/meal-schedules/deferrals/export?order_id=${encodeURIComponent(String(id))}&token=${encodeURIComponent(token)}`,
        '_blank'
      )
    },
    canPauseOrder(order) {
      if (!order || order.order_type !== '随心订') return false
      if (!this.getSubscriptionIdFromOrder(order)) return false
      if (String(order.meal_run_status || 'active') === 'paused') return false
      const st = String(order.raw_order_status || order.order_status || '')
      return !['已取消', '已退款', '已取消订阅'].includes(st)
    },
    canResumeOrder(order) {
      if (!order || order.order_type !== '随心订') return false
      return String(order.meal_run_status || 'active') === 'paused'
    },
    async confirmPauseOrder(order) {
      const id = order && (order._id || order.id)
      if (!id) return
      try {
        await appConfirm({
          title: '暂停订单',
          message: '暂停后：超时核销任务将跳过该订单，中餐/晚餐截止时间不会自动扣减剩余餐次；暂停期间不可发起延期。确定暂停？',
          type: 'warning',
          confirmText: '确定暂停'
        })
      } catch (e) {
        return
      }
      try {
        const resp = await this.$api.post(`/admin/orders/${id}/pause-meals`)
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '订单已暂停')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '暂停失败')
        }
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || '暂停失败'
        this.$message.error(msg)
      }
    },
    async confirmResumeOrder(order) {
      const id = order && (order._id || order.id)
      if (!id) return
      try {
        await appConfirm({
          title: '恢复继续',
          message: '恢复后订单回归正常排餐与超时扣餐；暂停空档不会补扣历史餐次，剩余餐次完整保留。确定恢复？',
          type: 'info',
          confirmText: '确定恢复'
        })
      } catch (e) {
        return
      }
      try {
        const resp = await this.$api.post(`/admin/orders/${id}/resume-meals`)
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '订单已恢复')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '恢复失败')
        }
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || '恢复失败'
        this.$message.error(msg)
      }
    },
    resetPrintDialog() {
      this.printDialogOrder = null
      this.printDialogMode = 'print'
      this.printDialogLoading = false
      this.printDialogSubmitting = false
      this.printDialogSlots = []
      this.printDialogForm = { mealCreditsCount: 1, mealSlot: '午餐', mealDate: '' }
      this.printDialogAvailability = { today: '', lunch: 0, dinner: 0, lunchPool: 0, dinnerPool: 0 }
    },
    async loadPrintDialogAvailability(order) {
      const today = this.getChinaTodayStr()
      this.printDialogAvailability = { today, lunch: 0, dinner: 0, lunchPool: 0, dinnerPool: 0 }
      const sid = this.getSubscriptionIdFromOrder(order)
      if (!sid) return
      this.printDialogLoading = true
      try {
        const resp = await this.$api.get(`/admin/meal-schedules/subscription/${sid}/stats`)
        if (this.printDialogOrder && resp && resp.data) {
          this.printDialogOrder = {
            ...this.printDialogOrder,
            total_meals: resp.data.total_meals,
            remaining_meals: resp.data.remaining_meals,
            used_meals: resp.data.used_meals
          }
        }
        this.printDialogAvailability.today = today
      } catch (_e) {
        /* 列表上的剩余餐次仍可用 */
      } finally {
        this.printDialogLoading = false
      }
    },
    pickDefaultPrintMealSlot(slots, availability) {
      if (!slots.length) return '午餐'
      if (slots.length === 1) return slots[0]
      const { lunch, dinner } = availability
      if (lunch > 0 && dinner <= 0) return '午餐'
      if (dinner > 0 && lunch <= 0) return '晚餐'
      try {
        const hm = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Shanghai',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).format(new Date())
        if (hm >= '12:30' && slots.includes('晚餐')) return '晚餐'
      } catch (_e) {
        if (new Date().getHours() >= 12 && slots.includes('晚餐')) return '晚餐'
      }
      return slots.includes('午餐') ? '午餐' : slots[0]
    },
    async openPrintDialog(order, mode = 'print') {
      this.resetPrintDialog()
      this.printDialogMode = mode === 'verify' ? 'verify' : 'print'
      if (this.printDialogMode === 'verify' && order.order_type !== '随心订') {
        this.$message.warning('仅随心订订单可手动核销')
        return
      }
      let freshOrder = { ...order }
      const sid = this.getSubscriptionIdFromOrder(order)
      if (sid && order.order_type === '随心订') {
        try {
          const resp = await this.$api.get(`/admin/meal-schedules/subscription/${sid}/stats`)
          if (resp && resp.data) {
            freshOrder = {
              ...freshOrder,
              total_meals: resp.data.total_meals,
              remaining_meals: resp.data.remaining_meals,
              used_meals: resp.data.used_meals
            }
          }
        } catch (_e) {
          /* 使用列表缓存 */
        }
      }
      this.printDialogOrder = freshOrder
      const savedCount = Math.max(1, Number(freshOrder.meal_credits_count) || 1)
      const defaultCount = savedCount > 1 ? savedCount : 1
      if (freshOrder.order_type === '随心订') {
        this.printDialogSlots = this.mealConfigToSlots(
          freshOrder.meal_config || this.mealConfigFromMode(this.mealsModeFromProduct([]))
        )
        if (!freshOrder.meal_config) {
          const fromSlot = this.parseMealSlotsToArray(freshOrder.meal_slot)
          if (fromSlot.length === 1) {
            this.printDialogSlots = fromSlot
          } else if (fromSlot.length >= 2) {
            this.printDialogSlots = ['午餐', '晚餐']
          }
        }
        await this.loadPrintDialogAvailability(freshOrder)
        this.printDialogForm.mealSlot = this.pickDefaultPrintMealSlot(
          this.printDialogSlots,
          this.printDialogAvailability
        )
        this.printDialogForm.mealCreditsCount = defaultCount
        this.printDialogForm.mealDate =
          this.printDialogAvailability.today || this.formatDatePickerVal(new Date())
      } else {
        this.printDialogForm.mealCreditsCount = defaultCount
      }
      this.printDialogVisible = true
    },
    async submitPrintDialog() {
      const order = this.printDialogOrder
      if (!order || !this.printDialogCanSubmit) return
      const id = order._id || order.id
      if (id == null) {
        this.$message.error('缺少订单 ID')
        return
      }
      const mealCreditsCount = Math.max(1, Number(this.printDialogForm.mealCreditsCount) || 1)
      let mealSlot = null
      if (order.order_type === '随心订') {
        mealSlot = this.printDialogForm.mealSlot
        if (this.printDialogSlots.length === 1) {
          mealSlot = this.printDialogSlots[0]
        }
      } else {
        if (this.printDialogMode === 'verify') {
          this.$message.warning('仅随心订订单可手动核销')
          return
        }
        try {
          await this.confirmWithPrintDialog(order, id)
        } catch (e) {
          return
        }
      }
      this.printDialogSubmitting = true
      try {
        const payload = { order_id: id, meal_credits_count: mealCreditsCount }
        if (mealSlot) payload.meal_slot = mealSlot
        const isVerify = this.printDialogMode === 'verify'
        // 走已有打印路由 + verify_only，避免线上缺少 /verify-only 时 404
        if (isVerify) {
          payload.verify_only = true
          if (this.printDialogForm.mealDate) payload.meal_date = this.printDialogForm.mealDate
        }
        const isFlex = order.order_type === '随心订'
        // 随心订核销/配送走映美；其余零售单走飞鹅
        const apiPath =
          isFlex || isVerify ? '/admin/print/jolimark/order' : '/admin/print/feie/order'
        const resp = await this.$api.post(apiPath, payload, { timeout: 120000 })
        if (resp && resp.code === 200) {
          this.$message.success(
            resp.message ||
              (isVerify ? '已手动核销' : isFlex ? '已提交映美打印' : '已提交飞鹅打印')
          )
          this.printDialogVisible = false
          if (isFlex) {
            this.getOrders()
            if (
              this.subCalendarVisible &&
              this.subCalendarOrder &&
              (this.subCalendarOrder._id || this.subCalendarOrder.id) === id
            ) {
              this.loadSubscriptionCalendar()
            }
          } else {
            this.getOrders()
          }
        } else {
          this.$message.error(
            (resp && resp.message) ||
              (isVerify ? '手动核销失败' : isFlex ? '映美打印失败' : '飞鹅打印失败')
          )
        }
      } catch (e) {
        const status = e && e.response && e.response.status
        const isFlex = order && order.order_type === '随心订'
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          (status === 404
            ? '核销接口未部署，请上传最新后端并重启服务'
            : this.printDialogMode === 'verify'
              ? '手动核销失败'
              : isFlex
                ? '映美打印失败，请检查 .env 与网络'
                : '飞鹅打印失败，请检查 FEIE_PRINT_* 配置')
        this.$message.error(msg)
      } finally {
        this.printDialogSubmitting = false
      }
    },
    printJolimarkOne(order) {
      this.openPrintDialog(order, 'print')
    },
    manualVerifyOne(order) {
      this.openPrintDialog(order, 'verify')
    },
    openBatchVerifyPastDialog() {
      const d =
        this.scheduleFilterDate ||
        this.formatDatePickerVal(new Date(Date.now() - 24 * 60 * 60 * 1000))
      this.batchVerifyPastForm = { date: d, mealSlot: '' }
      this.batchVerifyPastPreview = null
      this.batchVerifyPastVisible = true
      this.$nextTick(() => this.previewBatchVerifyPast())
    },
    getOrderPlainId(order) {
      return String((order && (order._id || order.id)) || '')
    },
    isOrderBatchSelectable(order) {
      if (!order || String(order.order_type || '') !== '随心订') return false
      const status = String(order.raw_order_status || order.order_status || '').trim()
      if (!status || status === '待支付') return false
      return !['已完成', '配送完成', '已取消', '已取消订阅', '已退款', '已退单'].includes(status)
    },
    isOrderSelected(order) {
      const id = this.getOrderPlainId(order)
      return id ? this.selectedOrderIds.includes(id) : false
    },
    toggleOrderSelection(order, checked) {
      const id = this.getOrderPlainId(order)
      if (!id || !this.isOrderBatchSelectable(order)) return
      const set = new Set(this.selectedOrderIds)
      if (checked) set.add(id)
      else set.delete(id)
      this.selectedOrderIds = Array.from(set)
    },
    toggleSelectAllOrders(checked) {
      const ids = this.selectableOrdersOnPage.map((o) => this.getOrderPlainId(o)).filter(Boolean)
      if (!checked) {
        const remove = new Set(ids)
        this.selectedOrderIds = this.selectedOrderIds.filter((id) => !remove.has(id))
        return
      }
      const set = new Set(this.selectedOrderIds)
      ids.forEach((id) => set.add(id))
      this.selectedOrderIds = Array.from(set)
    },
    clearOrderSelection() {
      this.selectedOrderIds = []
    },
    openBatchDelayDialog() {
      if (!this.selectedOrderIds.length) {
        this.$message.warning('请先勾选订单')
        return
      }
      this.batchDelayForm = { delayMode: '1', customMeals: 3 }
      this.batchDelayVisible = true
    },
    onBatchDelayModeChange() {
      if (this.batchDelayForm.delayMode === 'custom' && !this.batchDelayForm.customMeals) {
        this.batchDelayForm.customMeals = 3
      }
    },
    async submitBatchDelay() {
      if (!this.selectedOrderIds.length || this.batchDelaySubmitting) return
      const delayMeals = this.batchDelayMealsValue
      try {
        await appConfirm(this, {
          title: '确认批量延期',
          message: `将对 ${this.selectedOrderIds.length} 个订单各延期 ${delayMeals} 餐，是否继续？`,
          type: 'warning',
          confirmText: '确认延期'
        })
      } catch (e) {
        return
      }
      this.batchDelaySubmitting = true
      try {
        const resp = await this.$api.post('/admin/subscription-orders/batch-delay', {
          order_ids: this.selectedOrderIds,
          delay_meals: delayMeals
        })
        const batchCheck = validateBatchDeferResponse(resp)
        if (batchCheck.ok) {
          const data = batchCheck.data || {}
          const failed = Number(data.failed) || 0
          if (batchCheck.partial && Array.isArray(data.errors) && data.errors.length) {
            const detail = data.errors
              .slice(0, 5)
              .map((x) => `${x.order_no || x.order_id || ''}: ${x.message || ''}`)
              .join('\n')
            this.$alert(
              `${batchCheck.message || '批量延期完成'}\n\n${detail}${data.errors.length > 5 ? '\n…' : ''}`,
              '批量延期结果',
              { type: 'warning' }
            )
          } else {
            this.$message.success(batchCheck.message || '批量延期完成')
          }
          this.batchDelayVisible = false
          this.clearOrderSelection()
          await this.getOrders()
        } else {
          this.$message.error(batchCheck.message || extractDeferErrorMessage(null, resp, '批量延期失败'))
        }
      } catch (e) {
        this.$message.error(extractDeferErrorMessage(e, null, e._networkHint || '批量延期失败'))
      } finally {
        this.batchDelaySubmitting = false
      }
    },
    openBatchCheckDialog() {
      if (!this.selectedOrderIds.length) {
        this.$message.warning('请先勾选订单')
        return
      }
      this.batchCheckForm = {
        checkDate: this.formatDatePickerVal(new Date()),
        mealType: ''
      }
      this.batchCheckPreview = null
      this.batchCheckVisible = true
      this.$nextTick(() => this.previewBatchCheck())
    },
    async previewBatchCheck() {
      if (!this.batchCheckForm.checkDate || !this.selectedOrderIds.length) {
        this.batchCheckPreview = null
        return
      }
      this.batchCheckLoading = true
      try {
        const resp = await this.$api.post('/admin/subscription-orders/batch-check', {
          order_ids: this.selectedOrderIds,
          check_date: this.batchCheckForm.checkDate,
          meal_type: this.batchCheckForm.mealType || '全部',
          dry_run: true
        })
        if (resp && resp.code === 200) {
          this.batchCheckPreview = resp.data || null
        } else {
          this.batchCheckPreview = null
          this.$message.error((resp && resp.message) || '预览失败')
        }
      } catch (e) {
        this.batchCheckPreview = null
        this.$message.error(e._networkHint || '预览失败')
      } finally {
        this.batchCheckLoading = false
      }
    },
    async submitBatchCheck() {
      if (!this.batchCheckCanSubmit || this.batchCheckSubmitting) return
      const pending = Number(this.batchCheckPreview && this.batchCheckPreview.pending) || 0
      try {
        await appConfirm(this, {
          title: '确认批量核销',
          message: `将核销 ${this.batchCheckForm.checkDate} 共 ${pending} 笔餐次（不打印小票），是否继续？`,
          type: 'warning',
          confirmText: '确认核销'
        })
      } catch (e) {
        return
      }
      this.batchCheckSubmitting = true
      try {
        const resp = await this.$api.post('/admin/subscription-orders/batch-check', {
          order_ids: this.selectedOrderIds,
          check_date: this.batchCheckForm.checkDate,
          meal_type: this.batchCheckForm.mealType || '全部',
          dry_run: false
        })
        if (resp && resp.code === 200) {
          const data = resp.data || {}
          const failed = Number(data.failed) || 0
          const skipped = Number(data.skipped) || 0
          if ((failed > 0 || skipped > 0) && Array.isArray(data.errors) && data.errors.length) {
            const detail = data.errors
              .slice(0, 5)
              .map((x) => `${x.order_no || x.order_id || ''}: ${x.message || ''}`)
              .join('\n')
            this.$alert(
              `${resp.message || '批量核销完成'}\n\n${detail}${data.errors.length > 5 ? '\n…' : ''}`,
              '批量核销结果',
              { type: failed > 0 ? 'warning' : 'success' }
            )
          } else {
            this.$message.success(resp.message || '批量核销完成')
          }
          this.batchCheckVisible = false
          this.clearOrderSelection()
          await this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '批量核销失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || (e && e.message) || '批量核销失败')
      } finally {
        this.batchCheckSubmitting = false
      }
    },
    async previewBatchVerifyPast() {
      if (!this.batchVerifyPastForm.date) {
        this.batchVerifyPastPreview = null
        return
      }
      this.batchVerifyPastLoading = true
      try {
        const resp = await this.$api.post('/admin/meal-schedules/batch-verify-past', {
          date: this.batchVerifyPastForm.date,
          meal_slot: this.batchVerifyPastForm.mealSlot || '',
          dry_run: true
        })
        if (resp && resp.code === 200) {
          this.batchVerifyPastPreview = resp.data || null
        } else {
          this.batchVerifyPastPreview = null
          this.$message.error((resp && resp.message) || '预览失败')
        }
      } catch (e) {
        this.batchVerifyPastPreview = null
        this.$message.error(e._networkHint || '预览失败')
      } finally {
        this.batchVerifyPastLoading = false
      }
    },
    async submitBatchVerifyPast() {
      if (!this.batchVerifyPastCanSubmit) return
      const date = this.batchVerifyPastForm.date
      const pending = Number(this.batchVerifyPastPreview && this.batchVerifyPastPreview.pending) || 0
      try {
        await appConfirm({
          title: '确认补核销',
          message: `将核销 ${date} 共 ${pending} 笔未用餐餐次（不打印小票）。是否继续？`,
          type: 'warning',
          confirmText: '确认核销'
        })
      } catch (e) {
        return
      }
      this.batchVerifyPastSubmitting = true
      try {
        const resp = await this.$api.post('/admin/meal-schedules/batch-verify-past', {
          date,
          meal_slot: this.batchVerifyPastForm.mealSlot || '',
          dry_run: false
        })
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '补核销完成')
          this.batchVerifyPastVisible = false
          this.getOrders()
          if (this.showScheduleTable) this.loadTodayMealOrders()
          if (this.subCalendarVisible) this.loadSubscriptionCalendar()
        } else {
          this.$message.error((resp && resp.message) || '补核销失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || '补核销失败')
      } finally {
        this.batchVerifyPastSubmitting = false
      }
    },
    subCalendarDayHasPendingVerify(day) {
      if (!day || !day.date) return false
      const today = this.formatDatePickerVal(new Date())
      if (String(day.date) > String(today)) return false
      const isPending = (meal) =>
        !!(meal && meal.active && (meal.source === 'initial' || meal.status === '未用餐'))
      return isPending(day.lunch) || isPending(day.dinner)
    },
    async verifyCalendarDayPending(day) {
      if (!day || !day.date) return
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) {
        this.$message.error('缺少订阅 ID')
        return
      }
      try {
        await appConfirm({
          title: '核销本日未用餐',
          message: `将本订单 ${day.date} 仍为未用餐的餐次全部补核销（不打印小票）。是否继续？`,
          type: 'warning',
          confirmText: '确认核销'
        })
      } catch (e) {
        return
      }
      this.batchVerifyPastSubmitting = true
      try {
        const resp = await this.$api.post('/admin/meal-schedules/batch-verify-past', {
          date: day.date,
          subscription_id: sid,
          dry_run: false
        })
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '已补核销')
          await this.loadSubscriptionCalendar()
          if (this.subCalendarDayDetail && this.subCalendarDayDetail.date) {
            const next = ((this.subCalendarData && this.subCalendarData.days) || []).find(
              (d) => d.date === this.subCalendarDayDetail.date
            )
            this.subCalendarDayDetail = next || null
          }
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '补核销失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || '补核销失败')
      } finally {
        this.batchVerifyPastSubmitting = false
      }
    },
    async printJolimarkTodayBatch(meal) {
      const m = meal || 'both'
      try {
        await appConfirm({
          title: '批量映美打印',
          message: `将按订单逐张提交映美云打印（每单按「本餐消耗餐次」出票，默认 1 张/餐次）。餐次筛选：${
            m === 'lunch' ? '仅午餐' : m === 'dinner' ? '仅晚餐' : '午餐+晚餐'
          }，且为今日下单的随心订订单。是否继续？`,
          type: 'warning',
          confirmText: '开始打印'
        })
      } catch (e) {
        return
      }
      const loading = this.$loading({ lock: true, text: '正在提交映美打印…' })
      try {
        const resp = await this.$api.post(
          '/admin/print/jolimark/today-meals',
          { meal: m },
          { timeout: 300000 }
        )
        if (resp && resp.code === 200) {
          const d = resp.data || {}
          this.$message.success(
            resp.message || `已提交 ${d.okCount != null ? d.okCount : 0}/${d.count || 0} 单`
          )
        } else {
          this.$message.error((resp && resp.message) || '批量打印失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          '批量打印失败'
        this.$message.error(msg)
      } finally {
        loading.close()
      }
    },
    async previewJolimarkTodayBatch(meal) {
      const m = meal || 'both'
      const mealLabel = m === 'lunch' ? '仅午餐' : m === 'dinner' ? '仅晚餐' : '午餐+晚餐'
      try {
        const resp = await this.$api.get('/admin/print/jolimark/today-meals-preview', { params: { meal: m } })
        if (resp && resp.code === 200 && resp.data) {
          this.batchPreview = {
            mode: 'today',
            today: resp.data.today || '',
            meal: m,
            mealLabel,
            included: Array.isArray(resp.data.included) ? resp.data.included : [],
            excluded: Array.isArray(resp.data.excluded) ? resp.data.excluded : [],
            from: '',
            to: '',
            periodDates: [],
            skippedCount: 0
          }
          this.batchPreviewDialogVisible = true
        } else {
          this.$message.error((resp && resp.message) || '预览失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          '预览失败'
        this.$message.error(msg)
      }
    },
    async previewJolimarkPeriodBatch() {
      const r = this.jolimarkPeriodRange
      if (!r || r.length !== 2 || !r[0] || !r[1]) {
        this.$message.warning('请选择日期区间')
        return
      }
      const [from, to] = r
      const m = 'both'
      const mealLabel = '午餐+晚餐'
      try {
        const resp = await this.$api.get('/admin/print/jolimark/period-meals-preview', {
          params: { from, to, meal: m }
        })
        if (resp && resp.code === 200 && resp.data) {
          const pd = Array.isArray(resp.data.dates) ? resp.data.dates : []
          const skipped = Array.isArray(resp.data.skipped_dates) ? resp.data.skipped_dates : []
          this.batchPreview = {
            mode: 'period',
            today: '',
            meal: m,
            mealLabel,
            included: [],
            excluded: [],
            from: resp.data.from || from,
            to: resp.data.to || to,
            periodDates: pd,
            skippedCount: skipped.length
          }
          this.batchPreviewDialogVisible = true
        } else {
          this.$message.error((resp && resp.message) || '预览失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          '预览失败'
        this.$message.error(msg)
      }
    },
    modifyOrder(order) {
      const id = order && (order._id || order.id)
      if (id == null || id === '') {
        this.$message.error('缺少订单 ID')
        return
      }
      this.orderDialogMode = 'edit'
      this.orderFormLoading = true
      this.currentOrderIndex = this.allOrders.findIndex(
        (o) => String(o._id || o.id) === String(id)
      )
      this.orderDialogVisible = true
      this.loadCustomerUsers()
      Promise.all([
        this.loadOrderProducts(),
        this.loadAdminDeliveryMeta(),
        this.fetchOrderForEdit(id)
      ]).finally(() => {
        this.orderFormLoading = false
      })
    },
    async fetchOrderForEdit(id) {
      try {
        const resp = await this.$api.get(`/admin/orders/${id}`, { params: { _t: Date.now() } })
        if (!resp || resp.code !== 200 || !resp.data) {
          this.$message.error((resp && resp.message) || '加载订单失败')
          this.orderDialogVisible = false
          return
        }
        this.populateOrderEditForm(this.mapRawOrderToCard(resp.data), resp.data)
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '加载订单失败'
        this.$message.error(msg)
        this.orderDialogVisible = false
      }
    },
    populateOrderEditForm(card, raw) {
      const periodText = (card.subscription_period || (raw && raw.remark) || '').trim()
      const range0 = this.parseSubscriptionPeriodToRange(periodText)
      const savedMealSlots = this.parseMealSlotsToArray(card.meal_slot || (raw && raw.meal_slot))
      const sub =
        raw && raw.subscription_id && typeof raw.subscription_id === 'object' ? raw.subscription_id : null
      const targetPerCopy =
        sub && sub.target_total_meals != null && Number(sub.target_total_meals) > 0
          ? Math.floor(Number(sub.target_total_meals))
          : card.total_meals != null && card.quantity
            ? Math.floor(Number(card.total_meals) / Math.max(1, Number(card.quantity)))
            : 0
      const qtyNum = card.quantity != null ? Math.max(1, Number(card.quantity)) : 1
      const histTotalFromApi =
        raw && raw.historical_total_meals != null && Number(raw.historical_total_meals) > 0
          ? Math.floor(Number(raw.historical_total_meals))
          : targetPerCopy > 0
            ? targetPerCopy * qtyNum
            : card.total_meals != null
              ? Math.floor(Number(card.total_meals))
              : 0
      const usedMeals =
        card.used_meals != null ? Math.max(0, Math.floor(Number(card.used_meals))) : 0
      const histDelivered =
        raw && raw.historical_meals_delivered != null
          ? Math.max(0, Math.floor(Number(raw.historical_meals_delivered)))
          : usedMeals
      const isHistoricalOrder =
        raw && raw.historical_order === true
          ? true
          : raw && raw.historical_order === false
            ? false
            : histDelivered > 0 && histTotalFromApi > 0
      const uid = (() => {
        const u = raw && raw.user_id
        if (card.user_id) return String(card.user_id)
        if (u == null || u === '') return ''
        if (typeof u === 'object') return String(u.id || u._id || '')
        return String(u)
      })()

      this.orderForm = {
        _id: card._id,
        id: card.id,
        order_no: card.order_no,
        user_id: uid,
        user_name: card.user_name || '',
        consignee_name:
          (raw && raw.consignee_name != null ? String(raw.consignee_name) : '') ||
          card.consignee_name ||
          card.user_name ||
          '',
        user_phone: card.user_phone || '',
        address: card.address || '',
        order_note: card.order_note || '',
        delivery_region: card.delivery_region || '',
        rider_id: card.rider_id || '',
        rider_name: card.rider_name || '',
        rider_phone: card.rider_phone || '',
        product_name: card.product_name || '',
        productSelectKey: '',
        manual_target_total_meals: targetPerCopy > 0 ? targetPerCopy : null,
        unit_price: card.unit_price != null ? Number(card.unit_price) : 0,
        quantity: card.quantity != null ? Number(card.quantity) : 1,
        total_price: card.total_price != null ? Number(card.total_price) : 0,
        order_type: card.order_type || '随心订',
        order_status: card.order_status,
        raw_order_status: card.raw_order_status || (raw && raw.order_status) || '',
        subscription_period: periodText,
        subscription_date_range: range0,
        subscription_period_start:
          range0 && range0[0]
            ? range0[0]
            : sub && sub.start_date
              ? String(sub.start_date).slice(0, 10)
              : '',
        meal_slots: savedMealSlots.length ? savedMealSlots : ['午餐'],
        meal_config: (() => {
          const fromSlots = this.mealConfigFromSlots(
            savedMealSlots.length ? savedMealSlots : ['午餐']
          )
          if (fromSlots !== 'both') return fromSlots
          const oc = String(card.meal_config || '').trim().toLowerCase()
          if (oc === 'only_lunch' || oc === 'only_dinner' || oc === 'both') return oc
          const sc = String((sub && sub.meal_config) || '').trim().toLowerCase()
          if (sc === 'only_lunch' || sc === 'only_dinner' || sc === 'both') return sc
          return fromSlots
        })(),
        delivery_method: card.delivery_method || '同城配送',
        payment_method: card.payment_method || (raw && raw.payment_method) || 'mini_order',
        shipping_fee: card.shipping_fee != null ? Number(card.shipping_fee) : 0,
        createdAt: card.createdAt || '',
        historical_order: isHistoricalOrder,
        historical_delivery_mode: 'count',
        historical_total_meals: histTotalFromApi > 0 ? histTotalFromApi : targetPerCopy > 0 ? targetPerCopy : 0,
        historical_meals_delivered: histDelivered,
        plan_link_mode: 'new',
        link_subscription_id: sub ? String(sub.id || sub._id) : ''
      }
      this.syncCustomerSearchQueryFromForm()

      const name = (this.orderForm.product_name || '').trim()
      const hit = name ? this.orderProductList.find((p) => String(p.name || '') === name) : null
      this.$set(this.orderForm, 'productSelectKey', hit ? hit.id : '')
      if (hit) this.applyProductMealConfig(hit, { preserveMealSlots: true, preserveManualTarget: true })
      if (savedMealSlots.length) {
        this.$set(this.orderForm, 'meal_slots', savedMealSlots)
      }
      this.$nextTick(() => {
        if (range0 && range0[0]) {
          this.$set(this.orderForm, 'subscription_period_start', range0[0])
          if (range0[1]) {
            this.$set(this.orderForm, 'subscription_date_range', [range0[0], range0[1]])
          }
        }
        this.refreshSubscriptionPeriodPreview()
        this.resolveOrderFormDelivery()
      })
    },
    async shipOrder(order) {
      try {
        await appConfirm({
          title: '发货确认',
          message: `确认将订单 ${order.order_no} 标记为已发货吗？`,
          type: 'warning',
          confirmText: '确定发货'
        })
        const id = order._id || order.id
        await this.$api.put(`/orders/${id}/status`, { order_status: '待配送' })
        this.$message.success('已标记为进行中')
        this.getOrders()
      } catch (e) {
        if (e === 'cancel') return
        this.$message.error('发货更新失败')
      }
    },
    async deleteOrder(order) {
      const id = order._id || order.id
      if (id == null || id === '') {
        this.$message.error('缺少订单 ID')
        return
      }
      try {
        await appConfirm({
          title: '删除确认',
          message: '删除后不可恢复，请确认以下订单信息无误。',
          details: this.buildOrderConfirmDetails(order, id),
          type: 'danger',
          confirmText: '确定删除'
        })
        const resp = await this.$api.delete(`/admin/orders/${id}`)
        if (resp && resp.code === 202) {
          this.$message.warning((resp && resp.message) || '删除申请已提交')
        } else {
          this.$message.success((resp && resp.message) || '删除成功')
        }
        this.getOrders()
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '删除失败'
        this.$message.error(msg)
      }
    },
    async batchDeleteSelectedOrders() {
      if (!this.canAdminDeleteSession) {
        this.$message.warning('当前账号无删除权限')
        return
      }
      if (!this.selectedOrderIds.length || this.batchDeleteSubmitting) return
      const count = this.selectedOrderIds.length
      try {
        await appConfirm({
          title: '批量删除确认',
          message: `确定删除已选的 ${count} 个订单？删除后不可恢复。`,
          type: 'danger',
          confirmText: '确定删除'
        })
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        return
      }
      this.batchDeleteSubmitting = true
      const ids = this.selectedOrderIds.slice()
      let ok = 0
      let pending = 0
      let fail = 0
      const errors = []
      try {
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i]
          try {
            const resp = await this.$api.delete(`/admin/orders/${id}`)
            if (resp && resp.code === 202) {
              pending += 1
            } else if (resp && (resp.code === 200 || resp.code === 0)) {
              ok += 1
            } else {
              fail += 1
              errors.push(`${id}：${(resp && resp.message) || '失败'}`)
            }
          } catch (e) {
            fail += 1
            const msg =
              (e && e.response && e.response.data && e.response.data.message) ||
              (e && e.message) ||
              '失败'
            errors.push(`${id}：${msg}`)
          }
        }
        this.clearOrderSelection()
        await this.getOrders()
        const parts = []
        if (ok) parts.push(`成功 ${ok}`)
        if (pending) parts.push(`已提交审核 ${pending}`)
        if (fail) parts.push(`失败 ${fail}`)
        const summary = parts.join('，') || '无结果'
        if (fail && errors.length) {
          this.$alert(
            `${summary}\n\n${errors.slice(0, 8).join('\n')}${errors.length > 8 ? '\n…' : ''}`,
            '批量删除结果',
            { confirmButtonText: '知道了', type: fail === ids.length ? 'error' : 'warning' }
          )
        } else if (pending && !ok) {
          this.$message.warning(summary)
        } else {
          this.$message.success(summary)
        }
      } finally {
        this.batchDeleteSubmitting = false
      }
    },
    async confirmCompleteOrder(order) {
      const cur = String((order && (order.raw_order_status || order.order_status)) || '').trim()
      if (cur === '已完成') {
        this.$message.info('该订单已是已完成状态')
        return
      }
      const id = order && (order._id || order.id)
      if (id == null || id === '') {
        this.$message.error('缺少订单 ID')
        return
      }
      try {
        await appConfirm({
          title: '确认订单完成',
          message: '确认将该订单标记为已完成？',
          details: this.buildOrderConfirmDetails(order, id),
          type: 'success',
          confirmText: '确认完成'
        })
        await this.$api.put(`/orders/${id}/status`, { order_status: '已完成' })
        this.$message.success('订单已标记为已完成')
        this.getOrders()
      } catch (e) {
        if (e === 'cancel') return
      }
    },
    buildOrderConfirmDetails(order, fallbackId) {
      const id = fallbackId != null ? fallbackId : (order && (order._id || order.id))
      const orderNo = (order && (order.order_no || order.orderNo)) || id || '-'
      const userName = (order && (order.user_name || order.userName)) || '未知用户'
      const userPhone = (order && (order.user_phone || order.phone)) || '—'
      const amount = order && (order.total_amount != null ? order.total_amount : order.final_amount)
      const orderType = (order && (order.order_type || order.orderType)) || '普通订单'
      const deliveryTime =
        (order && (order.delivery_time || order.deliveryTime || order.delivery_date || order.deliveryDate)) || '—'
      const deliveryAddress =
        (order && (order.delivery_address || order.address || order.user_address || order.userAddress)) || '—'
      return [
        { label: '订单号', value: orderNo },
        { label: '客户', value: userName },
        { label: '电话', value: userPhone },
        { label: '金额', value: amount != null ? `¥${amount}` : '—' },
        { label: '订单类型', value: orderType },
        { label: '配送时间', value: deliveryTime },
        { label: '收货地址', value: deliveryAddress }
      ]
    },
    confirmWithPrintDialog(order, fallbackId) {
      return appConfirm({
        title: '映美打印确认',
        message: '请核对以下订单信息后提交打印。',
        details: this.buildOrderConfirmDetails(order, fallbackId),
        type: 'info',
        confirmText: '确认打印'
      })
    },
    confirmWithDetailDialog(order, fallbackId, title, options = {}) {
      return appConfirm({
        title: title || '确认',
        message: options.message || '请核对以下订单信息。',
        details: this.buildOrderConfirmDetails(order, fallbackId),
        type: options.type || 'warning',
        confirmText: options.confirmText || '确定'
      })
    },
    async refundOrder(order) {
      try {
        await appConfirm({
          title: '退款确认',
          message: '确认将该订单标记为「已退款」？请在线下完成退款后再操作。',
          type: 'warning',
          confirmText: '确定退款'
        })
        const id = order._id || order.id
        const resp = await this.$api.put(`/admin/orders/${id}/refund`)
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '已标记为已退款')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '退款失败')
        }
      } catch (e) {
        if (e === 'cancel') return
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '退款失败'
        this.$message.error(msg)
      }
    },
    async extendOrder(order) {
      this.extendTargetOrder = order
      const oid = order && (order._id || order.id)
      if (this.isPackageDelivery && oid != null && oid !== '' && order.order_type === '随心订') {
        try {
          const resp = await this.$api.get(`/admin/orders/${oid}`, { params: { _t: Date.now() } })
          if (resp && resp.code === 200 && resp.data) {
            this.extendTargetOrder = this.mapRawOrderToCard(resp.data)
          }
        } catch (_e) {
          /* 列表数据继续用 */
        }
      }
      this.extendForm = {
        original_date: '',
        new_date: '',
        meal_type: 'lunch',
        meal_slot: '午餐',
        also_dinner: false
      }
      this.extendDialogVisible = true
    },
    async submitMealDefer() {
      const order = this.extendTargetOrder
      if (!order) return
      const subId = this.getSubscriptionIdFromOrder(order)
      const orderId = order._id != null ? order._id : order.id
      const slot = this.extendForm.meal_slot || '午餐'
      const payload = {
        meal_date: this.getChinaTodayStr(),
        meal_slot: slot
      }
      if (subId) payload.subscription_id = subId
      else if (orderId != null && String(orderId).trim() !== '') payload.order_id = orderId
      else {
        this.$message.error('该订单未关联随心订计划')
        return
      }
      try {
        const resp = await this.$api.post('/admin/meal-schedules/defer', {
          ...payload,
          autoSlot: true
        })
        const check = validateDeferSuccessResponse(resp)
        if (check.ok) {
          const data = check.data || {}
          const after = data.after || {}
          const afterDate = after.date ? String(after.date).slice(0, 10) : ''
          const afterPeriod = after.period === 'DINNER' || after.period === 'dinner' ? '晚餐' : after.period === 'LUNCH' || after.period === 'lunch' ? '午餐' : ''
          let msg = '延期成功'
          if (afterDate) {
            msg = `第${data.mealNo || ''}餐已延期至 ${afterDate}${afterPeriod ? ' ' + afterPeriod : ''}`
          }
          this.$message.success(msg)
          this.extendDialogVisible = false
          if (this.subCalendarVisible) {
            await this.reloadAfterDeferSuccess(after)
          } else {
            await this.getOrders()
            if (this.showScheduleTable) this.loadTodayMealOrders()
          }
        } else {
          this.showDeferFailureToast(check.message || extractDeferErrorMessage(null, resp, '延期失败'))
        }
      } catch (e) {
        this.showDeferFailureToast(extractDeferErrorMessage(e, null, '延期失败'))
      }
    },
    async loadTodayMealOrders() {
      if (!this.isPackageDeliveryMain) return
      const date = this.scheduleFilterDate || this.formatDatePickerVal(new Date())
      const mealSlot = String((this.searchForm && this.searchForm.mealSlot) || '').trim()
      try {
        const params = { date }
        if (mealSlot) params.meal_slot = mealSlot
        const resp = await this.$api.get('/admin/meal-schedules/today', { params })
        if (resp && resp.code === 200 && resp.data) {
          const list = resp.data.list || []
          this.todayMealOrders = list
          this.scheduleFilterActive = true
          this.scheduleFilterMatchKeys = list.map(
            (x) => `${String(x.subscription_id || '')}|${String(x.meal_slot || '').trim()}`
          )
          this.scheduleFilterOrderIds = list
            .map((x) => String(x.order_id || '').trim())
            .filter(Boolean)
          this.showScheduleTable = list.length > 0
          this.currentPage = 1
          this.applyFilters()
          const slotHint = mealSlot ? `（${mealSlot}）` : ''
          if (list.length) {
            this.$message.success(`已加载 ${date}${slotHint} 排餐 ${list.length} 条，并筛选下方订单列表`)
          } else {
            this.$message.info(`${date}${slotHint} 暂无排餐记录`)
          }
          return
        }
        this.$message.error((resp && resp.message) || '加载当日排餐失败')
      } catch (e) {
        this.$message.error('加载当日排餐失败')
      }
    },
    exportTodayMeals() {
      const date = this.scheduleFilterDate || this.formatDatePickerVal(new Date())
      const token = getAdminToken() || ''
      const base = this.$api.defaults.baseURL || '/api'
      window.open(`${base}/admin/meal-schedules/today/export?date=${encodeURIComponent(date)}&token=${encodeURIComponent(token)}`, '_blank')
    },
    exportSubscriptionOrders() {
      if (this.subscriptionExportLoading) return
      this.subscriptionExportLoading = true
      const f = this.searchForm || {}
      const params = {
        orderSearchType: f.orderSearchType,
        keyword: f.keyword,
        timeSearchType: f.timeSearchType,
        startDate: f.startDate,
        endDate: f.endDate,
        userName: f.userName,
        userPhone: f.userPhone,
        orderStatus: f.orderStatus,
        mealSlot: f.mealSlot,
        deliveryMethod: f.deliveryMethod,
        paymentMethod: f.paymentMethod
      }
      if (this.scheduleFilterActive && (this.scheduleFilterOrderIds || []).length) {
        params.schedule_filter_active = '1'
        params.schedule_order_ids = this.scheduleFilterOrderIds.join(',')
      }
      axios
        .get(`${apiBase}/admin/subscription-orders/export`, {
          params,
          responseType: 'blob',
          headers: getAdminAuthHeader()
        })
        .then(async (res) => {
          const ct = String((res.headers && res.headers['content-type']) || '')
          if (ct.includes('application/json')) {
            const text = await res.data.text()
            let msg = '导出失败，请稍后重试'
            try {
              const j = JSON.parse(text)
              msg = j.message || msg
            } catch (_e) {
              /* ignore */
            }
            this.$message.error(msg)
            return
          }
          downloadBlobResponse(res, `随心订订单_${this.formatDatePickerVal(new Date())}`)
          this.$message.success('导出成功，请查看浏览器下载记录')
        })
        .catch((e) => {
          this.$message.error((e && e.message) || '导出失败，请稍后重试')
        })
        .finally(() => {
          this.subscriptionExportLoading = false
        })
    },
    downloadSubscriptionOrderTemplate() {
      const token = getAdminToken() || ''
      const base = this.$api.defaults.baseURL || '/api'
      window.open(`${base}/admin/subscription-orders/import-template?token=${encodeURIComponent(token)}`, '_blank')
    },
    handleSubscriptionOrderImport(file) {
      if (!guardUploadFile(this, file, { allowCsv: false })) return false
      const formData = new FormData()
      formData.append('file', file)
      this.$api
        .post('/admin/subscription-orders/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(async (resp) => {
          if (resp && resp.code === 200) {
            showImportResult(this, resp, { successMessage: '导入完成' })
            await this.getOrders()
            return
          }
          const errText = resp && resp.data ? formatImportErrors(resp.data) : ''
          if (errText) {
            this.$alert(errText, '导入校验失败', { type: 'error' })
          } else {
            this.$message.error((resp && resp.message) || '导入失败')
          }
        })
        .catch((e) => {
          const respData = e && e.response && e.response.data
          const errText = respData && respData.data ? formatImportErrors(respData.data) : ''
          const msg = (respData && respData.message) || '导入失败，请确认文件格式与后端服务'
          if (errText) {
            this.$alert(`${msg}\n\n${errText}`, '导入校验失败', { type: 'error' })
          } else {
            this.$message.error(msg)
          }
        })
      return false
    },
    connectMealWebSocket() {
      try {
        const token = getAdminToken() || ''
        if (!token) return
        if (this.mealWs) {
          try {
            this.mealWs.close()
          } catch (e) { /* ignore */ }
          this.mealWs = null
        }
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const host = window.location.host
        const url = `${proto}://${host}/ws/meal-sync?token=${encodeURIComponent(token)}`
        const ws = new WebSocket(url)
        ws.onerror = () => {
          /* Nginx 未配置 /ws/ 反代时会失败，不影响订单列表手动刷新 */
        }
        ws.onclose = () => {
          if (this.mealWs === ws) this.mealWs = null
        }
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data)
            if (msg && msg.type === 'meal_update') {
              this.getOrders()
              if (this.showScheduleTable) this.loadTodayMealOrders()
            }
            if (msg && (msg.type === 'meal_deferral' || msg.type === 'meal_pause' || msg.type === 'meal_resume')) {
              this.getOrders()
              if (this.subCalendarVisible) this.loadSubscriptionCalendar()
              if (this.showScheduleTable) this.loadTodayMealOrders()
            }
          } catch (e) { /* ignore */ }
        }
        this.mealWs = ws
      } catch (e) { /* ignore */ }
    },
    getSubscriptionIdFromOrder(order) {
      const sub = order && order.subscription_id
      if (sub == null || sub === '') return ''
      if (typeof sub === 'object') return String(sub.id || sub._id || '').trim()
      return String(sub).trim()
    },
    subscriptionPlanStatus(order) {
      const sub = order && order.subscription_id
      if (sub && typeof sub === 'object' && sub.status != null) return Number(sub.status)
      return null
    },
    hasSubscriptionAdminPerm() {
      const u = getUserInfo()
      if (!u || typeof u !== 'object') return false
      if (u.role === 'admin' || u.roleLabel === '管理员' || u.roleLabel === '超级管理员') return true
      const perms = Array.isArray(u.permissions) ? u.permissions : []
      return perms.includes('随心订管理')
    },
    canAdminCancelSubscription(order) {
      if (!canAdminDelete()) return false
      if (!order || order.order_type !== '随心订') return false
      if (!this.getSubscriptionIdFromOrder(order)) return false
      if (!this.hasSubscriptionAdminPerm()) return false
      const st = this.subscriptionPlanStatus(order)
      if (st === 4) return false
      return true
    },
    async adminCancelSubscription(order) {
      const sid = this.getSubscriptionIdFromOrder(order)
      if (!sid) return
      try {
        await appConfirm({
          title: '取消订阅计划',
          message: '确定取消该随心订计划？取消后系统不再自动生成新的配送主单（与顾客在小程序取消订阅一致）。已生成的订单仍留在列表中，退款或删单请另行处理。',
          type: 'warning',
          confirmText: '确定取消',
          cancelText: '保留计划'
        })
      } catch (e) {
        return
      }
      try {
        const resp = await this.$api.delete(`/admin/subscriptions/${sid}`)
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '已取消订阅计划')
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '取消失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '取消失败'
        this.$message.error(msg)
      }
    },
    getExtendCycleEndStr() {
      const range = this.getExtendSubscriptionRange()
      if (!range) return ''
      return this.formatDatePickerVal(range.end)
    },
    addCalendarDaysLocal(isoStr, deltaDays) {
      const s = String(isoStr || '').slice(0, 10)
      const parts = s.split('-').map(Number)
      if (parts.length !== 3) return ''
      const t = new Date(parts[0], parts[1] - 1, parts[2] + (Number(deltaDays) || 0))
      if (Number.isNaN(t.getTime())) return ''
      const p = (n) => String(n).padStart(2, '0')
      return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`
    },
    getExtendMinNewDateStr() {
      const end = this.getExtendCycleEndStr()
      if (!end) return ''
      return this.addCalendarDaysLocal(end, 1)
    },
    formatCalendarDaySummary(day) {
      if (!day) return '无餐'
      const s = day.day_meal_summary
      if (s && s !== 'none') return s
      const lunch = day.lunch || {}
      const dinner = day.dinner || {}
      if (lunch.active || dinner.active) return '有餐'
      if (day.delays && day.delays.length) return '含延期'
      return '无餐'
    },
    subCalendarDayToCell(day, idx) {
      const lunch = day.lunch || {}
      const dinner = day.dinner || {}
      const inC = day.in_cycle !== false
      const slots = (this.subCalendarData && this.subCalendarData.meal_slots) ||
        this.mealConfigToSlots((this.subCalendarData && this.subCalendarData.meal_config) || 'both')
      const showLunch = slots.includes('午餐')
      const showDinner = slots.includes('晚餐')
      const lunchTags = this.filterQueueTagsWithinPackage(
        (day.queue && day.queue.lunch) || lunch.queue_tags || []
      )
      const dinnerTags = this.filterQueueTagsWithinPackage(
        (day.queue && day.queue.dinner) || dinner.queue_tags || []
      )
      const queueTagsRaw =
        (day.queue && day.queue.all) || lunchTags.concat(dinnerTags)
      const queueTags = this.filterQueueTagsWithinPackage(queueTagsRaw).slice().sort((a, b) => {
        const ai = Number(a.meal_index) || 0
        const bi = Number(b.meal_index) || 0
        return ai - bi
      })
      const queueMode =
        !!(this.subCalendarData &&
          (this.subCalendarData.calendar_engine === 'meal-queue-v1' ||
            this.subCalendarData.calendar_engine === 'meal-sequence-v2'))
      const hasQueueTags = queueTags.length > 0
      let cls = 'sub-cal-day'
      if (!inC) cls += ' out-cycle'
      if (day.paused) cls += ' has-paused'
      if (day.is_rest) cls += ' has-rest'
      if (day.day_type === 'holiday') cls += ' day-holiday'
      if (day.day_type === 'makeup') cls += ' day-makeup'
      if (day.day_type === 'rest') cls += ' day-rest'

      const slotClass = (meal) => {
        if (!meal || !meal.active) return 'empty'
        if (meal.paused) return 'paused'
        if (meal.source === 'multi_anchor') return 'multi-anchor'
        if (meal.source === 'multi_linked') return 'multi-linked'
        if (meal.source === 'verified') return 'verified'
        if (meal.source === 'defer_source' || meal.source === 'delayed') return 'delayed'
        return 'initial'
      }
      const slotLabel = (meal, short) => {
        if (!meal || !meal.active) return ''
        const batch = meal.verify_batch
        if (meal.source === 'multi_anchor' && batch && Number(batch.total) > 1) {
          return `${short}×${batch.total}`
        }
        return short
      }
      const lunchClass = slotClass(lunch)
      const dinnerClass = slotClass(dinner)
      const any = !!(lunch.active || dinner.active || hasQueueTags)
      if (!any && !day.paused && day.is_rest) cls += ' is-rest-day seq-non-business'
      else if (!any && !day.paused) cls += ' empty'
      else if (hasQueueTags) {
        if (queueTags.some((t) => t.kind === 'delay_source')) cls += ' has-delayed'
        else if (queueTags.some((t) => t.kind === 'checked')) cls += ' has-verified'
        else if (queueTags.some((t) => t.kind === 'delayed')) cls += ' has-delayed'
        else cls += ' has-initial'
      } else if (lunchClass === 'multi-anchor' || dinnerClass === 'multi-anchor') cls += ' has-multi-verify'
      else if (lunchClass === 'multi-linked' || dinnerClass === 'multi-linked') cls += ' has-multi-linked'
      else if (lunchClass === 'verified' || dinnerClass === 'verified') cls += ' has-verified'
      else if (lunchClass === 'delayed' || dinnerClass === 'delayed') cls += ' has-delayed'
      else if (lunchClass === 'initial' || dinnerClass === 'initial') cls += ' has-initial'

      return {
        key: (day.date || 'x') + '-' + idx,
        day,
        dayNum: day.date ? Number(String(day.date).slice(8, 10)) : '',
        showLunch,
        showDinner,
        lunchClass,
        dinnerClass,
        lunchLabel: slotLabel(lunch, '午'),
        dinnerLabel: slotLabel(dinner, '晚'),
        queueMode,
        lunchTags,
        dinnerTags,
        queueTags,
        hasQueueTags,
        className: cls
      }
    },
    onSubCalendarTabClick() {
      if (this.subCalendarTab === 'ops') this.loadMealOps()
      if (this.subCalendarTab === 'queue') this.syncMealQueueFromCalendarData(true)
    },
    syncMealQueueFromCalendarData(ensureLoaded) {
      const cal = this.subCalendarData
      if (cal && Array.isArray(cal.meal_queue)) {
        this.subCalendarMealQueueList = this.filterMealQueueListWithinPackage(cal.meal_queue)
        return true
      }
      if (ensureLoaded) {
        return this.loadSubscriptionCalendar()
      }
      return false
    },
    async loadMealScheduleDetail() {
      if (this.syncMealQueueFromCalendarData(false)) return
      await this.loadSubscriptionCalendar()
    },
    mealOpTypeLabel(t) {
      const map = {
        check: '核销',
        delay: '延期',
        undo_check: '撤销核销',
        undo_delay: '撤销延期',
        regenerate: '重新排餐',
        undo_last: '撤销上一步',
        generate: '生成排餐',
        migrate: '迁移'
      }
      return map[t] || t || '—'
    },
    mealOpTypeTagType(t) {
      const map = {
        check: 'success',
        delay: 'warning',
        undo_check: 'info',
        undo_delay: 'danger',
        regenerate: 'warning',
        undo_last: 'info',
        generate: '',
        migrate: 'info'
      }
      return map[t] || 'info'
    },
    mealQueueStatusLabel(row) {
      if (!row) return '—'
      if (row.status === 'checked') return '已核销'
      if (row.is_delay_source || row.status === 'delayed') return '已延期'
      if (row.status === 'cancelled') return '已取消'
      const postponeCount = Math.floor(Number(row.postpone_count) || 0)
      if (postponeCount > 0) return '待核销（曾延期）'
      return '待核销'
    },
    formatMealQueueDate(val) {
      if (val == null || val === '') return '—'
      const s = String(val).trim()
      if (!s) return '—'
      const m = s.match(/^(\d{4}-\d{2}-\d{2})/)
      if (m) return m[1]
      if (this.$formatDate) return this.$formatDate(val)
      return s.slice(0, 10)
    },
    formatMealQueueSlot(row, kind) {
      if (!row) return '—'
      const date = kind === 'original' ? row.original_date : row.current_date
      const slot =
        kind === 'original' ? row.original_meal_type_label : row.current_meal_type_label
      const dateText = this.formatMealQueueDate(date)
      if (dateText === '—' && !slot) return '—'
      if (dateText === '—') return slot || '—'
      return slot ? `${dateText} ${slot}` : dateText
    },
    queueTagTooltip(tag, cellTags) {
      if (!tag) return ''
      const parts = []
      if (tag.skip_ahead_title) parts.push(tag.skip_ahead_title)
      const siblings = Array.isArray(cellTags) ? cellTags : []
      const hasCheckedSibling = siblings.some((t) => t && t.kind === 'checked' && t.id !== tag.id)
      const hasPendingSibling = siblings.some(
        (t) => t && t.id !== tag.id && t.kind !== 'checked' && t.status !== 'cancelled'
      )
      if (tag.kind === 'checked' || tag.status === 'checked') {
        parts.push('已核销 · 历史配送（只读，不可延期/核销）')
      } else if (tag.kind === 'delay_source' || tag.is_delay_source) {
        parts.push('延期源 · 不可再次延期 · 可撤销延期')
        if (hasCheckedSibling) parts.push('与已核销同格并列显示')
      } else if (tag.kind === 'delayed' || tag.status === 'delayed') {
        parts.push('已延期 · 不可延期')
        if (hasCheckedSibling) parts.push('与已核销同格并列显示')
      } else if (this.isQueueTagDeferrable(tag)) {
        parts.push('待核销 · 可延期/核销')
        if (hasCheckedSibling) parts.push('与已核销同格并列显示')
      } else {
        parts.push('当前餐次已非待核销状态，不可延期')
      }
      if (hasPendingSibling && this.isQueueTagDeferrable(tag)) {
        parts.push('与同格其他未核销餐并列显示')
      }
      return parts.filter(Boolean).join(' · ')
    },
    isQueueTagDeferrable(tag) {
      if (!tag) return false
      if (this.isQueueTagDeferred(tag)) return false
      if (tag.status === 'checked' || tag.status === 'cancelled' || tag.kind === 'checked') return false
      return tag.status === 'pending' && tag.kind === 'pending'
    },
    isQueueTagDeferred(tag) {
      if (!tag) return false
      const st = String(tag.status || '').trim().toLowerCase()
      if (st === 'delayed' || st === 'deferred') return true
      if (tag.is_delay_source) return true
      if (tag.kind === 'delayed' || tag.kind === 'delay_source') return true
      return false
    },
    showDeferredMealClickHint() {
      this.$message({
        type: 'warning',
        message: '该餐次已延期，不可重复操作',
        duration: 2500,
        showClose: false
      })
    },
    queueTagInteractionClass(tag) {
      if (this.isQueueTagDeferrable(tag)) return 'is-deferrable'
      if (tag.kind === 'checked' || tag.status === 'checked') return 'is-readonly'
      return 'is-defer-disabled'
    },
    cloneSubCalendarSnapshot(data) {
      if (!data) return null
      try {
        return JSON.parse(JSON.stringify(data))
      } catch (_e) {
        return null
      }
    },
    isApiSuccess(resp) {
      return !!(resp && Number(resp.code) === 200)
    },
    extractApiErrorMessage(e, resp, fallback = '操作失败') {
      if (resp && resp.message && (resp.success === false || Number(resp.code) !== 200)) {
        return String(resp.message)
      }
      if (e && e.apiBody) {
        const d = e.apiBody
        if (d.message) return String(d.message)
        if (d.error) return String(d.error)
      }
      if (e && e.response && e.response.data) {
        const d = e.response.data
        if (typeof d === 'string' && d.trim()) return d.trim()
        if (d && d.message) return String(d.message)
        if (d && d.error) return String(d.error)
      }
      if (e && e._networkHint) return e._networkHint
      if (e && e.message) return e.message
      return fallback
    },
    showDeferFailureToast(reason) {
      const raw = String(reason || '未知原因').trim()
      const msg = raw.replace(/^延期失败[：:]\s*/, '')
      this.$message({
        type: 'error',
        message: `延期失败：${msg}`,
        duration: 6000,
        showClose: true
      })
    },
    async reloadAfterDeferSuccess(after) {
      // 保持当前浏览月，避免延期后日历跳到落点月造成「乱跳」
      await this.loadSubscriptionCalendar()
      if (this.subCalendarTab === 'ops') await this.loadMealOps()
      await this.getOrders()
      const afterDate = after && after.date ? String(after.date).slice(0, 10) : ''
      if (afterDate) {
        this.subCalendarDeferActionHint = `已延期至 ${afterDate}${after.period === 'dinner' || after.meal_type === 'dinner' ? ' 晚餐' : after.period === 'lunch' || after.meal_type === 'lunch' ? ' 午餐' : ''}`
      }
    },
    resetSubCalendarActionHints() {
      this.subCalendarDeferActionHint = ''
      this.subCalendarRegenerateSummary = ''
    },
    rollbackSubCalendarSnapshot(calendarSnapshot, queueSnapshot) {
      if (calendarSnapshot) this.subCalendarData = calendarSnapshot
      if (queueSnapshot) this.subCalendarMealQueueList = queueSnapshot
    },
    async loadMealOps() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      this.mealOpsLoading = true
      try {
        const params = { _t: Date.now() }
        if (this.mealOpsFilter) params.operation_type = this.mealOpsFilter
        const oid =
          this.subCalendarOrder &&
          (this.subCalendarOrder._id != null ? this.subCalendarOrder._id : this.subCalendarOrder.id)
        if (oid != null && String(oid).trim() !== '') {
          params.order_id = String(oid)
        }
        const resp = await this.$api.get(`/admin/subscriptions/${sid}/meal-ops`, { params })
        if (resp && resp.code === 200) {
          this.mealOpsList = (resp.data && resp.data.list) || []
        } else {
          this.mealOpsList = []
          this.$message.error((resp && resp.message) || '加载失败，请刷新重试')
        }
      } catch (e) {
        this.mealOpsList = []
        this.$message.error(
          (e.response && e.response.data && e.response.data.message) ||
            e._networkHint ||
            '加载失败，请刷新重试'
        )
      } finally {
        this.mealOpsLoading = false
      }
    },
    async undoMealQueueCheck(row) {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid || !row) return
      try {
        await this.$confirm('确认撤销该餐次核销？撤销后恢复为待核销状态', '撤销核销', { type: 'warning' })
      } catch (_e) {
        return
      }
      try {
        const resp = await this.$api.post('/admin/meal-queue/undo-check', {
          subscription_id: sid,
          meal_id: row.id
        })
        if (resp && resp.code === 200) {
          const noOp = resp.data && resp.data.no_op
          const msg = (resp && resp.message) || '已撤销核销'
          if (noOp) this.$message.info(msg)
          else this.$message.success(msg)
          await this.loadSubscriptionCalendar()
          if (this.subCalendarTab === 'ops') await this.loadMealOps()
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '撤销失败')
        }
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || e.message || '撤销失败')
      }
    },
    async undoMealQueueDelay(row) {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid || !row) return
      try {
        await this.$confirm(
          '确认撤销该餐次延期？撤销后该餐次及后续受影响餐次将恢复原位',
          '撤销延期',
          { type: 'warning' }
        )
      } catch (_e) {
        return
      }
      try {
        const resp = await this.$api.post('/admin/meal-queue/undo-delay', {
          subscription_id: sid,
          meal_id: row.id,
          meal_index: row.meal_index
        })
        if (this.isApiSuccess(resp)) {
          this.$message.success('已撤销延期')
          await this.loadSubscriptionCalendar()
          if (this.subCalendarTab === 'ops') await this.loadMealOps()
        } else {
          this.$message.error(this.extractApiErrorMessage(null, resp, '撤销失败'))
        }
      } catch (e) {
        this.$message.error(this.extractApiErrorMessage(e, null, '撤销失败'))
      }
    },
    mealQueueRowSelectable(row) {
      if (!row) return false
      if (row.status === 'checked' || row.status === 'cancelled') return false
      if (row.is_delay_source || row.status === 'delayed') return false
      return true
    },
    mealQueueStatusTagType(row) {
      if (!row) return 'info'
      if (row.status === 'checked') return 'info'
      if (row.is_delay_source || row.status === 'delayed') return 'warning'
      return 'success'
    },
    onMealQueueSelectionChange(rows) {
      this.subCalendarQueueSelection = rows || []
    },
    async confirmRegenerateSubscriptionMeals() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      const mealConfig = String(this.regenerateMealForm.meal_config || '').trim()
      if (!mealConfig || !['only_lunch', 'only_dinner', 'both'].includes(mealConfig)) {
        this.$message.warning('请选择餐次')
        return
      }

      let delayCount = 0
      try {
        const order = this.subCalendarOrder
        const oid = order && (order._id != null ? order._id : order.id)
        const params = {}
        if (oid != null && String(oid).trim() !== '') params.order_id = String(oid)
        const dcResp = await this.$api.get(`/admin/subscriptions/${sid}/meal-delay-count`, { params })
        if (dcResp && dcResp.data) {
          delayCount = Math.max(0, Number(dcResp.data.delay_count) || 0)
        }
      } catch (_e) {
        delayCount = 0
      }

      const details = []
      if (delayCount > 0) {
        details.push({
          label: '延期警告',
          value: `检测到该订单已有 ${delayCount} 次延期操作，重新排餐后延期与核销记录将被全部清空，请谨慎操作`,
          danger: true
        })
      }

      try {
        await appConfirm({
          title: '确认重新排餐？',
          message:
            '将按订餐周期起始日全部重新排餐：已核销与延期都会清空，全部餐次重新生成。确定继续吗？',
          type: 'danger',
          confirmText: '确认重新排餐',
          cancelText: '取消',
          details
        })
      } catch (_e) {
        return
      }

      this.subCalendarRegenerating = true
      try {
        const packageTotal = this.resolveSubCalendarPackageTotal(this.subCalendarOrder)
        // eslint-disable-next-line no-console
        console.log('[regenerate] request target_total_meals =', packageTotal)
        const resp = await this.$api.post('/admin/meal-schedules/regenerate', {
          subscription_id: sid,
          meal_config: mealConfig,
          force_meal_config: true,
          target_total_meals: packageTotal > 0 ? packageTotal : undefined,
          order_id:
            this.subCalendarOrder &&
            (this.subCalendarOrder._id != null
              ? this.subCalendarOrder._id
              : this.subCalendarOrder.id)
        })
        // eslint-disable-next-line no-console
        console.log('[regenerate] response.data =', resp && resp.data)
        const respMeals = resp && resp.data && resp.data.meals
        // eslint-disable-next-line no-console
        console.log(
          '[regenerate] meals.length =',
          Array.isArray(respMeals) ? respMeals.length : '(no meals array)',
          '| stats =',
          resp && resp.data && resp.data.stats,
          '| regenerate_summary =',
          resp && resp.data && resp.data.regenerate_summary
        )
        if (this.isApiSuccess(resp)) {
          this.resetSubCalendarActionHints()
          const summary =
            (resp.data && resp.data.message) ||
            resp.message ||
            '重新排餐成功'
          this.$message.success(summary)
          this.regenerateMealDialogVisible = false
          if (this.subCalendarOrder) {
            this.$set(this.subCalendarOrder, 'meal_config', mealConfig)
            this.$set(this.subCalendarOrder, 'meal_slot', this.mealConfigToSlots(mealConfig).join('、'))
            const st = resp.data && resp.data.stats
            if (st && typeof st === 'object') {
              if (st.pending != null) {
                this.$set(this.subCalendarOrder, 'remaining_meals', Math.max(0, Number(st.pending) || 0))
              }
              if (st.checked != null) {
                this.$set(this.subCalendarOrder, 'used_meals', Math.max(0, Number(st.checked) || 0))
              }
              if (st.total != null) {
                this.$set(
                  this.subCalendarOrder,
                  'target_total_meals',
                  Math.max(0, Number(st.total) || 0)
                )
              }
            }
          }
          this.subCalendarData = null
          this.subCalendarMealQueueList = []
          await this.loadSubscriptionCalendar()
          if (this.subCalendarTab === 'ops') await this.loadMealOps()
          this.getOrders()
        } else {
          this.$message.error(this.extractApiErrorMessage(null, resp, '重新排餐失败'))
        }
      } catch (e) {
        this.$message.error(this.extractApiErrorMessage(e, null, '重新排餐失败'))
      } finally {
        this.subCalendarRegenerating = false
      }
    },
    async undoLastMealQueueOp() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      try {
        await appConfirm({
          title: '撤销上一步？',
          message: '将根据操作日志中的快照，恢复上一步操作之前的餐次排布。',
          type: 'warning',
          confirmText: '确认撤销'
        })
      } catch (_e) {
        return
      }
      this.subCalendarUndoLastLoading = true
      try {
        const order = this.subCalendarOrder
        const oid = order && (order._id != null ? order._id : order.id)
        const resp = await this.$api.post('/admin/meal-queue/undo-last', {
          subscription_id: sid,
          order_id: oid != null ? String(oid) : null
        })
        if (resp && resp.code === 200) {
          this.$message.success((resp.data && resp.data.message) || resp.message || '已撤销上一步')
          await this.loadSubscriptionCalendar()
          if (this.subCalendarTab === 'ops') await this.loadMealOps()
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '撤销失败')
        }
      } catch (e) {
        this.$message.error(
          (e.response && e.response.data && e.response.data.message) || e.message || '撤销失败'
        )
      } finally {
        this.subCalendarUndoLastLoading = false
      }
    },
    async batchVerifyMealQueue() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      const ids = (this.subCalendarQueueSelection || []).map((r) => r.id)
      if (!sid || !ids.length) return
      this.mealQueueVerifySubmitting = true
      try {
        const resp = await this.$api.post('/admin/meal-queue/verify', {
          subscription_id: sid,
          meal_ids: ids
        })
        if (resp && resp.code === 200) {
          this.$message.success(`已核销 ${ids.length} 餐`)
          await this.loadSubscriptionCalendar()
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '核销失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || e.message || '核销失败')
      } finally {
        this.mealQueueVerifySubmitting = false
      }
    },
    async verifyMealQueueRow(row) {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid || !row) return
      try {
        const resp = await this.$api.post('/admin/meal-queue/verify', {
          subscription_id: sid,
          meal_ids: [row.id]
        })
        if (resp && resp.code === 200) {
          this.$message.success('已核销')
          await this.loadSubscriptionCalendar()
        } else {
          this.$message.error((resp && resp.message) || '核销失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || e.message || '核销失败')
      }
    },
    async deferMealQueueRow(row) {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid || !row) return
      if (!this.mealQueueRowSelectable(row)) {
        this.$message.warning('当前餐次已非待核销状态，不可延期')
        return
      }
      if (this.mealQueueDeferSubmitting) return
      const curType = String(row.current_meal_type || 'lunch').toLowerCase()
      this.deferMealForm = {
        meal_id: row.id,
        meal_index: row.meal_index,
        current_date: row.current_date,
        current_meal_type: curType === 'dinner' ? 'dinner' : 'lunch',
        targetDate: '',
        targetPeriod: curType === 'dinner' ? 'dinner' : 'lunch',
        autoSlot: false,
        reason: ''
      }
      this.deferMealDialogVisible = true
    },
    makeDeferIdempotencyKey() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
      return `defer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    },
    async confirmDeferMealQueue() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      const form = this.deferMealForm || {}
      if (!sid || !form.meal_id) return
      if (!form.autoSlot && !form.targetDate) {
        this.$message.warning('请选择延期目标日期，或勾选自动顺延')
        return
      }
      if (this.mealQueueDeferSubmitting) return
      this.mealQueueDeferSubmitting = true
      try {
        const order = this.subCalendarOrder
        const oid = order && (order._id != null ? order._id : order.id)
        const payload = {
          subscription_id: sid,
          meal_schedule_id: form.meal_id,
          meal_id: form.meal_id,
          meal_index: form.meal_index,
          order_id: oid != null ? String(oid) : null,
          autoSlot: !!form.autoSlot,
          reason: form.reason || null,
          idempotencyKey: this.makeDeferIdempotencyKey()
        }
        if (!form.autoSlot) {
          payload.targetDate = form.targetDate
          payload.targetPeriod = form.targetPeriod || form.current_meal_type
        }
        const resp = await this.$api.post('/admin/meal-queue/defer', payload)
        const check = validateDeferSuccessResponse(resp)
        if (check.ok) {
          const data = check.data || {}
          const after = data.after || {}
          const afterDate = after.date ? String(after.date).slice(0, 10) : ''
          const afterPeriod = after.period === 'DINNER' || after.period === 'dinner' ? '晚餐' : after.period === 'LUNCH' || after.period === 'lunch' ? '午餐' : ''
          let msg = '延期成功'
          if (afterDate) {
            msg = `第${data.mealNo || form.meal_index}餐已延期至 ${afterDate}${afterPeriod ? ' ' + afterPeriod : ''}`
          }
          this.subCalendarDeferActionHint = msg
          this.$message.success(msg)
          this.deferMealDialogVisible = false
          await this.reloadAfterDeferSuccess(after)
        } else {
          this.showDeferFailureToast(check.message || extractDeferErrorMessage(null, resp, '未知原因'))
          await this.loadSubscriptionCalendar()
        }
      } catch (e) {
        this.showDeferFailureToast(extractDeferErrorMessage(e, null, '网络或服务器错误'))
        await this.loadSubscriptionCalendar()
      } finally {
        this.mealQueueDeferSubmitting = false
      }
    },
    async onQueueTagClick(tag) {
      if (!tag) return
      if (this.isQueueTagDeferred(tag)) {
        this.showDeferredMealClickHint()
        return
      }
      if (this.mealQueueDeferSubmitting) {
        this.$message.warning('正在处理延期，请稍候')
        return
      }
      if (tag.status === 'checked' || tag.status === 'cancelled' || tag.kind === 'checked') {
        this.$message.info(`第${tag.meal_index}餐已核销（历史配送，只读），不可延期或核销`)
        return
      }
      if (!this.isQueueTagDeferrable(tag)) {
        this.$message.warning('当前餐次已非待核销状态，不可延期')
        return
      }
      try {
        await this.$confirm('选择操作', '餐次操作', {
          distinguishCancelAndClose: true,
          confirmButtonText: '核销',
          cancelButtonText: '延期',
          type: 'info'
        })
        await this.verifyMealQueueRow(tag)
      } catch (action) {
        if (action === 'cancel') {
          await this.deferMealQueueRow(tag)
        }
      }
    },
    openSubscriptionCalendar(order) {
      this.subCalendarOrder = order
      this.subCalendarDayDetail = null
      this.subCalendarData = null
      this.subCalendarTab = 'calendar'
      this.subCalendarQueueSelection = []
      this.resetSubCalendarActionHints()
      const now = new Date()
      let y = now.getFullYear()
      let m = now.getMonth() + 1
      const period = String(order.subscription_period || '')
      const periodMatch = period.match(/(\d{4}-\d{2}-\d{2})/)
      const startStr =
        order.subscription_start ||
        (periodMatch ? periodMatch[1] : '') ||
        ''
      if (startStr) {
        const parts = startStr.split('-').map(Number)
        if (parts.length >= 2 && parts[0] > 2000) {
          y = parts[0]
          m = parts[1]
        }
      }
      this.subCalendarYear = y
      this.subCalendarMonth = m
      this.subCalendarView = 'month'
      const ws = new Date(y, m - 1, 1)
      const p = (n) => String(n).padStart(2, '0')
      this.subCalendarWeekStart = `${ws.getFullYear()}-${p(ws.getMonth() + 1)}-${p(ws.getDate())}`
      this.subCalendarVisible = true
      this.subCalendarMealQueueList = []
      this.subCalendarTab = 'calendar'
    },
    onSubCalendarOpened() {
      this.loadSubscriptionCalendar()
    },
    async loadSubscriptionCalendar() {
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      this.subCalendarLoading = true
      if (this.subCalendarTab === 'queue') this.subCalendarMealQueueLoading = true
      try {
        const params = { view: this.subCalendarView, _t: Date.now() }
        if (this.subCalendarView === 'month') {
          params.year = this.subCalendarYear
          params.month = this.subCalendarMonth
        } else {
          params.weekStart = this.subCalendarWeekStart
        }
        const resp = await this.$api.get(`/admin/subscriptions/${sid}/calendar`, { params })
        if (resp && resp.code === 200 && resp.data) {
          this.subCalendarData = resp.data
          const raw = Array.isArray(resp.data.meal_queue) ? resp.data.meal_queue : []
          this.subCalendarMealQueueList = this.filterMealQueueListWithinPackage(raw)
        } else {
          this.$message.error((resp && resp.message) || '加载日历失败')
        }
      } catch (e) {
        this.$message.error(e._networkHint || '加载日历失败')
      } finally {
        this.subCalendarLoading = false
        this.subCalendarMealQueueLoading = false
      }
    },
    subCalendarPrev() {
      if (this.subCalendarView === 'month') {
        if (this.subCalendarMonth <= 1) {
          this.subCalendarYear -= 1
          this.subCalendarMonth = 12
        } else {
          this.subCalendarMonth -= 1
        }
      } else {
        this.subCalendarWeekStart = this.addCalendarDaysLocal(this.subCalendarWeekStart, -7)
      }
      this.loadSubscriptionCalendar()
    },
    subCalendarNext() {
      if (this.subCalendarView === 'month') {
        if (this.subCalendarMonth >= 12) {
          this.subCalendarYear += 1
          this.subCalendarMonth = 1
        } else {
          this.subCalendarMonth += 1
        }
      } else {
        this.subCalendarWeekStart = this.addCalendarDaysLocal(this.subCalendarWeekStart, 7)
      }
      this.loadSubscriptionCalendar()
    },
    onSubCalendarPickDay(day) {
      if (!day || !day.date) return
      this.subCalendarDayDetail = day
    },
    async restoreCalendarVerify(vr) {
      if (!vr) return
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      const batchTotal = Number(vr.batch_total) || 1
      const msg =
        batchTotal > 1
          ? `确定撤销该次配送核销？将恢复 ${batchTotal} 餐次到「未用餐」。`
          : '确定撤销该餐次核销？核销次数将恢复。'
      try {
        await appConfirm({
          title: '撤销核销',
          message: msg,
          type: 'warning',
          confirmText: '确定撤销'
        })
      } catch (e) {
        return
      }
      try {
        const payload = { subscription_id: sid }
        if (vr.batch_id) payload.batch_id = vr.batch_id
        else if (vr.schedule_id) payload.schedule_id = vr.schedule_id
        else {
          this.$message.error('无法识别核销记录')
          return
        }
        const resp = await this.$api.post('/admin/meal-schedules/restore', payload)
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '已撤销核销')
          await this.loadSubscriptionCalendar()
          if (this.subCalendarDayDetail && this.subCalendarDayDetail.date) {
            const day = (this.subCalendarData && this.subCalendarData.days || []).find(
              (d) => d.date === this.subCalendarDayDetail.date
            )
            this.subCalendarDayDetail = day || null
          }
          this.getOrders()
        } else {
          this.$message.error((resp && resp.message) || '撤销失败')
        }
      } catch (e) {
        const msg2 =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '撤销失败'
        this.$message.error(msg2)
      }
    },
    async deleteSubscriptionDelayRow(d) {
      if (!d || !d.id) return
      const sid = this.getSubscriptionIdFromOrder(this.subCalendarOrder)
      if (!sid) return
      await this.performDeleteDeferral(d, sid, {
        onSuccess: async () => {
          await this.loadSubscriptionCalendar()
          this.subCalendarDayDetail = null
          this.getOrders()
        }
      })
    },
    async deleteDelayRecordItem(row) {
      if (!row || !row.id) return
      const order = this.delayRecordOrder
      const sid = this.getSubscriptionIdFromOrder(order)
      if (!sid) return
      await this.performDeleteDeferral(row, sid, {
        onSuccess: async () => {
          await this.loadDelayRecordPage(this.delayRecordPagination.page)
          this.getOrders()
        }
      })
    },
    async performDeleteDeferral(d, sid, { onSuccess } = {}) {
      try {
        await appConfirm({
          title: '删除延期记录',
          message: '确定删除该条延期记录？排餐与周期结束日将自动重算（需从最近一条开始删）。',
          type: 'warning',
          confirmText: '确定删除'
        })
      } catch (e) {
        return
      }
      try {
        let resp
        if (d.legacy) {
          resp = await this.$api.delete(`/orders/subscription/${sid}/delay/${d.id}`)
        } else {
          resp = await this.$api.delete(`/admin/meal-schedules/deferrals/${d.id}`)
        }
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '已删除')
          if (onSuccess) await onSuccess()
        } else {
          this.$message.error((resp && resp.message) || '删除失败')
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          '删除失败'
        this.$message.error(msg)
      }
    },
    getNextAvailableWorkdayStr (startStr) {
      const s = String(startStr || '').slice(0, 10)
      const parts = s.split('-').map(Number)
      if (parts.length !== 3) return ''
      const d = new Date(parts[0], parts[1] - 1, parts[2])
      if (Number.isNaN(d.getTime())) return ''
      while (true) {
        try {
          if (!isRestDay(d)) break
          d.setDate(d.getDate() + 1)
        } catch (e) {
          break
        }
      }
      return this.formatDatePickerVal(d)
    },
    // 新日期允许手动选择；这里只在空值或非法时兜底填充
    ensureExtendNewDate() {
      if (!this.extendForm.original_date || !this.extendTargetOrder) {
        return
      }
      const minStr = this.getExtendMinNewDateStr()
      if (!minStr) {
        return
      }
      const fallbackDate = this.getNextAvailableWorkdayStr(minStr)
      if (!fallbackDate) return

      const current = this.formatDatePickerVal(this.extendForm.new_date)
      if (!current) {
        this.extendForm.new_date = fallbackDate
        return
      }
      if (current < minStr) {
        this.extendForm.new_date = fallbackDate
        return
      }
      try {
        if (isRestDay(new Date(current))) {
          this.extendForm.new_date = fallbackDate
        }
      } catch (_e) {
        this.extendForm.new_date = fallbackDate
      }
    },
    formatDatePickerVal(d) {
      if (d == null || d === '') return ''
      if (typeof d === 'string') return d.slice(0, 10)
      const x = d instanceof Date ? d : new Date(d)
      if (Number.isNaN(x.getTime())) return ''
      const p = (n) => String(n).padStart(2, '0')
      return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`
    },
    getChinaTodayStr() {
      try {
        return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai' }).format(new Date())
      } catch (_e) {
        return this.formatDatePickerVal(new Date())
      }
    },
    formatMealSlotWithWindow(mealSlot) {
      const s = String(mealSlot || '').trim()
      if (!s || s === '-') return s || '-'
      const map = {
        lunch: '午餐：10:30-12:30',
        午餐: '午餐：10:30-12:30',
        dinner: '晚餐：16:00-17:30',
        晚餐: '晚餐：16:00-17:30'
      }
      if (map[s]) return map[s]
      if (/[+、,/，]/.test(s)) {
        return s
          .split(/[+、,/，]+/)
          .map((x) => map[String(x).trim()] || String(x).trim())
          .filter(Boolean)
          .join(' / ')
      }
      return s
    },
    formatOrderMealSlotDisplay(order) {
      const cfg = this.resolveOrderMealConfigFromRow(order)
      if (cfg === 'only_lunch' || cfg === 'only_dinner' || cfg === 'both') {
        return this.formatMealSlotWithWindow(this.mealConfigToSlots(cfg).join('、'))
      }
      return this.formatMealSlotWithWindow((order && order.meal_slot) || '午餐')
    },
    async submitExtend() {
      if (!this.extendForm.original_date || !this.extendForm.new_date) {
        this.$message.error('请选择原计划日期和新日期');
        return;
      }
      const range = this.getExtendSubscriptionRange()
      if (!range) {
        this.$message.error('当前订单缺少订餐周期，无法计算延期后的区间');
        return;
      }
      const ori = this.formatDatePickerVal(this.extendForm.original_date)
      const neu = this.formatDatePickerVal(this.extendForm.new_date)
      const startStr = this.formatDatePickerVal(range.start)
      const newPeriodText = `${startStr} 至 ${neu}`
      const mealMap = { breakfast: '早餐', lunch: '午餐', dinner: '晚餐' };
      const mealLabel = mealMap[this.extendForm.meal_type] || '午餐';
      
      // 前端校验：新日期必须大于当前“餐次”可延期上限
      const periodStartStr = this.formatDatePickerVal(range.start)
      const periodEndStr = this.formatDatePickerVal(range.end)
      if (new Date(ori) < new Date(periodStartStr) || new Date(ori) > new Date(periodEndStr)) {
        this.$message.error('原计划日期必须在当前订餐周期内')
        return
      }
      const minNewStr = this.getExtendMinNewDateStr()
      if (minNewStr && neu < minNewStr) {
        this.$message.error(`新日期须不早于当前周期结束日的次日（${minNewStr}）`)
        return
      }
      
      try {
        const subRaw = this.extendTargetOrder && this.extendTargetOrder.subscription_id
        const subId =
          subRaw && typeof subRaw === 'object' ? subRaw.id || subRaw._id : subRaw
        if (subId != null && subId !== '') {
          const also =
            this.extendForm.also_dinner &&
            this.extendShowAlsoDinnerOption &&
            mealLabel === '午餐'
          let resp
          if (also) {
            resp = await this.$api.post(`/orders/subscription/${subId}/extend-batch`, {
              items: [
                { original_date: ori, new_date: neu, meal_type: '午餐' },
                { original_date: ori, new_date: neu, meal_type: '晚餐' }
              ]
            })
          } else {
            resp = await this.$api.put(`/orders/subscription/${subId}/extend`, {
              original_date: ori,
              new_date: neu,
              meal_type: mealLabel
            })
          }
          if (resp && resp.code === 200) {
            const updated = resp.data || {}
            const nextEnd =
              updated.end_date != null && String(updated.end_date).trim() !== ''
                ? String(updated.end_date).slice(0, 10)
                : neu
            const nextPeriodText = `${startStr} 至 ${nextEnd}`
            if (this.extendTargetOrder) {
              this.extendTargetOrder.subscription_period = nextPeriodText
              if (this.extendTargetOrder.subscription_id && typeof this.extendTargetOrder.subscription_id === 'object') {
                this.extendTargetOrder.subscription_id.end_date = nextEnd
                if (Array.isArray(updated.delay_log)) {
                  this.extendTargetOrder.subscription_id.delay_log = updated.delay_log
                }
              }
            }
            this.$message.success(
              `订单 ${this.extendTargetOrder.order_no} 已将 ${ori} 的${mealLabel}${
                also ? '与晚餐' : ''
              } 延期至 ${neu}（已写入套餐延期记录）`
            )
            this.extendDialogVisible = false
            this.legacyExtendDialogVisible = false
            this.getOrders()
          } else {
            this.$message.error((resp && resp.message) || '延期失败')
          }
        } else {
          // 未关联随心订计划（例如后台新建的线下订单）：将延期信息追加到发货备注中，方便人工对账
          const orderId = this.extendTargetOrder && (this.extendTargetOrder._id || this.extendTargetOrder.id)
          if (orderId == null || orderId === '') {
            this.$message.error('无法记录延期：缺少订单 ID')
            return
          }
          const existedNote = (this.extendTargetOrder.order_note || '').trim()
          const delaySnippet = `[延期] ${ori} 的${mealLabel} → ${neu}`
          const mergedNote = existedNote ? `${existedNote}\n${delaySnippet}` : delaySnippet
          const resp = await this.$api.put(`/admin/orders/${orderId}/note`, {
            order_note: mergedNote,
            // 同步更新 remark/订餐周期，便于列表与小票展示最新的「开始日 至 最终结束日」
            subscription_period: newPeriodText
          })
          if (resp && resp.code === 200) {
            if (this.extendTargetOrder) {
              this.extendTargetOrder.subscription_period = newPeriodText
              this.extendTargetOrder.order_note = mergedNote
            }
            this.$message.success(
              `订单 ${this.extendTargetOrder.order_no} 已延期至 ${neu}，并记录在发货备注中`
            )
            this.extendDialogVisible = false
            this.legacyExtendDialogVisible = false
            this.getOrders()
          } else {
            this.$message.error((resp && resp.message) || '延期失败')
          }
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e._networkHint ||
          '延期失败'
        this.$message.error(msg)
      }
    },
    disabledExtendDate (time) {
      let d = time
      if (time && typeof time === 'object' && time.date) d = time.date
      if (!(d instanceof Date)) return false
      try {
        if (isRestDay(d)) return true
      } catch (e) {}
      return false
    },
    extendCellClass (time) {
      let d = time
      if (time && typeof time === 'object' && time.date) d = time.date
      if (!(d instanceof Date)) return ''
      let cls = ''
      try {
        if (isRestDay(d)) {
          cls = 'rest-day-cell'
        }
      } catch (e) {}
      const range = this.getExtendSubscriptionRange()
      if (range && d >= range.start && d <= range.end) {
        cls = (cls ? cls + ' ' : '') + 'sub-period-cell'
      }
      return cls
    },
    getExtendSubscriptionRange () {
      if (!this.extendTargetOrder) return null
      // 优先从 subscription_id 对象中获取最新的开始和结束日期
      if (this.extendTargetOrder.subscription_id && typeof this.extendTargetOrder.subscription_id === 'object' && this.extendTargetOrder.subscription_id.start_date && this.extendTargetOrder.subscription_id.end_date) {
        const start = new Date(this.extendTargetOrder.subscription_id.start_date)
        const end = new Date(this.extendTargetOrder.subscription_id.end_date)
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          // 把结束时间设置到当天 23:59:59，方便比较
          end.setHours(23, 59, 59, 999)
          return { start, end }
        }
      }
      // 兼容旧格式：从 subscription_period 字符串中解析
      if (this.extendTargetOrder.subscription_period) {
        const parts = this.extendTargetOrder.subscription_period.split('至').map(p => p.trim())
        if (parts.length === 2) {
          const start = new Date(parts[0])
          const end = new Date(parts[1])
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            // 把结束时间设置到当天 23:59:59，方便比较
            end.setHours(23, 59, 59, 999)
            return { start, end }
          }
        }
      }
      return null
    },
    printReceipt(order) {
      const id = order._id != null ? order._id : order.id
      if (id == null || id === '') {
        this.$message.error('订单 ID 无效，无法打印')
        return
      }
      const token = getAdminToken() || ''
      if (!token) {
        this.$message.error('登录已失效，请重新登录后再打印')
        return
      }
      const base = this.$api.defaults.baseURL || '/api'
      window.open(
        `${base}/admin/print/receipt-html/${encodeURIComponent(String(id))}?token=${encodeURIComponent(token)}`,
        '_blank'
      )
    },
    generateSituationOrderNo() {
      const digits = Array.from({ length: 11 }, () => Math.floor(Math.random() * 10)).join('')
      return 'Y' + digits
    },
    openCreateOrder() {
      this.orderDialogMode = 'create';
      this.currentOrderIndex = null;
      this.customerSearchQuery = '';
      this.customerPhoneLookupHint = '';
      this.customerPhoneBindHint = '';
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const dt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
      if (this.isPackageDelivery) {
        this.orderForm = {
          order_no: 'ORD' + now.getTime(),
          user_id: '',
          user_name: '',
          consignee_name: '',
          user_phone: '',
          address: '',
          product_name: '',
          productSelectKey: '',
          manual_target_total_meals: null,
          unit_price: 0,
          quantity: 1,
          total_price: 0,
          order_type: '随心订',
          order_status: '待发货',
          subscription_period: '',
          subscription_date_range: null,
          subscription_period_start: '',
          meal_slots: ['午餐'],
        meal_config: 'both',
          delivery_method: '同城配送',
          payment_method: 'mini_order',
          shipping_fee: 0,
        order_note: '',
        delivery_region: '',
        rider_id: '',
        rider_name: '',
        rider_phone: '',
        createdAt: dt,
          historical_order: false,
          historical_delivery_mode: 'past_date',
          historical_total_meals: 0,
          historical_meals_delivered: 0,
          plan_link_mode: 'new',
          link_subscription_id: ''
        };
      } else {
        this.orderForm = {
          order_no: this.generateSituationOrderNo(),
          user_id: '',
          user_name: '',
          consignee_name: '',
          user_phone: '',
          address: '',
          product_name: '',
          productSelectKey: '',
          manual_target_total_meals: null,
          unit_price: 0,
          quantity: 1,
          total_price: 0,
          order_type: '普通',
          order_status: '待支付',
          subscription_period: '',
          subscription_date_range: null,
          subscription_period_start: '',
          meal_slots: ['午餐'],
        meal_config: 'both',
          delivery_method: '同城配送',
          payment_method: 'wechat',
          shipping_fee: 0,
        order_note: '',
        delivery_region: '',
        rider_id: '',
        rider_name: '',
        rider_phone: '',
        createdAt: dt,
          historical_order: false,
          historical_delivery_mode: 'past_date',
          historical_total_meals: 0,
          historical_meals_delivered: 0,
          plan_link_mode: 'new',
          link_subscription_id: ''
        };
      }
      this.loadOrderProducts().then(() => {
        this.orderForm.productSelectKey = ''
        this.productMealsMode = 'both'
        this.productTotalMeals = 0
        this.subscriptionPeriodHint = ''
        this.subscriptionPeriodError = ''
        this.schedulePreviewData = null
        this.schedulePreviewPendingSubmit = false
      })
      this.loadCustomerUsers()
      this.orderDialogVisible = true;
    },
    async submitOrder() {
      await this.ensureCustomerAccountBound()
      this.orderForm.total_price = Number(this.orderForm.unit_price) * Number(this.orderForm.quantity) + Number(this.orderForm.shipping_fee || 0);
      if (!this.orderForm.user_name || !String(this.orderForm.product_name || '').trim()) {
        this.$message.error('请填写顾客姓名和商品名称');
        return;
      }
      if (
        this.orderDialogMode === 'create' &&
        this.isPackageDelivery &&
        String(this.orderForm.order_type || '') === '普通' &&
        !SITUATION_ORDER_NO_RE.test(String(this.orderForm.order_no || ''))
      ) {
        this.$message.error('当前商品为非随心订类，订单号须为 Y 开头 + 11 位数字（切换商品后会自动更新）')
        return
      }
      if (
        this.orderDialogMode === 'create' &&
        this.needsSubscriptionPeriodInDialog &&
        this.orderForm.plan_link_mode === 'link' &&
        !String(this.orderForm.link_subscription_id || '').trim()
      ) {
        this.$message.error('请填写要关联的随心订计划 ID，或改回「新建计划」')
        return
      }
      const needSubscriptionPeriod = this.needsSubscriptionPeriodInDialog
      if (needSubscriptionPeriod) {
        const ok = await this.validateSubscriptionForm(true)
        if (!ok) return
      }
      if (this.orderDialogMode === 'create' && needSubscriptionPeriod) {
        const data = this.schedulePreviewData || await this.refreshSubscriptionPeriodPreview()
        if (data && data.list && data.list.length) {
          this.schedulePreviewPendingSubmit = true
          this.schedulePreviewVisible = true
          return
        }
      }
      await this.doSubmitOrder()
    },
    async doSubmitOrder() {
      if (this.orderDialogMode === 'create') {
        const maxTry = 5
        let lastMsg = '保存失败'
        for (let attempt = 0; attempt < maxTry; attempt++) {
          if (attempt > 0 && !this.isPackageDelivery) {
            this.orderForm.order_no = this.generateSituationOrderNo()
          }
          try {
            const resp = await this.$api.post('/admin/orders', this.buildOrderSubmitPayload())
            if (resp && resp.code === 201) {
              this.$message.success('订单添加成功')
              this.orderDialogVisible = false
              if (resp.data) {
                this.upsertOrderInList(this.mapRawOrderToCard(resp.data))
              } else {
                this.getOrders()
              }
              return
            }
            if (resp && resp.code === 503) {
              this.$message.error((resp && resp.message) || '数据库未就绪，请在服务器执行 npm run db:sync')
              return
            }
            lastMsg = (resp && resp.message) || '保存失败'
            if (String(lastMsg).includes('订单号已存在') && attempt < maxTry - 1) {
              continue
            }
            this.$message.error(lastMsg)
            return
          } catch (e) {
            const msg =
              (e && e.response && e.response.data && e.response.data.message) ||
              '保存失败，请确认后端已启动且数据库可写'
            lastMsg = msg
            if (String(msg).includes('订单号已存在') && attempt < maxTry - 1) {
              continue
            }
            this.$message.error(msg)
            return
          }
        }
        this.$message.error(lastMsg)
        return
      }
      const id = this.orderForm._id || this.orderForm.id
      if (id == null || id === '') {
        this.$message.error('无法保存：缺少订单 ID')
        return
      }
      try {
        const statusForDb =
          this.orderForm.raw_order_status != null && this.orderForm.raw_order_status !== ''
            ? this.orderForm.raw_order_status
            : this.orderForm.order_status
        await this.resolveOrderFormDelivery()
        const payload = this.buildOrderSubmitPayload()
        const resp = await this.$api.put(`/admin/orders/${id}`, {
          ...payload,
          order_status: statusForDb,
          raw_order_status: statusForDb
        })
        if (resp && resp.code === 200) {
          this.$message.success('订单修改成功')
          this.orderDialogVisible = false
          if (resp.data) {
            this.upsertOrderInList(this.mapRawOrderToCard(resp.data))
          } else {
            try {
              const one = await this.$api.get(`/admin/orders/${id}`, { params: { _t: Date.now() } })
              if (one && one.code === 200 && one.data) {
                this.upsertOrderInList(this.mapRawOrderToCard(one.data))
              }
            } catch (e) {
              /* 单条刷新失败时仍拉全量 */
            }
          }
          if (this.orderDetailVisible) {
            const did = this.orderDetailCard && (this.orderDetailCard._id || this.orderDetailCard.id)
            if (did != null && String(did) === String(id)) {
              await this.fetchOrderDetail(id)
            }
          }
          await this.getOrders()
          return
        }
        this.$message.error((resp && resp.message) || '更新失败')
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          '更新失败，请确认后端已启动'
        this.$message.error(msg)
      }
    },
    exportOrders() {
      if (!this.orders.length) {
        this.$message.info('当前没有可导出的订单');
        return;
      }
      const header = ['订单编号','顾客','电话','地址','商品','单价','数量','实收金额','状态','下单时间'];
      const rows = this.orders.map(o => [
        o.order_no,
        o.user_name,
        o.user_phone,
        o.address,
        o.product_name,
        o.unit_price,
        o.quantity,
        o.total_price,
        o.order_status,
        o.createdAt
      ]);
      const csv = [header.join(','), ...rows.map(r => r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'orders.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.$message.success('导出成功，请查看浏览器下载记录');
    },
    viewExported() {
      this.$message.info('导出的订单文件会出现在浏览器的下载记录中。');
    },
    handleOrderImport(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const run = async () => {
          const text = e.target.result;
          const lines = text.split(/\r?\n/).filter(l => l.trim());
          if (lines.length <= 1) {
            this.$message.error('文件没有数据');
            return;
          }
          // 跳过首行表头，按导出格式：订单编号,顾客,电话,地址,商品,单价,数量,实收金额,状态,下单时间
          const imported = lines.slice(1).map((line) => {
            const cols = line.split(',').map(c => c.replace(/^"|"$/g, '').replace(/""/g, '"'));
            return {
              order_no: String(cols[0] || '').trim(),
              user_name: cols[1] || '',
              user_phone: cols[2] || '',
              address: cols[3] || '',
              product_name: cols[4] || '',
              unit_price: Number(cols[5] || 0),
              quantity: Number(cols[6] || 0),
              total_price: Number(cols[7] || 0),
              order_status: cols[8] || '',
              createdAt: cols[9] || ''
            };
          });
          // 先与数据库对齐：仅用文件更新「当前库里仍存在」的订单，已删除的订单号会被跳过，避免旧 CSV 把幽灵订单铺满列表
          await this.getOrders();
          const byNo = new Map();
          imported.forEach((row) => {
            if (row.order_no) byNo.set(row.order_no, row);
          });
          const existingNos = new Set(this.allOrders.map((o) => String(o.order_no || '')));
          const fileRows = imported.filter((r) => r.order_no);
          const skipped = fileRows.filter((r) => !existingNos.has(r.order_no)).length;
          const next = this.allOrders.map((existing) => {
            const imp = byNo.get(String(existing.order_no || ''));
            if (!imp) return existing;
            const q = Number(imp.quantity);
            const up = Number(imp.unit_price);
            const tp = Number(imp.total_price);
            return {
              ...existing,
              user_name: imp.user_name || existing.user_name,
              user_phone: imp.user_phone || existing.user_phone,
              address: imp.address || existing.address,
              product_name: imp.product_name || existing.product_name,
              unit_price: Number.isFinite(up) && up >= 0 ? up : existing.unit_price,
              quantity: Number.isFinite(q) && q > 0 ? q : existing.quantity,
              total_price: Number.isFinite(tp) && !Number.isNaN(tp) ? tp : existing.total_price,
              createdAt: imp.createdAt || existing.createdAt
            };
          });
          this.allOrders = next;
          this.applyFilters();
          localStorage.setItem('dashboard_order_count', String(this.allOrders.length));
          const merged = fileRows.length - skipped;
          this.$message.success(
            skipped > 0
              ? `已按文件更新 ${merged} 条（与库内订单号匹配）；已跳过 ${skipped} 条（库中已无此订单号，可能已删除）`
              : `已按文件更新 ${merged} 条订单的展示字段`
          );
        };
        run().catch(() => {
          this.$message.error('导入失败，请确认后端可用后重试');
        });
      };
      reader.readAsText(file, 'utf-8');
      return false; // 阻止 el-upload 自动上传
    }
  }
}
</script>

<style scoped>
.order-management {
  padding: 0;
}

.lvyun-profile-banner {
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 20px;
  border: 1px solid #dbeafe;
}
.lvyun-profile-banner h2 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #1e293b;
}
.lvyun-profile-banner p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}
.schedule-filter-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.schedule-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.filter-label {
  font-weight: 500;
  color: #334155;
}
.meal-stats-line {
  color: #165dff;
  font-weight: 500;
}

.card-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  row-gap: 10px;
}

.card-header-title {
  flex: 0 0 auto;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.order-status-boxes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px 10px;
  row-gap: 8px;
  flex: 1 1 260px;
  min-width: 0;
  max-width: 100%;
}

.order-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.status-box-btn {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s ease;
}

.status-box-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.status-box-btn.active {
  background: #ecf5ff;
  border-color: #409eff;
  color: #409eff;
}

.status-box-count {
  margin-left: 6px;
  font-weight: 600;
}

.order-filter-card {
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
}

.order-filter-panel {
  padding: 4px 0 0;
}

.order-filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;
}

.filter-field-span2 {
  grid-column: span 2;
}

.filter-field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.filter-field-control {
  width: 100%;
}

.filter-field-control >>> .el-select,
.filter-field-control >>> .el-input,
.filter-field-control >>> .el-date-editor {
  width: 100%;
}

.filter-field-split {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 8px;
}

.filter-time-range {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.filter-range-sep {
  color: #909399;
  font-size: 13px;
  text-align: center;
}

.order-filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}

@media (max-width: 1200px) {
  .order-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .filter-field-span2 {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .order-filter-grid {
    grid-template-columns: 1fr;
  }
  .filter-field-span2 {
    grid-column: span 1;
  }
  .filter-time-range {
    grid-template-columns: 1fr;
  }
  .filter-field-split {
    grid-template-columns: 1fr;
  }
}

.demo-form-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.order-dialog-footer {
  text-align: right;
}

.order-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
  background-color: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.order-item-card--selected {
  border-color: #10b981;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.25);
}

.order-list-select-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  padding: 8px 4px;
}

.order-list-select-hint {
  font-size: 13px;
  color: #64748b;
}

.order-select-checkbox {
  margin-right: 10px;
}

.order-batch-float-bar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.28);
  color: #fff;
}

.order-batch-float-count {
  font-size: 14px;
  font-weight: 600;
  margin-right: 4px;
}

.order-batch-float-delete {
  background: #fff1f2 !important;
  border-color: #fecdd3 !important;
  color: #be123c !important;
}

.order-batch-float-delete:hover,
.order-batch-float-delete:focus {
  background: #e11d48 !important;
  border-color: #e11d48 !important;
  color: #fff !important;
}

.batch-dialog-summary {
  margin: 0 0 12px;
  font-size: 14px;
  color: #334155;
}

.batch-bar-fade-enter-active,
.batch-bar-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.batch-bar-fade-enter,
.batch-bar-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}

.order-item-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

.order-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.order-item-header-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.order-item-header-left > span {
  margin-right: 16px;
}

.order-no {
  font-weight: 600;
  color: #303133;
}

.order-created-at {
  color: #909399;
}

.order-channel {
  color: #909399;
}

.order-item-body {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1.3fr 0.9fr;
  column-gap: 20px;
  padding: 12px 0;
  margin-bottom: 10px;
  font-size: 13px;
}

.order-body-section {
  min-width: 0;
}

.obs-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
}

.obs-product-name,
.obs-customer-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 14px;
  margin-bottom: 4px;
  line-height: 1.4;
}

.obs-muted {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.obs-meal-stats {
  margin-top: 8px;
  padding: 6px 10px;
  background: #f0f9ff;
  border-radius: 8px;
  font-size: 12px;
  color: #0369a1;
  font-weight: 500;
}

.obs-meal-stats .obs-dot {
  margin: 0 4px;
  opacity: 0.5;
}

.obs-address {
  margin-top: 4px;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.obs-tag {
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
}

.obs-tag-region {
  background: #eff6ff;
  color: #2563eb;
}

.obs-tag-rider {
  background: #ecfdf5;
  color: #059669;
  margin-left: 4px;
}

.obs-meta-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  line-height: 1.5;
  color: #334155;
}

.obs-meta-k {
  flex: 0 0 52px;
  color: #94a3b8;
  font-size: 12px;
}

.obs-pause-badge {
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  background: #fef3c7;
  color: #b45309;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.obs-delay-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #d97706;
  line-height: 1.4;
}

.obs-amount-main {
  font-size: 22px;
  font-weight: 700;
  color: #ef4444;
  line-height: 1.2;
}

.obs-amount-label {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.obs-amount-sub {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
}

.order-body-amount {
  text-align: right;
}

.order-item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.order-footer-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
}

.btn-ghost-warn {
  border-color: #fde68a;
  background: #fffbeb;
  color: #b45309;
}

.btn-more {
  border-color: #e2e8f0;
  color: #475569;
}

.order-product-info {
  display: flex;
  align-items: center;
}

.product-thumb {
  width: 50px;
  height: 50px;
  margin-right: 10px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  background: url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2250%22 height=%2250%22%3E%3Crect width=%2250%22 height=%2250%22 fill=%22%23f5f7fa%22/%3E%3C/svg%3E') center/cover no-repeat;
}

.product-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.product-meta {
  color: #909399;
}

.order-user-info {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
}

.user-name {
  font-weight: 500;
}

.user-phone {
  color: #909399;
}

.user-address {
  color: #606266;
}

.delivery-region {
  color: #409eff;
  font-weight: 500;
}

.rider-line {
  color: #67c23a;
  font-size: 12px;
}

.delay-log-line {
  color: #909399;
  font-size: 12px;
}

.order-summary {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  color: #606266;
}

.order-shipping-note {
  color: #e6a23c;
  font-weight: 500;
  white-space: pre-line;
}

.delay-record-box {
  border-color: #f7cba2;
  background: #fdf6ec;
  color: #e6a23c;
}

.order-amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
}

.order-total {
  font-size: 16px;
  font-weight: 600;
  color: #f56c6c;
}

.order-sub-amount {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

.order-list-card ::v-deep(.cell) {
  white-space: nowrap;
}

.customer-bind-ok {
  color: #67c23a;
}

.form-item-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.order-form-rider-preview {
  color: #409eff;
  font-weight: 500;
}
.order-form-rider-empty {
  color: #e6a23c;
}
.historical-order-options {
  margin-top: 10px;
}
.historical-order-options .el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.historical-count-panel {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.historical-count-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: #606266;
}
.historical-count-remaining {
  font-size: 13px;
  color: #409eff;
}
.historical-count-remaining strong {
  font-size: 16px;
  color: #303133;
}

.customer-suggest-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  line-height: 1.5;
  padding: 2px 0;
}

.customer-suggest-name {
  font-weight: 600;
  color: #303133;
}

.customer-suggest-phone {
  color: #409eff;
}

.customer-suggest-addr {
  flex: 1 1 100%;
  font-size: 12px;
  color: #909399;
}

.period-hint-error {
  color: #f56c6c;
}

.schedule-preview-summary {
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
}

.schedule-preview-table {
  width: 100%;
  margin-top: 12px;
}

.preview-row-warn {
  color: #e6a23c;
}

.order-detail-dialog .detail-line {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #303133;
}

.order-detail-dialog .detail-k {
  color: #909399;
  margin-right: 8px;
}

.order-detail-dialog .text-muted {
  color: #909399;
  font-size: 12px;
}

/* 订单详情弹窗 */
.odm-header {
  padding-right: 8px;
}

.odm-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.odm-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.odm-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.odm-status-tag {
  border-radius: 6px;
  font-weight: 600;
}

.odm-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.odm-close:hover {
  background: #e2e8f0;
  color: #334155;
}

.odm-order-no {
  margin-top: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: #64748b;
  letter-spacing: 0.02em;
}

.odm-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.odm-header-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.odm-amount-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%);
  border: 1px solid #fecaca;
  border-radius: 12px;
}

.odm-amount-item {
  flex: 1 1 120px;
  min-width: 100px;
}

.odm-amount-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.odm-amount-value {
  font-size: 22px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1.2;
}

.odm-amount-value.odm-amount-sub {
  font-size: 16px;
  color: #475569;
  font-weight: 600;
}

.odm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.odm-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.odm-section-full {
  margin-bottom: 12px;
}

.odm-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.odm-section-head i {
  color: #10b981;
  font-size: 15px;
}

.odm-section-body {
  padding: 12px 14px;
}

.odm-field {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.odm-field:last-child {
  margin-bottom: 0;
}

.odm-field-block {
  flex-direction: column;
  gap: 4px;
}

.odm-field-k {
  flex: 0 0 56px;
  color: #94a3b8;
  font-size: 12px;
}

.odm-field-block .odm-field-k {
  flex: none;
}

.odm-field-v {
  color: #1e293b;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.odm-phone {
  color: #2563eb;
  font-weight: 500;
}

.odm-product-name {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 10px;
  line-height: 1.4;
}

.odm-period-bar {
  padding: 10px 14px;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
}

.odm-meal-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
}

.odm-meal-stats strong {
  color: #0369a1;
  font-weight: 700;
}

.odm-delay-note {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fffbeb;
  border-radius: 8px;
  font-size: 12px;
  color: #b45309;
  line-height: 1.5;
}

.odm-delay-note i {
  margin-right: 4px;
}

.odm-delivery-card {
  background: linear-gradient(180deg, #ecfdf5 0%, #f8fafc 100%);
  border: 1px solid #a7f3d0;
  border-radius: 12px;
  overflow: hidden;
}

.odm-delivery-card .odm-section-head {
  background: transparent;
  border-bottom-color: #bbf7d0;
}

.odm-delivery-form {
  padding: 0 14px 4px;
}

.odm-delivery-form .el-form-item {
  margin-bottom: 12px;
}

.odm-delivery-form .el-form-item__label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  padding-bottom: 4px;
  line-height: 1.4;
}

.odm-delivery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.odm-current-rider {
  margin: 0 14px 12px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  color: #059669;
}

.odm-current-rider i {
  margin-right: 4px;
}
.odm-mask-phone-item {
  margin-top: 8px;
  margin-bottom: 0;
}
.odm-mask-phone-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
}

.odm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.odm-btn-cancel {
  border-radius: 10px;
}

.odm-btn-save {
  border-radius: 10px;
  font-weight: 600;
  min-width: 120px;
}

@media (max-width: 640px) {
  .odm-grid,
  .odm-delivery-grid {
    grid-template-columns: 1fr;
  }

  .order-item-body {
    grid-template-columns: 1fr 1fr;
    row-gap: 14px;
  }

  .order-body-amount {
    grid-column: 1 / -1;
    text-align: left;
    display: flex;
    align-items: baseline;
    gap: 10px;
  }
}
</style>

<!-- 日期选择器面板挂载在 body，需全局样式才能显示「休」 -->
<style>
.el-date-table td.rest-day-cell div {
  position: relative;
  color: #f56c6c;
}
.el-date-table td.rest-day-cell div::after {
  content: '休';
  position: absolute;
  right: 2px;
  bottom: 0;
  font-size: 10px;
  color: #f56c6c;
  font-weight: bold;
}

.delay-record-dialog-content {
  padding-top: 4px;
}

.delay-record-dialog-hint {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.delay-record-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.delay-record-tile {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  overflow: hidden;
}

.drt-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.drt-date {
  font-weight: 700;
  color: #0f172a;
  font-size: 14px;
}

.drt-operator {
  flex: 1;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.drt-delete {
  flex-shrink: 0;
}

.drt-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drt-cell {
  display: flex;
  gap: 10px;
  font-size: 13px;
  line-height: 1.5;
}

.drt-k {
  flex: 0 0 72px;
  color: #94a3b8;
  font-size: 12px;
}

.drt-v {
  color: #334155;
  flex: 1;
}

.drt-v.highlight {
  color: #059669;
  font-weight: 600;
}

.sub-cal-regen-summary {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #166534;
  font-size: 13px;
  line-height: 1.5;
}

.sub-cal-defer-action-hint {
  margin-top: 12px;
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  color: #047857;
  font-size: 13px;
  line-height: 1.5;
}

.sub-cal-deferral-all {
  margin-top: 12px;
  padding: 12px 14px;
  background: #faf5ff;
  border-radius: 10px;
  border: 1px solid #e9d5ff;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sub-cal-detail-flat {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.sub-cal-detail-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}

.sub-cal-delay-tiles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sub-cal-verify-tiles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.sub-cal-verify-tile {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.sub-cal-verify-tile.role-anchor {
  background: #fff7ed;
  border-color: #fdba74;
}

.sub-cal-verify-tile.role-linked {
  background: #fffbeb;
  border-color: #fcd34d;
}

.scv-main {
  font-size: 13px;
  color: #334155;
}

.scv-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.85);
  color: #c2410c;
}

.sub-cal-verify-tile.role-linked .scv-tag {
  color: #b45309;
}

.scv-sub {
  margin-top: 4px;
  font-size: 12px;
}

.sub-cal-delay-tile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
}

.scd-main {
  color: #475569;
}

.scd-main i {
  margin: 0 6px;
  color: #94a3b8;
}

.scd-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.delay-record-dialog-text {
  white-space: pre-line;
  line-height: 1.8;
  font-size: 14px;
  color: #303133;
  min-height: 260px;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.preview-list {
  max-height: 220px;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 10px;
}

.preview-line {
  font-size: 12px;
  line-height: 1.7;
  color: #303133;
}

.extend-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.5;
}

.feie-period-range {
  vertical-align: middle;
  width: 240px;
  max-width: 100%;
  flex: 1 1 200px;
}

.period-preview-blocks {
  max-height: 360px;
}

.period-day-block {
  margin-bottom: 12px;
}

.period-day-title {
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 13px;
}

.sub-cal-wrap {
  min-height: 120px;
}

.sub-cal-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.sub-cal-nav {
  margin-left: auto;
}

.sub-cal-legend {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.sub-cal-legend .muted {
  margin-left: auto;
}

.sub-cal-legend .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}

.sub-cal-legend .dot.initial {
  background: #22c55e;
}

.sub-cal-legend .dot.delayed {
  background: #f97316;
}

.sub-cal-legend .dot.verified {
  background: #64748b;
}

.sub-cal-legend .dot.multi-anchor {
  background: #f97316;
}

.sub-cal-legend .dot.multi-linked {
  background: #fbbf24;
}

.sub-cal-legend .dot.empty {
  background: #e2e8f0;
}

.sub-cal-legend .dot.paused {
  background: #94a3b8;
}

.sub-cal-legend .dot.rest {
  background: #fecaca;
  border: 1px solid #f87171;
}

.sub-cal-legend .dot.delay-source {
  background: #fdba74;
  border: 2px solid #dc2626;
  box-sizing: border-box;
}

.sub-cal-cell.has-rest,
.sub-cal-cell.is-rest-day,
.sub-cal-cell.day-rest {
  background: #f1f5f9;
}

.sub-cal-cell.day-holiday {
  background: #ffe4e6;
}

.sub-cal-cell.day-makeup {
  background: #e0f2fe;
}

.sub-cal-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.sub-cal-stats {
  margin-left: 12px;
  font-size: 12px;
}

.sub-cal-queue-empty {
  text-align: center;
  padding: 48px 16px;
  color: #64748b;
}
.sub-cal-queue-empty p {
  margin: 0 0 16px;
}
.sub-cal-queue-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.meal-queue-table {
  width: 100%;
  min-width: 880px;
}
.meal-queue-table >>> th .cell,
.meal-queue-table >>> td .cell {
  white-space: nowrap;
  word-break: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}
.meal-queue-table >>> .el-table__header-wrapper,
.meal-queue-table >>> .el-table__body-wrapper {
  overflow-x: visible;
}
.meal-queue-op-col >>> .cell {
  white-space: nowrap;
}
.meal-queue-op-col >>> .el-button + .el-button {
  margin-left: 4px;
}
.meal-ops-table {
  width: 100%;
  table-layout: fixed;
}
.meal-ops-table >>> .el-table__header-wrapper,
.meal-ops-table >>> .el-table__body-wrapper {
  overflow-x: hidden !important;
}
.meal-ops-table >>> .cell {
  white-space: normal !important;
  word-break: break-all;
  line-height: 1.45;
}
.meal-ops-change-col {
  min-width: 200px;
}
.meal-ops-change {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.45;
}
.meal-ops-change-block + .meal-ops-change-block {
  margin-top: 6px;
}
.meal-ops-idx {
  color: #334155;
  font-weight: 600;
}
.meal-ops-before {
  color: #94a3b8;
}
.meal-ops-after {
  color: #0f766e;
}
.meal-ops-tip-item + .meal-ops-tip-item {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}
.sub-cal-queue-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.sub-cal-rest-mark {
  text-align: center;
  font-size: 11px;
  color: #dc2626;
  font-weight: 600;
  margin-top: 2px;
}

.sub-cal-qtag.sub-cal-qtag-lunch::before {
  content: '午';
  font-size: 9px;
  opacity: 0.65;
  margin-right: 2px;
}

.sub-cal-qtag.sub-cal-qtag-dinner::before {
  content: '晚';
  font-size: 9px;
  opacity: 0.65;
  margin-right: 2px;
}

.sub-cal-cell.seq-non-business,
.sub-cal-cell.is-rest-day {
  background: #f5f5f5 !important;
  pointer-events: none;
}

.sub-cal-qtag.kind-checked {
  color: #595959;
}

.sub-cal-qtag.kind-pending {
  color: #fff;
}

.sub-cal-qtag.is-deferrable {
  cursor: pointer;
}

.sub-cal-qtag.is-defer-disabled,
.sub-cal-qtag.is-readonly {
  cursor: not-allowed;
  opacity: 0.92;
  pointer-events: auto;
}

.sub-cal-qtag.is-defer-disabled:hover,
.sub-cal-qtag.is-readonly:hover {
  filter: none;
}

.sub-cal-qtag.kind-delayed,
.sub-cal-qtag.kind-delay_source {
  color: #fff;
}

.sub-cal-qtag {
  text-align: center;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
  padding: 0 2px;
  white-space: nowrap;
}

.sub-cal-qtag.is-skip-ahead {
  box-shadow: inset 0 0 0 1px #f59e0b;
}

.sub-cal-stats-hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}

.sub-cal-coexist-rules {
  margin: 6px 0 10px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.5;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

html.dark .sub-cal-coexist-rules,
.theme-dark .sub-cal-coexist-rules {
  color: #cbd5e1;
  background: rgba(30, 41, 59, 0.5);
  border-color: #334155;
}

.sub-cal-qtag.is-compact {
  font-size: 9px;
  line-height: 14px;
  padding: 0 1px;
}

.sub-cal-meal-slots {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
  max-height: 72px;
  overflow: hidden;
}

.sub-cal-slot {
  text-align: center;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
}

.sub-cal-slot.initial {
  background: #a7f3d0;
  color: #065f46;
}

.sub-cal-slot.delayed {
  background: #fdba74;
  color: #9a3412;
}

.sub-cal-slot.verified {
  background: #e2e8f0;
  color: #475569;
  text-decoration: line-through;
}

.sub-cal-slot.paused {
  background: #f1f5f9;
  color: #94a3b8;
}

.sub-cal-slot.empty {
  display: none;
}

.sub-cal-qtag.kind-pending {
  background: #a7f3d0;
  color: #065f46;
}

.sub-cal-qtag.kind-delayed {
  background: #fdba74;
  color: #9a3412;
}

.sub-cal-qtag.kind-delay_source {
  background: #fdba74;
  color: #9a3412;
  border: 2px solid #dc2626;
  font-weight: 700;
}

.sub-cal-qtag.kind-checked {
  background: #e2e8f0;
  color: #64748b;
  text-decoration: line-through;
}

html.dark .sub-cal-qtag.kind-pending,
.theme-dark .sub-cal-qtag.kind-pending {
  background: #065f46;
  color: #a7f3d0;
}

html.dark .sub-cal-qtag.kind-delayed,
html.dark .sub-cal-qtag.kind-delay_source,
.theme-dark .sub-cal-qtag.kind-delayed,
.theme-dark .sub-cal-qtag.kind-delay_source {
  background: #9a3412;
  color: #ffedd5;
}

html.dark .sub-cal-qtag.kind-checked,
.theme-dark .sub-cal-qtag.kind-checked {
  background: #334155;
  color: #94a3b8;
}

.sub-cal-slot.multi-anchor {
  background: #ffedd5;
  color: #c2410c;
  font-weight: 700;
  border: 1px solid #fdba74;
}

.sub-cal-slot.multi-linked {
  background: #fef3c7;
  color: #b45309;
  font-weight: 600;
  border: 1px dashed #fcd34d;
}

.sub-cal-slot.paused {
  background: #cbd5e1;
  color: #475569;
}

.sub-cal-slot.empty {
  background: transparent;
  color: #cbd5e1;
}

.sub-cal-cell.has-paused {
  background: #f1f5f9;
}

.meal-pause-badge {
  color: #64748b;
  font-size: 12px;
  margin-top: 4px;
}

.sub-cal-weekhead {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.sub-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.sub-cal-cell {
  min-height: 52px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px;
  font-size: 11px;
}

.sub-cal-cell.sub-cal-day {
  cursor: pointer;
}

.sub-cal-cell.sub-cal-pad {
  border: none;
  background: transparent;
  min-height: 8px;
}

.sub-cal-cell.has-initial {
  background: #ecfdf5;
}

.sub-cal-cell.has-delayed {
  background: #f5f3ff;
}

.sub-cal-cell.has-verified {
  background: #f8fafc;
}

.sub-cal-cell.has-multi-verify {
  background: #fff7ed;
}

.sub-cal-cell.has-multi-linked {
  background: #fffbeb;
}

.sub-cal-cell.empty {
  background: #f8fafc;
  color: #94a3b8;
}

.sub-cal-cell.out-cycle {
  opacity: 0.5;
}

.sub-cal-dnum {
  font-weight: 600;
}

.sub-cal-tags {
  margin-top: 2px;
  color: #334155;
  line-height: 1.3;
}

.sub-cal-detail {
  font-size: 13px;
}

.sub-cal-delay-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
}

.filter-row-secondary {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.danger-text {
  color: #ef4444 !important;
}

.print-dialog-body {
  padding: 4px 0 8px;
}

.print-order-card {
  padding: 16px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  margin-bottom: 20px;
}

.print-order-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.print-order-product {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
}

.print-order-no {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  padding: 2px 8px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.print-order-user {
  font-size: 14px;
  color: #334155;
  margin-bottom: 10px;
}

.print-order-phone {
  margin-left: 8px;
  color: #64748b;
}

.print-meal-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.print-stat-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.print-stat-chip.remain {
  color: #059669;
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.print-stat-chip.used {
  color: #d97706;
  background: #fffbeb;
  border-color: #fde68a;
}

.print-stat-chip.verify-pool {
  color: #2563eb;
  background: #eff6ff;
  border-color: #bfdbfe;
}

.print-order-address {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  margin-top: 6px;
}

.print-section {
  margin-bottom: 18px;
}

.print-section-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}

.print-section-hint {
  font-weight: 500;
  color: #64748b;
}

.print-slot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  min-height: 88px;
}

.print-slot-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.print-slot-card:hover:not(.disabled) {
  border-color: #93c5fd;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.08);
}

.print-slot-card.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.12);
}

.print-slot-card.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f8fafc;
}

.print-slot-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.print-slot-window {
  font-size: 12px;
  color: #64748b;
}

.print-slot-avail {
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  margin-top: 4px;
}

.print-slot-avail.zero {
  color: #dc2626;
}

.print-single-slot-val {
  font-size: 14px;
  color: #334155;
}

.print-slot-avail-inline {
  font-size: 12px;
  color: #64748b;
}

.print-count-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.print-count-hint {
  font-size: 12px;
  color: #64748b;
}

.print-cross-day-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 12px;
  line-height: 1.45;
}

.print-slot-pool {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.print-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.45;
}

.print-non-flex-hint {
  font-size: 13px;
  color: #64748b;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.print-dialog-footer .el-button--primary {
  min-width: 120px;
}
</style>

<style>
.app-admin-dialog,
.schedule-preview-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.16) !important;
}

.app-admin-dialog .el-dialog__header,
.schedule-preview-dialog .el-dialog__header {
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.app-admin-dialog .el-dialog__title,
.schedule-preview-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.app-admin-dialog .el-dialog__headerbtn,
.schedule-preview-dialog .el-dialog__headerbtn {
  top: 18px;
  right: 18px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #f8fafc;
}

.app-admin-dialog .el-dialog__headerbtn:hover,
.schedule-preview-dialog .el-dialog__headerbtn:hover {
  background: #f1f5f9;
}

.app-admin-dialog .el-dialog__body,
.schedule-preview-dialog .el-dialog__body {
  padding: 20px 24px;
  color: #334155;
}

.app-admin-dialog .el-dialog__footer,
.schedule-preview-dialog .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.app-admin-dialog .el-dialog__footer .el-button--primary,
.schedule-preview-dialog .el-dialog__footer .el-button--primary {
  background: #10b981;
  border-color: #10b981;
  border-radius: 10px;
  font-weight: 600;
}

.app-admin-dialog .el-dialog__footer .el-button--default,
.schedule-preview-dialog .el-dialog__footer .el-button--default {
  border-radius: 10px;
}

.schedule-preview-dialog .el-dialog__body {
  padding: 16px 20px 12px;
  box-sizing: border-box;
}

.app-admin-dialog.order-detail-modal .el-dialog__header {
  padding: 20px 24px 16px;
}

.app-admin-dialog.order-detail-modal .el-dialog__body {
  padding: 0 24px 20px;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.app-admin-dialog.order-detail-modal .el-dialog__headerbtn {
  display: none;
}

.order-more-menu .danger-text {
  color: #ef4444 !important;
}
</style>